// ===== Packages =====

import React, {
    useRef,
    useState,
    useEffect,
    useMemo,
    useCallback,
}                                       from 'react';
import {
    css,
    Keyframes,
    FlattenSimpleInterpolation,
}                                       from 'styled-components';
import { ReactSVG }                     from 'react-svg';
import {
    useLocation,
    useNavigate,
    useSearchParams,
}                                       from 'react-router-dom';
import { useDropzone }                  from 'react-dropzone';
import {
    ref,
    getStorage,
    getDownloadURL,
    StorageError,
    StorageReference,
    UploadTaskSnapshot,
}                                       from 'firebase/storage';
import { v4 as uuidv4 }                 from 'uuid';
// eslint-disable-next-line import/no-extraneous-dependencies
import mime                             from 'mime';
import ShortUniqueId                    from 'short-unique-id';
import {
    User,
    GoogleAuthProvider,
    GithubAuthProvider,
    TwitterAuthProvider,
    FacebookAuthProvider,
}                                       from 'firebase/auth';
import {
    FirebaseError,
}                                       from 'firebase/app';
import TaiPasswordStrength              from 'tai-password-strength';
import PasswordValidator                from 'password-validator';
// eslint-disable-next-line import/no-extraneous-dependencies
import moment                           from 'moment-timezone';

// ===== Firebase =====

import fire                             from '../../fire';

// ===== Components =====

import { Transition }                   from '../Transition';
import {
    Button,
    GenericModal,
}                                       from '../Editor/helpers';
import Tooltip                          from '../Tooltip';
import ToggleButton                     from '../ToggleButton';
import Modal                            from '../Modal';

// ===== Services =====

import {
    authenticateWithProvider,
    completeAuthenticatedUserSetup,
    playAudio,
    getStorageErrorMessage,
    getMediaStorageBucket,
    uploadToCloudStorage,
    validateEmail,
    getMoveIndexLeftKeyframe,
    getMoveIndexRightKeyframe,
    recordUserAction,
    getAuthErrorMessage,
    authenticateWithEmailCredentials,
    formatTimeToFutureTimestamp,
    parseSpecialNumbers,
    updateUser,
    getUser,
    sendVerifyAccountEmail,
    sendResetPasswordEmail,
    stopUserSession,
    createMedia,
    getUserUndeletedTopLevelAnnotationCount,
    restoreStateAfterRedirect,
    getUserInviteState,
    createBookInvite,
    resendBookInvite,
    cancelBookInvite,
    getBookInvitePreview,
    getBookInviteClaimLink,
    copyToClipboard,
    getTouchMouseEventHandlers,
    getPressEventHandlers,
    logAuthDebug,
    isTestMode,
    getVerifyEmailRequestErrorMessage,
}                                       from '../../services';
import inferNameFromEmail                               from '../../services/inferNameFromEmail';
import resolvePreferredUserName                         from '../../services/resolvePreferredUserName';
import useLazyAudioRef                                   from '../../services/useLazyAudioRef';

// ===== Hooks =====

import {
    useLatest,
    useTimeout,
    useMediaQuality,
}                                       from '../../hooks';

// ===== Interfaces =====

import {
    IPartialUserItem,
    IDimension,
    IMediaItem,
    IAuthMethod,
    IPasswordError,
    ISnackbarItem,
    ICartItem,
    IPostAccessItem,
    IMailingListSubscriptionValue,
    IAnnotationCommentSubscriptionValue,
    IBookInvitePoolItem,
    IBookInviteAssignmentItem,
    IUserInviteState,
    IUserInviteStateResult,
    IBookInviteActionResult,
    IModalMessage,
    IBookInvitePreview,
}                                       from '../../interfaces';

// ===== Enums =====

import {
    MEDIA_TYPE,
    AUTHENTICATION_ERROR_CODE,
    AUTHENTICATION_TYPE,
    BUTTON_TYPE,
    CURSOR_TARGET,
    INTERACTABLE_OBJECT,
    KEYFRAME_ANCHOR_TYPE,
    PAGE_ROUTE,
    PASSWORD_STRENGTH,
    STORAGE_ENTITY,
    STORAGE_ERROR_CODE,
    TOOLTIP_TYPE,
    USER_ACTION_TYPE,
    POST_ACCESS_ITEM_SCOPE,
    RESET_PASSWORD_CALLABLE_ERROR_CODE,
    RESET_PASSWORD_CALLABLE_ERROR_REASON,
    BOOK_INVITE_ASSIGNMENT_STATUS,
    BOOK_INVITE_SUMMARY_ACCENT,
    BOOK_INVITE_ASSIGNMENT_ACCENT,
    BOOK_INVITE_ACTION_CALLABLE_ERROR_REASON,
    BOOK_INVITE_ACTION_OPERATION,
    AUTH_USER_DETAILS_ROW,
    USER_PROFILE_INFO_DISCLOSURE_KEY,
    USER_PROFILE_INFO_DISCLOSURE_BUTTON_LABEL,
    EMAIL_CREDENTIALS_AUTH_MODE,
    EMAIL_CONVERSION_CALLABLE_ERROR_CODE,
    FIREBASE_AUTH_SIGN_IN_PROVIDER_ID_TYPE,
    SEND_VERIFY_ACCOUNT_EMAIL_STATUS,
}                                       from '../../enums';

// ===== Sounds =====

import DialogExpand                     from '../../sounds/swoosh_in.mp3';
import DialogContract                   from '../../sounds/swoosh_out.mp3';
import InputClick                       from '../../sounds/button_click.mp3';
import ToggleSnap                       from '../../sounds/snap.mp3';
import CreateClip                       from '../../sounds/create.mp3';
import DeleteClip                       from '../../sounds/delete.mp3';

// ===== Images =====

import SmileyIcon                       from '../../images/editor/smiley.svg';
import ArrowIcon                        from '../../images/editor/arrow.svg';
import CheckmarkIcon                    from '../../images/editor/checkmark.svg';
import CrossIcon                        from '../../images/editor/cross.svg';
import PencilIcon                       from '../../images/editor/pencil.svg';
import EnvelopeIcon                     from '../../images/envelope.svg';
import CameraIcon                       from '../../images/camera.svg';
import GoogleIcon                       from '../../images/google.svg';
import GithubIcon                       from '../../images/github.svg';
import FacebookIcon                     from '../../images/facebook.svg';
import XTwitterIcon                     from '../../images/x-twitter.svg';
import CautionIcon                      from '../../images/caution.svg';
import HighlightIcon                    from '../../images/highlight.svg';
import HistoryIcon                      from '../../images/history.svg';
import UserIcon                         from '../../images/user.svg';
import DialogCornerCurve                from '../../images/editor/editor-toolbar-corner-curve.svg';
import MailboxIcon                      from '../../images/empty-mailbox.svg';
import EyeIcon                          from '../../images/eye.svg';
import BellIcon                         from '../../images/bell.svg';
import ShelfIcon                        from '../../images/shelf.svg';
import FileIcon                         from '../../images/file.svg';
import GiftIcon                         from '../../images/gift.svg';
import InfoIcon                         from '../../images/info.svg';
import PlusIcon                         from '../../images/editor/plus.svg';
import ClipboardIcon                    from '../../images/editor/clipboard.svg';
import Spinner                          from '../../images/editor/spinner.svg';
import BookmarkIcon                     from '../../images/bookmark.svg';
import CursorIcon                       from '../../images/cursor.svg';

// ===== Constants =====

import {
    DEFAULT_AUDIO_VOLUME,
    FADE_IN_STAGGER_DEFAULT_STYLE,
    FADE_IN_STAGGER_OFFSET_DURATION,
    FADE_IN_STAGGER_TRANSITION_DURATION,
    FADE_IN_STAGGER_TRANSITION_STYLES,
    HOVER_TARGET_CLASSNAME,
    MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
    DIALOG_BODY_CONTAINER_TRANSITION_DURATION,
    AVATAR_TRANSLATE_DURATION,
    DIALOG_BODY_OPACITY_DELAY_DURATION,
    USER_PROFILE_AVATAR_LENGTH_MULTIPLIER,
    USER_PROFILE_CLOSE_BUTTON_CONTAINER_LENGTH_MULTIPLIER,
    USER_PROFILE_CLOSE_BUTTON_LENGTH_MULTIPLIER,
    FADE_IN_DEFAULT_STYLE,
    FADE_IN_TRANSITION_STYLES,
    MOVING_BUTTON_BACKGROUND_TRANSITION_DURATION,
    MOVING_BUTTON_BACKGROUND_TRANSITION_MAX_DURATION,
    DEFAULT_SNACKBAR_VISIBLE_DURATION,
    HEADER_BUTTON_TRANSITION_DURATION,
    USER_PROFILE_DIALOG_TOP_CONTRACTED_MULTIPLIER,
    USER_PROFILE_DIALOG_RIGHT_CONTRACTED_MULTIPLIER,
    USER_PROFILE_DIALOG_BODY_WIDTH_EXPANDED_MULTIPLIER,
    BODY_FONT_SIZE,
    SUPPORT_EMAIL_ADDRESS,
    USER_VERIFY_EMAIL_BUTTON_TEXT,
    SNACKBAR_MESSAGE_VERIFY_EMAIL_SUCCESS,
    SNACKBAR_MESSAGE_VERIFY_EMAIL_RECENT_NEW_ACCOUNT,
    SNACKBAR_MESSAGE_VERIFY_EMAIL_USER_ERROR,
    SNACKBAR_MESSAGE_VERIFY_EMAIL_GENERAL_ERROR,
    BOOK_POST_ID,
    BOOK_INVITE_PROFILE_QUERY_PARAM_OPEN_PROFILE,
    BOOK_INVITE_PROFILE_QUERY_PARAM_PANEL,
    BOOK_INVITE_PROFILE_PANEL_BOOK_INVITES,
    BUILD_BOOK_INVITE_PREVIEW,
    SIGN_IN_ANNOTATION_CREATE_MESSAGE,
    EXTENDED_SNACKBAR_VISIBLE_DURATION,
    AUTH_SETUP_USER_REFRESH_BLOCKING_TIMEOUT_MS,
    EMAIL_SIGN_UP_ERROR,
    EMAIL_SIGN_IN_ERROR,
}                                       from '../../constants/generalConstants';
import MEDIA_QUERY_SIZE                 from '../../constants/mediaQuerySizes';
import CURSOR_SIGN                      from '../../constants/cursorSigns';
import KEYCODE                          from '../../constants/keycodes';

// ===== Styles =====

import {
    Container,
    AuthMethod,
    DialogBody,
    CornerCurve,
    NameContainer,
    AvatarOverlay,
    EmailAuthInput,
    AvatarContainer,
    DialogBodyContainer,
    AuthMethodsContainer,
    CloseButtonContainer,
    AuthenticationMessage,
    EmailAuthInputContainer,
    AvatarUploadProgressBar,
    EmailAuthFieldsContainer,
    AvatarUploadProgressBarContainer,
    PasswordStrengthContainer,
    PasswordStrengthBar,
    EmailAuthTabsContainer,
    EmailAuthBackButtonContainer,
    EmailAuthTabButtonContainer,
    ForgotPasswordText,
    SubmitEmailAuthButtonContainer,
    NameInputContainer,
    NameInput,
    NameText,
    NameButtonContainer,
    AuthUserDetails,
    AuthUserContainer,
    AuthUserIcon,
    AuthUserText,
    AuthUserDisclosureContainer,
    AuthUserDisclosureIcon,
    AuthUserDisclosureLabel,
    AuthUserDisclosureInfoButtonContainer,
    AuthUserDisclosureControl,
    AuthUserDisclosureText,
    AuthUserSignOutButtonContainer,
    AuthVerificationContainer,
    AuthEmailVerificationBadge,
    InfoDisclosureButton,
    InfoDisclosureIcon,
    BookInvitesOverlay,
    BookInvitesHeader,
    BookInvitesTitle,
    BookInvitesSummaryGrid,
    BookInvitesSummaryItem,
    BookInvitesSummaryLabel,
    BookInvitesSummaryLabelRow,
    BookInvitesSummaryInfoDisclosureText,
    BookInvitesSummaryValue,
    BookInvitesAssignmentsContainer,
    BookInvitesAssignmentsList,
    BookInvitesAssignmentsScrollHint,
    BookInvitesAssignmentsScrollHintText,
    BookInvitesAssignmentsScrollHintChevron,
    BookInvitesSummaryHeroAction,
    BookInvitesCreateInviteContainer,
    BookInvitesCreateInviteActions,
    BookInvitesCreateInviteActionButtonContainer,
    BookInvitesEmailPreviewContainer,
    BookInvitesAssignmentEmailPreviewContainer,
    BookInvitesEmailPreviewHeader,
    BookInvitesEmailPreviewHeaderText,
    BookInvitesEmailPreviewSubject,
    BookInvitesEmailPreviewBody,
    BookInvitesEmailPreviewLoadingContainer,
    BookInvitesEmailPreviewSpinner,
    BookInvitesCreateInviteField,
    BookInvitesCreateInviteLabel,
    BookInvitesCreateInviteInput,
    BookInvitesCreateInviteMessageInput,
    BookInvitesCreateInviteError,
    BookInvitesAssignmentItem,
    BookInvitesAssignmentTop,
    BookInvitesAssignmentRecipient,
    BookInvitesAssignmentStatus,
    BookInvitesAssignmentMeta,
    BookInvitesAssignmentActions,
    BookInvitesAssignmentActionButtonContainer,
    BookInvitesAssignmentIconButtonContainer,
    BookInvitesEmptyText,
    BookInvitesEmptyState,
    BookInvitesEmptyIcon,
    BookInvitesEmptyHelpText,
    BookInvitesEmptyActionContainer,
    BookInviteRemainingButton,
    BookInviteRemainingLabel,
    BookInviteRemainingValue,
    BookInvitesBackButtonContainer,
    InfoDisclosureButtonContainer,
}                                       from './styles';
import {
    UserAvatar,
    DropzoneInput,
    MovingButtonBackground,
}                                       from '../../styles';
import { theme }                        from '../../themes/theme-context';

// ===== Initialize Firebase =====

const { auth } = fire();

interface Props {
    customCursorActive: boolean,
    user: IPartialUserItem | null,
    userEmailVerified: boolean,
    userEmail: string | null,
    userIsAdmin: boolean,
    userPostAccess: Map<string, IPostAccessItem> | undefined,
    userCartItems: ICartItem[] | undefined,
    style: any,
    hasSound: boolean,
    lightBackground: boolean,
    currentSessionId: string | null,
    viewportDimensions: IDimension,
    isExpanded: boolean,
    signedIn: AUTHENTICATION_TYPE | null,
    signingInOut: boolean,
    redirectStateChecked: boolean,
    notifyUserToSignUpAfterAnnotationCreationAttempt: boolean,
    setIsExpanded: (isExpanded: boolean) => void,
    setSigningInOut: React.Dispatch<React.SetStateAction<boolean>>,
    setInputFocused: React.Dispatch<React.SetStateAction<boolean>>,
    setUser: React.Dispatch<React.SetStateAction<IPartialUserItem | null>>,
    setNotifyUserToSignUpAfterAnnotationCreationAttempt: React.Dispatch<React.SetStateAction<boolean>>,
    forceSignOutToAnonymous: () => Promise<boolean>,
    onCursorEnter: (
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        candidateTarget?: HTMLElement,
    ) => void,
    onCursorLeave: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
    setSnackbarData: React.Dispatch<React.SetStateAction<ISnackbarItem>>,
    setSignedIn: React.Dispatch<React.SetStateAction<AUTHENTICATION_TYPE | null>>,
    setRefreshNotifications: React.Dispatch<React.SetStateAction<boolean>>,
    setQueueRevealBuyButtons: React.Dispatch<React.SetStateAction<boolean>>,
}
function UserProfileDialog({
    customCursorActive,
    user,
    userEmailVerified,
    userEmail,
    userIsAdmin,
    userPostAccess,
    userCartItems,
    style,
    hasSound,
    lightBackground,
    currentSessionId,
    viewportDimensions,
    isExpanded,
    signedIn,
    signingInOut,
    redirectStateChecked,
    notifyUserToSignUpAfterAnnotationCreationAttempt,
    setIsExpanded,
    setSigningInOut,
    setInputFocused,
    setUser,
    setNotifyUserToSignUpAfterAnnotationCreationAttempt,
    forceSignOutToAnonymous,
    onCursorEnter,
    onCursorLeave,
    setSnackbarData,
    setSignedIn,
    setRefreshNotifications,
    setQueueRevealBuyButtons,
}: Props): JSX.Element {
    // ===== General Constants =====

    const OVERLAY_TRANSITION_DURATION = 200;
    const CLOSE_BUTTON_CONTAINER_OFFSET_TOP_EXPANDED_MULTIPLIER = 0.5; // em
    const CLOSE_BUTTON_CONTAINER_OFFSET_RIGHT_EXPANDED_MULTIPLIER = CLOSE_BUTTON_CONTAINER_OFFSET_TOP_EXPANDED_MULTIPLIER;
    // The 0.325 is a stylistic choice
    // eslint-disable-next-line max-len
    const DIALOG_OFFSET_TOP_EXPANDED_MULTIPLIER = CLOSE_BUTTON_CONTAINER_OFFSET_TOP_EXPANDED_MULTIPLIER + USER_PROFILE_CLOSE_BUTTON_CONTAINER_LENGTH_MULTIPLIER - 0.325; // em
    const DIALOG_OFFSET_RIGHT_EXPANDED_MULTIPLIER = CLOSE_BUTTON_CONTAINER_OFFSET_RIGHT_EXPANDED_MULTIPLIER;
    const DIALOG_BODY_PADDING_MULTIPLIER = 0.6; // em
    const AVATAR_CONTAINER_OFFSET_TOP_EXPANDED_MULTIPLIER = DIALOG_OFFSET_TOP_EXPANDED_MULTIPLIER + DIALOG_BODY_PADDING_MULTIPLIER;
    const AVATAR_CONTAINER_OFFSET_RIGHT_EXPANDED_MULTIPLIER = (
        DIALOG_OFFSET_RIGHT_EXPANDED_MULTIPLIER
        - USER_PROFILE_AVATAR_LENGTH_MULTIPLIER
        - DIALOG_BODY_PADDING_MULTIPLIER
    );
    const ANONYMOUS_AVATAR_ICON_ID = 'anonymous-avatar-icon-id';
    const NUM_EMAIL_AUTH_TABS = 2;
    const EMAIL_AUTH_TAB_HEIGHT_MULTIPLIER = 1.5; // em
    const EMAIL_AUTH_BACK_BUTTON_HEIGHT_MULTIPLIER = EMAIL_AUTH_TAB_HEIGHT_MULTIPLIER;
    const EMAIL_AUTH_SUBMIT_BUTTON_HEIGHT_MULTIPLIER = EMAIL_AUTH_TAB_HEIGHT_MULTIPLIER;
    const EMAIL_AUTH_INPUT_HEIGHT_MULTIPLIER = 2; // em
    const EMAIL_AUTH_TAB_WIDTH_MULTIPLIER = 4.75; // em
    const MOVING_BUTTON_INITIAL_INDEX = 1;
    const EMAIL_AUTH_TAB_MARGIN = 5;
    const EMAIL_AUTH_TAB_BACKGROUND_COLOR = theme.color.neutral800;
    const AUTH_METHOD_BUTTON_WIDTH_MULTIPLIER = 11.8; // em
    const AUTH_METHOD_BUTTON_HEIGHT_MULTIPLIER = 1.9; // em
    const SIGN_IN_INDEX = 1;
    const SIGN_UP_INDEX = 2;
    const NAME_INPUT_WIDTH_MULTIPLIER = 5; // em
    const NAME_CONTAINER_PADDING_MULTIPLIER = DIALOG_BODY_PADDING_MULTIPLIER;
    const NAME_BUTTON_LENGTH = USER_PROFILE_AVATAR_LENGTH_MULTIPLIER * BODY_FONT_SIZE;
    const NAME_BUTTON_SPACING_WIDTH = 5;
    const DIALOG_BODY_CONTENT_BOTTOM_BUFFER_MULTIPLIER = 0.833; // em
    const AUTH_USER_DETAILS_MAILING_LIST_TOOLTIP_TEXT = 'Receive email updates about verascope news.';
    const AUTH_USER_DETAILS_BOOK_ACCESS_TOOLTIP_TEXT = 'Level of access granted to verascope\'s book.';
    const AUTH_USER_DETAILS_BOOK_INVITEES_TOOLTIP_TEXT = 'View and manage all book invites from your purchases.';
    const AUTH_USER_DETAILS_ANNOTATION_UPDATES_TOOLTIP_TEXT = 'Get notified for new comments in annotation threads you\'ve joined.';
    const AUTH_USER_DETAILS_HIDE_ANNOTATIONS_TOOLTIP_TEXT = 'Hide annotations within written works.';
    const AUTH_USER_DETAILS_CUSTOM_CURSOR_TOOLTIP_TEXT = (
        'Replace your system cursor with the website\'s custom cursor when your device is running smoothly.'
    );
    const AUTH_USER_DETAILS_SAVE_READER_PROGRESS_TOOLTIP_TEXT = 'Persist and restore your last read scroll position.';
    const AUTH_VERIFY_EMAIL_BUTTON_HEIGHT_MULTIPLIER = 1.2;
    const AUTH_VERIFY_BOOK_ACCESS_BUTTON_HEIGHT_MULTIPLIER = AUTH_VERIFY_EMAIL_BUTTON_HEIGHT_MULTIPLIER;
    const AUTH_USER_ROW_PILL_HEIGHT_MULTIPLIER = EMAIL_AUTH_TAB_HEIGHT_MULTIPLIER;
    const MAILING_SUBSCRIPTION_TOGGLE_BUTTON_WIDTH = 3 * BODY_FONT_SIZE;
    const MAILING_SUBSCRIPTION_TOGGLE_BUTTON_HEIGHT = 1.25 * BODY_FONT_SIZE;
    const MAILING_SUBSCRIPTION_TOGGLE_BUTTON_MARGIN = 0.2 * BODY_FONT_SIZE;
    const MAILING_SUBSCRIPTION_TOGGLE_BUTTON_HOVER_TRANSITION_DURATION = 400;
    const SNACKBAR_MESSAGE_EXISTING_AVATAR_UPLOAD = 'Unable to upload new avatar while upload another';
    const SNACKBAR_MESSAGE_AVATAR_MISSING_USER = 'There was a problem uploading avatar. Please try again.';
    const SNACKBAR_MESSAGE_SIGN_IN_ERROR = 'There was a problem signing in. Please reach out to verascope support or try again.';
    const SNACKBAR_MESSAGE_RESET_EMAIL_SENT = (email: string): string => `An email has been sent to your email address, ${
        email
    }. Follow the directions in the email to reset your password.`;
    const SNACKBAR_MESSAGE_RESET_EMAIL_ACCOUNT_NOT_FOUND = 'We couldn\'t find an account with that email. Check for typos or create a new account.';
    const SNACKBAR_MESSAGE_RESET_EMAIL_WRONG_PROVIDER = 'This email is linked to a social sign-in account. Please sign in with Google, GitHub, Facebook, or X/Twitter.';
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_RESET_EMAIL_MISSING_EMAIL = `We found your account, but it's missing an email address. Please contact verascope support at ${SUPPORT_EMAIL_ADDRESS}.`;
    const SNACKBAR_MESSAGE_RESET_EMAIL_FALLBACK_ERROR = (errorMessage: string): string => (
        `Encountered error while resetting password: ${errorMessage}. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`
    );
    const SNACKBAR_MESSAGE_SIGN_OUT_ERROR = 'There was a problem signing out. Please try again.';
    const SNACKBAR_MESSAGE_SIGN_OUT_SUCCESS = 'Sucessfully signed out!';
    const SNACKBAR_MESSAGE_AUTHENTICATION_ERROR = 'There was a problem authenticating. Please try again.';
    const SNACKBAR_MESSAGE_TOGGLE_ON_MAILING_LIST = 'Successfully subscribed to mailing list!';
    const SNACKBAR_MESSAGE_TOGGLE_OFF_MAILING_LIST = 'Unsubscribed from mailing list.';
    const SNACKBAR_MESSAGE_TOGGLE_ON_COMMENT_UPDATES = 'Successfully subscribed to annotation comments notification emails!';
    const SNACKBAR_MESSAGE_TOGGLE_OFF_COMMENT_UPDATES = 'Unsubscribed from annotation comments notification emails.';
    const SNACKBAR_MESSAGE_TOGGLE_ON_HIDE_ANNOTATIONS = 'Annotations hidden.';
    const SNACKBAR_MESSAGE_TOGGLE_OFF_HIDE_ANNOTATIONS = 'Annotations revealed.';
    const SNACKBAR_MESSAGE_TOGGLE_ON_CUSTOM_CURSOR = 'Custom cursor enabled.';
    const SNACKBAR_MESSAGE_TOGGLE_OFF_CUSTOM_CURSOR = 'Custom cursor disabled.';
    const SNACKBAR_MESSAGE_TOGGLE_ON_SAVE_READER_PROGRESS = 'Post bookmarking enabled.';
    const SNACKBAR_MESSAGE_TOGGLE_OFF_SAVE_READER_PROGRESS = 'Post bookmarking disabled.';
    const SNACKBAR_MESSAGE_TOGGLE_ERROR = 'Encountered an error modifying settings. Please try again.';
    const USER_AUTH_SUBMIT_BUTTON_HEIGHT_MULTIPLIER = EMAIL_AUTH_TAB_HEIGHT_MULTIPLIER;
    // eslint-disable-next-line max-len
    const GENERIC_SIGN_IN_BOOK_ACCESS_MESSAGE = 'You’re currently browsing as a guest. Sign in to see your personalized information.';
    const BOOK_INVITEES_TRIGGER_LABEL_TEXT = 'Remaining:';
    const BOOK_INVITEES_EMPTY_TEXT = 'No book invites yet.';
    const BOOK_INVITEES_EMPTY_HELP_TEXT = 'Purchase the web book or eligible book formats to receive giftable book invites.';
    const BOOK_INVITEES_EMPTY_BUTTON_TEXT = 'Go to Book Page';
    const BOOK_INVITE_STATUS_PENDING_TOOLTIP_TEXT = 'Invite was sent and is waiting to be claimed.';
    const BOOK_INVITE_STATUS_CLAIMED_TOOLTIP_TEXT = 'Invite was claimed and the recipient now has the book invite.';
    const BOOK_INVITE_STATUS_REVOKED_TOOLTIP_TEXT = 'Invite was canceled and the book invite returned to Remaining.';
    const SNACKBAR_MESSAGE_BOOK_INVITE_INVALID_RECIPIENT_EMAIL = 'Please provide a valid recipient email.';
    const SNACKBAR_MESSAGE_BOOK_INVITE_ACTION_FAILED = 'Unable to update Book invites right now. Please try again.';
    const SNACKBAR_MESSAGE_BOOK_INVITE_ALREADY_EXISTS = (
        'A pending book invite already exists for this recipient.'
    );
    const SNACKBAR_MESSAGE_BOOK_INVITE_NO_REMAINING = (
        'No book invites are currently available to send.'
    );
    const SNACKBAR_MESSAGE_BOOK_INVITE_RECIPIENT_ALREADY_HAS_ACCESS = (
        'Recipient already has active web book access.'
    );
    const SNACKBAR_MESSAGE_BOOK_INVITE_UNAUTHENTICATED = 'Please sign in to manage book invites.';
    const SNACKBAR_MESSAGE_BOOK_INVITE_DEEP_LINK_REQUIRES_SIGN_IN = (
        'Please sign in to open your Book Invites, then try that link again.'
    );
    const SNACKBAR_MESSAGE_BOOK_INVITE_USER_NOT_FOUND = (
        `We couldn't find your account data. Please refresh and try again. If this persists, contact ${SUPPORT_EMAIL_ADDRESS}.`
    );
    const SNACKBAR_MESSAGE_BOOK_INVITE_COPY_LINK_SUCCESS = 'Claim link copied to clipboard.';
    const SNACKBAR_MESSAGE_BOOK_INVITE_COPY_LINK_FAILED = (
        `Unable to copy claim link right now. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`
    );
    const SNACKBAR_MESSAGE_BOOK_INVITE_NOT_FOUND = 'This book invite could not be found.';
    const SNACKBAR_MESSAGE_BOOK_INVITE_PENDING_ONLY_RESEND = 'Only pending book invites can be resent.';
    const SNACKBAR_MESSAGE_BOOK_INVITE_PENDING_ONLY_CANCEL = 'Only pending book invites can be revoked.';
    const SNACKBAR_MESSAGE_BOOK_INVITE_PENDING_ONLY_LINK = (
        'Claim links are only available for pending book invites.'
    );
    const SNACKBAR_MESSAGE_BOOK_INVITE_SEND_SUCCESS = 'Book invite sent!';
    const SNACKBAR_MESSAGE_BOOK_INVITE_SELF_ASSIGN_SUCCESS = 'Book access granted to your account!';
    const SNACKBAR_MESSAGE_BOOK_INVITE_RESEND_SUCCESS = 'Book invite resent!';
    const SNACKBAR_MESSAGE_BOOK_INVITE_REVOKE_SUCCESS = 'Book invite revoked.';
    const BOOK_INVITE_SEND_BOOK_INVITE_MODAL_TITLE = 'Send Book Invite';
    const BOOK_INVITE_SEND_BOOK_INVITE_MODAL_MESSAGE = (recipientLabel: string): string => (
        `Send a Book Invite to ${recipientLabel}? This sends an email immediately and cannot be unsent.`
    );
    const BOOK_INVITE_RESEND_BOOK_INVITE_MODAL_TITLE = 'Resend Book Invite';
    const BOOK_INVITE_RESEND_BOOK_INVITE_MODAL_MESSAGE = (recipientEmail: string): string => (
        `Resend the Book Invite to ${recipientEmail}? This sends another email immediately and cannot be unsent.`
    );
    const BOOK_INVITE_CANCEL_BOOK_INVITE_MODAL_TITLE = 'Revoke Book Invite';
    const BOOK_INVITE_CANCEL_BOOK_INVITE_MODAL_MESSAGE = (recipientEmail: string): string => (
        `Revoke the pending invite for ${recipientEmail}? This returns the book invite to Remaining.`
    );
    const BOOK_INVITE_CANCEL_CREATE_INVITE_DRAFT_MODAL_TITLE = 'Discard Book Invite';
    const BOOK_INVITE_CANCEL_CREATE_INVITE_DRAFT_MODAL_MESSAGE = 'Your invite draft is unsaved. Discard it?';
    // ===== Refs =====

    const emailAuthInputRef = useRef<HTMLInputElement>(null);
    const passwordAuthInputRef = useRef<HTMLInputElement>(null);
    const firstNameInputRef = useRef<HTMLInputElement>(null);
    const lastNameInputRef = useRef<HTMLInputElement>(null);
    const dialogBodyRef = useRef<HTMLDivElement>(null);

    // ----- Sound Clips
    const dialogExpandClip = useLazyAudioRef({
        src: DialogExpand,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const dialogContractClip = useLazyAudioRef({
        src: DialogContract,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const inputClickClip = useLazyAudioRef({
        src: InputClick,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const toggleSnapClip = useLazyAudioRef({
        src: ToggleSnap,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const createBookInviteClip = useLazyAudioRef({
        src: CreateClip,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const deleteClip = useLazyAudioRef({
        src: DeleteClip,
        volume: DEFAULT_AUDIO_VOLUME,
    });

    // ===== React Router =====

    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    // ===== State =====

    const [signingInMethod, setSigningInMethod] = useState<AUTHENTICATION_TYPE | undefined>(undefined);
    const [providerAuthAwaitingBackend, setProviderAuthAwaitingBackend] = useState<boolean>(false);
    const [avatarURL, setAvatarURL] = useState<string | null>(null);
    const [avatarPath, setAvatarPath] = useState<string | undefined>(undefined);
    const [uploadingMedia, setUploadingMedia] = useState<IMediaItem | null>(null);
    // We track the progress of avatar upload here
    const [uploadingStateChangeEvent, setUploadingStateChangeEvent] = useState<{
        mediaItem: IMediaItem,
        snapshot: UploadTaskSnapshot,
        progress: number,
    } | null>(null);
    // We track the completion of avatar here
    const [uploadingCompleteEvent, setUploadingCompleteEvent] = useState<{
        mediaItem: IMediaItem,
        storageRef: StorageReference,
    } | null>(null);
    const [showEmailAuthFields, setShowEmailAuthFields] = useState<boolean>(false);
    const [isSigningUp, setIsSigningUp] = useState<boolean>(false);
    const [transitioningEmailAuthFields, setTransitioningEmailAuthFields] = useState<boolean>(false);
    // stores desired state of pivot button background
    const [movingButtonBackground, setMovingButtonBackground] = useState<{ active: boolean, desiredIndex: number}>({
        active: false,
        desiredIndex: 0,
    });
    // stores the current index of the pivot button background
    const [movingButtonCurrentIndex, setMovingButtonCurrentIndex] = useState(MOVING_BUTTON_INITIAL_INDEX);
    // stores the next index of the movingButtonCurrentIndex to be set following a timeout
    const [movingButtonNextIndex, setMovingButtonNextIndex] = useState(MOVING_BUTTON_INITIAL_INDEX + 1);
    const [emailAuth, setEmailAuth] = useState<string>('');
    const [passwordAuth, setPasswordAuth] = useState<string>('');
    // indicates whether email address of user is valid or not
    const [emailValid, setEmailValid] = useState<boolean>(true);
    // indicates whether password of user is valid or not
    const [passwordValid, setPasswordValid] = useState<boolean>(true);
    // stores schema used to evaluate password validity
    const [passwordSchema] = useState<PasswordValidator>(
        new PasswordValidator()
            .is()
            .min(8)                                         // Minimum length 8
            .is()
            .max(100)                                       // Maximum length 100
            .has()
            .uppercase()                                    // Must have uppercase letters
            .has()
            .lowercase()                                    // Must have lowercase letters
            .has()
            .digits(2)                                      // Must have at least 2 digits
            .has()
            .not()
            .spaces()                                       // Should not have spaces
            .is()
            .not()
            .oneOf(TaiPasswordStrength.commonPasswords),    // Blacklist these values
    );
    // stores any password errors
    const [passwordErrors, setPasswordErrors] = useState<IPasswordError[]>([]);
    // stores the password strength value
    const [passwordStrength, setPasswordStrength] = useState<PASSWORD_STRENGTH | null>(null);
    const onDropAccepted = useCallback((acceptedFiles: File[]) => {
        if (acceptedFiles.length > 0) {
            handleAvatarReplace(acceptedFiles[0]);
        }
    }, [user]);
    const {
        open: openDropzone,
        getRootProps,
        getInputProps,
    } = useDropzone({
        noClick: true,
        noDrag: true,
        noKeyboard: true,
        accept: 'image/*',
        onDropAccepted,
    });
    const [hasForgotPassword, setHasForgotPassword] = useState<boolean>(false);
    const [showEmailAuthSubmitButton, setShowEmailAuthSubmitButton] = useState<boolean>(false);
    // Indicates whether tooltip encouraging user to sign up is visible
    // Presents itself when user creates an annotation
    const [showMakeAnnotationTooltip, setShowMakeAnnotationTooltip] = useState<boolean>(false);
    const [activeInfoDisclosureKey, setActiveInfoDisclosureKey] = useState<USER_PROFILE_INFO_DISCLOSURE_KEY | null>(null);
    const [editingUserName, setEditingUserName] = useState<boolean>(false);
    const [showBookInvitesPage, setShowBookInvitesPage] = useState<boolean>(false);
    const [showBookInviteCreateInvite, setShowBookInviteCreateInvite] = useState<boolean>(false);
    const [bookInviteRecipientEmail, setBookInviteRecipientEmail] = useState<string>('');
    const [bookInviteRecipientName, setBookInviteRecipientName] = useState<string>('');
    const [bookInviteMessage, setBookInviteMessage] = useState<string>('');
    const [bookInviteRecipientEmailFocused, setBookInviteRecipientEmailFocused] = useState<boolean>(false);
    const [bookInviteRecipientNameFocused, setBookInviteRecipientNameFocused] = useState<boolean>(false);
    const [bookInviteMessageFocused, setBookInviteMessageFocused] = useState<boolean>(false);
    const [showBookInviteCreateInvitePreview, setShowBookInviteCreateInvitePreview] = useState<boolean>(false);
    const [bookInvitePreviewAssignmentId, setBookInvitePreviewAssignmentId] = useState<string | null>(null);
    const [bookInviteCreateInviteEmailPreview, setBookInviteCreateInviteEmailPreview] = useState<IBookInvitePreview | null>(null);
    const [bookInviteCreateInviteEmailPreviewLoading, setBookInviteCreateInviteEmailPreviewLoading] = useState<boolean>(false);
    const [hasLoggedCreateInvitePreviewOpen, setHasLoggedCreateInvitePreviewOpen] = useState<boolean>(false);
    const [bookInviteAssignmentEmailPreviews, setBookInviteAssignmentEmailPreviews] = useState<Record<string, IBookInvitePreview>>({});
    const [bookInviteAssignmentEmailPreviewLoadingIds, setBookInviteAssignmentEmailPreviewLoadingIds] = useState<Record<string, boolean>>({});
    const [bookInviteActionSubmitting, setBookInviteActionSubmitting] = useState<boolean>(false);
    const [bookInvitePendingSendInviteRecipientEmail, setBookInvitePendingSendInviteRecipientEmail] = useState<string | null>(null);
    const [bookInvitePendingSendInviteRecipientLabel, setBookInvitePendingSendInviteRecipientLabel] = useState<string | null>(null);
    const [bookInvitePendingResendAssignment, setBookInvitePendingResendAssignment] = useState<IBookInviteAssignmentItem | null>(null);
    const [bookInvitePendingCancelAssignment, setBookInvitePendingCancelAssignment] = useState<IBookInviteAssignmentItem | null>(null);
    const [userInviteState, setUserInviteState] = useState<IUserInviteStateResult | null>(null);
    const [showBookInviteCancelCreateInviteDraftModal, setShowBookInviteCancelCreateInviteDraftModal] = useState<boolean>(false);
    const [showBookInviteAssignmentsScrollHint, setShowBookInviteAssignmentsScrollHint] = useState<boolean>(false);
    const [authDisclosureLabelColumnWidth, setAuthDisclosureLabelColumnWidth] = useState<number>(0);
    const bookInviteAssignmentsListRef = useRef<HTMLDivElement>(null);
    const bookInviteActionSubmittingRef = useRef<boolean>(false);
    const previousShowBookInvitesPageRef = useRef<boolean>(false);
    const authUserDetailsRef = useRef<HTMLDivElement>(null);
    const [measuredDialogBodyContentHeight, setMeasuredDialogBodyContentHeight] = useState<number>(0);
    // Stores number of undeleted top-level annotations for the profile dialog.
    const [numUndeletedTopLevelAnnotations, setNumUndeletedTopLevelAnnotations] = useState<number | null>(null);
    // Indicates whether email verification request is being processed
    const [isProcessingEmailVerificationRequest, setIsProcessingEmailVerificationRequest] = useState<boolean>(false);
    // Indicates whether auth state from sign-in redirect has been restored
    const [redirectAuthStateRestored, setRedirectAuthStateRestored] = useState<boolean>(false);
    const generateBookInviteCreateInviteDraftId = (): string => (
        typeof crypto !== 'undefined'
        && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : uuidv4()
    );
    const [bookInviteCreateInviteDraftId, setBookInviteCreateInviteDraftId] = useState<string>(
        () => generateBookInviteCreateInviteDraftId(),
    );

    // ===== Animation Constants =====

    const DIALOG_BODY_OPACITY_DURATION = 150;
    const NUM_EMAIL_AUTH_FIELDS = 4;
    const EMAIL_AUTH_TABS_INDEX = NUM_EMAIL_AUTH_FIELDS - (NUM_EMAIL_AUTH_FIELDS - 0);
    const EMAIL_AUTH_EMAIL_FIELD_INDEX = NUM_EMAIL_AUTH_FIELDS - (NUM_EMAIL_AUTH_FIELDS - 1);
    const EMAIL_AUTH_PASSWORD_FIELD_INDEX = NUM_EMAIL_AUTH_FIELDS - (NUM_EMAIL_AUTH_FIELDS - 2);
    const EMAIL_AUTH_FORGOT_PASSWORD_INDEX = NUM_EMAIL_AUTH_FIELDS - (NUM_EMAIL_AUTH_FIELDS - 3);
    const EMAIL_AUTH_BACK_BUTTON_INDEX = NUM_EMAIL_AUTH_FIELDS;
    const AUTH_METHOD_TRANSITION_DURATION = 150;
    const EMAIL_AUTH_FIELD_TRANSITION_DURATION = 150;
    const PASSWORD_STRENGTH_CONTAINER_TRANSITION_DURATION = 150;
    const FORGOT_PASSWORD_TRANSITION_DURATION = 150;
    const EMAIL_AUTH_SUBMIT_BUTTON_TRANSITION_DURATION = 150;
    const HIDE_SIGN_UP_TOOLTIP_DURATION = 8000;
    const AUTH_USER_DETAILS_TRANSITION_DURATION = 150;
    const AUTH_USER_DETAILS_ROWS = [
        AUTH_USER_DETAILS_ROW.authType,
        AUTH_USER_DETAILS_ROW.membership,
        AUTH_USER_DETAILS_ROW.annotations,
        AUTH_USER_DETAILS_ROW.bookAccess,
        AUTH_USER_DETAILS_ROW.bookInvites,
        AUTH_USER_DETAILS_ROW.mailingList,
        AUTH_USER_DETAILS_ROW.annotationUpdates,
        AUTH_USER_DETAILS_ROW.hideAnnotations,
        AUTH_USER_DETAILS_ROW.saveReaderProgress,
        AUTH_USER_DETAILS_ROW.customCursor,
        AUTH_USER_DETAILS_ROW.signOutButton,
    ] as const;
    const authUserDetailsTransitionIndexByRow = AUTH_USER_DETAILS_ROWS.reduce<Record<AUTH_USER_DETAILS_ROW, number>>(
        (indexByRow, row, index) => ({
            ...indexByRow,
            [row]: index,
        }),
        {} as Record<AUTH_USER_DETAILS_ROW, number>,
    );
    const NUM_AUTH_USER_DETAILS = AUTH_USER_DETAILS_ROWS.length;
    const AUTH_USER_DETAILS_ENTER_DURATION = 150;

    // ===== Methods =====

    const onAvatarMouseEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
        onCursorEnter(
            CURSOR_TARGET.userAvatar,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onAvatarMouseLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const onButtonMouseEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
        onCursorEnter(
            CURSOR_TARGET.userProfileButton,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onButtonMouseLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const onInfoDisclosureButtonClick = useCallback((disclosureKey: USER_PROFILE_INFO_DISCLOSURE_KEY): void => {
        setActiveInfoDisclosureKey((currentKey) => (
            currentKey === disclosureKey
                ? null
                : disclosureKey
        ));
    }, []);

    const recordProfileBookInviteAction = useCallback((type: USER_ACTION_TYPE, payload?: unknown): void => {
        if (!currentSessionId) return;
        recordUserAction({
            test: isTestMode(),
            type,
            sessionId: currentSessionId,
            payload,
        });
    }, [
        currentSessionId,
    ]);

    const onToggleExpand = (): void => {
        const wasExpanded = isExpanded;
        setIsExpanded(!isExpanded);

        // If sign in to navigate book flag active, deactivate it
        if (notifyUserToSignUpAfterAnnotationCreationAttempt) {
            setNotifyUserToSignUpAfterAnnotationCreationAttempt(false);
        }

        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: wasExpanded
                    ? USER_ACTION_TYPE.hideProfile
                    : USER_ACTION_TYPE.viewProfile,
                sessionId: currentSessionId,
            });
        }

        // Play Sound
        if (
            !isExpanded
            && hasSound
            && dialogExpandClip.current
        ) {
            dialogExpandClip.current.pause();
            dialogExpandClip.current.currentTime = 0;
            playAudio(dialogExpandClip.current);
        } else if (
            isExpanded
            && hasSound
            && dialogContractClip.current
        ) {
            dialogContractClip.current.pause();
            dialogContractClip.current.currentTime = 0;
            playAudio(dialogContractClip.current);
        }
    };

    const onOpenBookInvitesPage = (): void => {
        setShowBookInvitesPage(true);
        setShowBookInviteCreateInvite(false);
        setShowBookInviteCreateInvitePreview(false);
        setBookInvitePreviewAssignmentId(null);
        if (hasSound && toggleSnapClip.current) {
            toggleSnapClip.current.pause();
            toggleSnapClip.current.currentTime = 0;
            playAudio(toggleSnapClip.current);
        }
    };

    const onCloseBookInvitesPage = (): void => {
        onCursorLeave();
        setShowBookInvitesPage(false);
        setShowBookInviteCreateInvite(false);
        setShowBookInviteCreateInvitePreview(false);
        setBookInvitePreviewAssignmentId(null);
        if (hasSound && toggleSnapClip.current) {
            toggleSnapClip.current.pause();
            toggleSnapClip.current.currentTime = 0;
            playAudio(toggleSnapClip.current);
        }
    };

    const normalizeEmail = (value: string): string => `${value || ''}`.trim().toLowerCase();

    const getBookInviteActionErrorMessage = ({
        error,
        operation,
    }: {
        error: unknown,
        operation: BOOK_INVITE_ACTION_OPERATION,
    }): string => {
        // Normalize callable errors from all Book Invite actions into stable UI copy.
        const parsedError = error as {
            code?: string,
            message?: string,
            details?: {
                reason?: string,
            },
        };
        const defaultMessage = (
            operation === BOOK_INVITE_ACTION_OPERATION.copyLink
                ? SNACKBAR_MESSAGE_BOOK_INVITE_COPY_LINK_FAILED
                : SNACKBAR_MESSAGE_BOOK_INVITE_ACTION_FAILED
        );
        if (parsedError?.code === EMAIL_CONVERSION_CALLABLE_ERROR_CODE.unauthenticated) {
            return SNACKBAR_MESSAGE_BOOK_INVITE_UNAUTHENTICATED;
        }
        if (parsedError?.code === EMAIL_CONVERSION_CALLABLE_ERROR_CODE.notFound) {
            return operation === BOOK_INVITE_ACTION_OPERATION.list
                ? SNACKBAR_MESSAGE_BOOK_INVITE_USER_NOT_FOUND
                : SNACKBAR_MESSAGE_BOOK_INVITE_NOT_FOUND;
        }
        if (parsedError?.code === EMAIL_CONVERSION_CALLABLE_ERROR_CODE.alreadyExists) {
            return SNACKBAR_MESSAGE_BOOK_INVITE_ALREADY_EXISTS;
        }
        if (parsedError?.code === EMAIL_CONVERSION_CALLABLE_ERROR_CODE.invalidArgument) {
            return operation === BOOK_INVITE_ACTION_OPERATION.create
                ? SNACKBAR_MESSAGE_BOOK_INVITE_INVALID_RECIPIENT_EMAIL
                : defaultMessage;
        }
        if (parsedError?.code === EMAIL_CONVERSION_CALLABLE_ERROR_CODE.failedPrecondition) {
            // Prefer explicit backend reasons over message-text matching.
            if (
                parsedError.details?.reason
                === BOOK_INVITE_ACTION_CALLABLE_ERROR_REASON.recipientHasActiveWebBookAccess
            ) {
                return SNACKBAR_MESSAGE_BOOK_INVITE_RECIPIENT_ALREADY_HAS_ACCESS;
            }
            if (
                parsedError.details?.reason
                === BOOK_INVITE_ACTION_CALLABLE_ERROR_REASON.noUnassignedInvites
            ) {
                return SNACKBAR_MESSAGE_BOOK_INVITE_NO_REMAINING;
            }
            if (
                parsedError.details?.reason
                === BOOK_INVITE_ACTION_CALLABLE_ERROR_REASON.inviteNotPending
            ) {
                // Same reason code maps to operation-specific UX language.
                if (operation === BOOK_INVITE_ACTION_OPERATION.resend) {
                    return SNACKBAR_MESSAGE_BOOK_INVITE_PENDING_ONLY_RESEND;
                }
                if (operation === BOOK_INVITE_ACTION_OPERATION.cancel) {
                    return SNACKBAR_MESSAGE_BOOK_INVITE_PENDING_ONLY_CANCEL;
                }
                if (operation === BOOK_INVITE_ACTION_OPERATION.copyLink) {
                    return SNACKBAR_MESSAGE_BOOK_INVITE_PENDING_ONLY_LINK;
                }
            }
            if (
                parsedError.details?.reason
                === BOOK_INVITE_ACTION_CALLABLE_ERROR_REASON.invitePoolNotFound
            ) {
                return SNACKBAR_MESSAGE_BOOK_INVITE_ACTION_FAILED;
            }
        }
        return defaultMessage;
    };

    const applyBookInviteActionResultToUser = useCallback((actionResult: IBookInviteActionResult): void => {
        if (!actionResult.success) return;
        setUserInviteState({
            invitePool: actionResult.invitePool,
            inviteAssignments: actionResult.inviteAssignments,
            inviteSummary: actionResult.inviteSummary,
        });
    }, []);

    const refreshBookInviteAssignments = useCallback(async (): Promise<void> => {
        if (!user?.id || bookInviteActionSubmittingRef.current) return;
        bookInviteActionSubmittingRef.current = true;
        try {
            const inviteState = await getUserInviteState({
                test: isTestMode(),
            });
            setUserInviteState(inviteState);
        } catch (error) {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: getBookInviteActionErrorMessage({
                    error,
                    operation: BOOK_INVITE_ACTION_OPERATION.list,
                }),
                icon: CautionIcon,
                hasFailure: true,
            });
        } finally {
            bookInviteActionSubmittingRef.current = false;
        }
    }, [
        user?.id,
        setSnackbarData,
    ]);

    const onCreateBookInvite = async (): Promise<void> => {
        const normalizedRecipientEmail = normalizeEmail(bookInviteRecipientEmail);
        const recipientInputContainsDelimiters = (
            bookInviteRecipientEmail.includes(',')
            || bookInviteRecipientEmail.includes('\n')
        );
        const recipientInputInvalid = (
            !recipientInputContainsDelimiters
            && bookInviteRecipientEmail.trim().length > 0
            && !validateEmail(normalizedRecipientEmail)
        );
        if (
            recipientInputContainsDelimiters
            || recipientInputInvalid
            || normalizedRecipientEmail.length === 0
        ) {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_BOOK_INVITE_INVALID_RECIPIENT_EMAIL,
                icon: CautionIcon,
                hasFailure: true,
            });
            return;
        }
        setBookInviteActionSubmitting(true);
        try {
            const actionResult = await createBookInvite({
                test: isTestMode(),
                recipientEmail: normalizedRecipientEmail,
                recipientName: bookInviteRecipientName.trim().length > 0
                    ? bookInviteRecipientName.trim()
                    : undefined,
                inviteMessage: bookInviteMessage.trim().length > 0
                    ? bookInviteMessage.trim()
                    : undefined,
            });
            applyBookInviteActionResultToUser(actionResult);
            if (actionResult.success) {
                const bookInviteActionPayload = {
                    createInviteDraftId: bookInviteCreateInviteDraftId,
                    previewOpened: (
                        hasLoggedCreateInvitePreviewOpen
                        || showBookInviteCreateInvitePreview
                    ),
                };
                recordProfileBookInviteAction(
                    USER_ACTION_TYPE.sendProfileBookInvite,
                    bookInviteActionPayload,
                );
                if (actionResult.selfAssignedToOwner) {
                    recordProfileBookInviteAction(
                        USER_ACTION_TYPE.sendProfileBookInviteToSelf,
                        bookInviteActionPayload,
                    );
                }
                if (bookInviteRecipientName.trim().length > 0) {
                    recordProfileBookInviteAction(
                        USER_ACTION_TYPE.sendProfileBookInviteWithRecipientName,
                        bookInviteActionPayload,
                    );
                }
                if (bookInviteMessage.trim().length > 0) {
                    recordProfileBookInviteAction(
                        USER_ACTION_TYPE.sendProfileBookInviteWithMessage,
                        bookInviteActionPayload,
                    );
                }
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: actionResult.selfAssignedToOwner
                        ? SNACKBAR_MESSAGE_BOOK_INVITE_SELF_ASSIGN_SUCCESS
                        : SNACKBAR_MESSAGE_BOOK_INVITE_SEND_SUCCESS,
                    icon: EnvelopeIcon,
                    hasSuccess: true,
                });
            }
            setBookInviteRecipientEmail('');
            setBookInviteRecipientName('');
            setBookInviteMessage('');
            setHasLoggedCreateInvitePreviewOpen(false);
            setBookInviteCreateInviteDraftId(generateBookInviteCreateInviteDraftId());
        } catch (error) {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: getBookInviteActionErrorMessage({
                    error,
                    operation: BOOK_INVITE_ACTION_OPERATION.create,
                }),
                icon: CautionIcon,
                hasFailure: true,
            });
        } finally {
            setBookInviteActionSubmitting(false);
        }
    };

    const onResendBookInvite = async (assignmentId: string): Promise<void> => {
        setBookInviteActionSubmitting(true);
        try {
            const actionResult = await resendBookInvite({
                test: isTestMode(),
                assignmentId,
            });
            applyBookInviteActionResultToUser(actionResult);
            if (actionResult.success) {
                recordProfileBookInviteAction(USER_ACTION_TYPE.resendProfileBookInvite);
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_BOOK_INVITE_RESEND_SUCCESS,
                    icon: EnvelopeIcon,
                    hasSuccess: true,
                });
            }
        } catch (error) {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: getBookInviteActionErrorMessage({
                    error,
                    operation: BOOK_INVITE_ACTION_OPERATION.resend,
                }),
                icon: CautionIcon,
                hasFailure: true,
            });
        } finally {
            setBookInviteActionSubmitting(false);
        }
    };

    const onCopyBookInviteClaimLink = async (assignmentId: string): Promise<void> => {
        setBookInviteActionSubmitting(true);
        try {
            const claimLink = await getBookInviteClaimLink({
                test: isTestMode(),
                assignmentId,
            });
            copyToClipboard(
                claimLink,
                () => {
                    recordProfileBookInviteAction(USER_ACTION_TYPE.copyProfileBookInviteLink);
                    setSnackbarData({
                        visible: true,
                        duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                        text: SNACKBAR_MESSAGE_BOOK_INVITE_COPY_LINK_SUCCESS,
                        icon: ClipboardIcon,
                        hasSuccess: true,
                    });
                },
                () => {
                    setSnackbarData({
                        visible: true,
                        duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                        text: SNACKBAR_MESSAGE_BOOK_INVITE_COPY_LINK_FAILED,
                        icon: CautionIcon,
                        hasFailure: true,
                    });
                },
            );
        } catch (error) {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: getBookInviteActionErrorMessage({
                    error,
                    operation: BOOK_INVITE_ACTION_OPERATION.copyLink,
                }),
                icon: CautionIcon,
                hasFailure: true,
            });
        } finally {
            setBookInviteActionSubmitting(false);
        }
    };

    const onOpenSendBookInviteModal = (): void => {
        if (bookInviteActionSubmitting) return;
        const normalizedRecipientEmail = normalizeEmail(bookInviteRecipientEmail);
        const recipientInputContainsDelimiters = (
            bookInviteRecipientEmail.includes(',')
            || bookInviteRecipientEmail.includes('\n')
        );
        const recipientInputInvalid = (
            !recipientInputContainsDelimiters
            && bookInviteRecipientEmail.trim().length > 0
            && !validateEmail(normalizedRecipientEmail)
        );
        if (
            recipientInputContainsDelimiters
            || recipientInputInvalid
            || normalizedRecipientEmail.length === 0
        ) {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_BOOK_INVITE_INVALID_RECIPIENT_EMAIL,
                icon: CautionIcon,
                hasFailure: true,
            });
            return;
        }
        const normalizedRecipientName = `${bookInviteRecipientName || ''}`.trim();
        const recipientLabel = normalizedRecipientName.length > 0
            ? `${normalizedRecipientName} (${normalizedRecipientEmail})`
            : normalizedRecipientEmail;
        setBookInvitePendingSendInviteRecipientLabel(recipientLabel);
        setBookInvitePendingSendInviteRecipientEmail(normalizedRecipientEmail);
    };

    const onCloseSendBookInviteModal = (): void => {
        setBookInvitePendingSendInviteRecipientEmail(null);
        setBookInvitePendingSendInviteRecipientLabel(null);
    };
    const onConfirmSendBookInvite = async (): Promise<void> => {
        setBookInvitePendingSendInviteRecipientEmail(null);
        setBookInvitePendingSendInviteRecipientLabel(null);
        setShowBookInviteCreateInvitePreview(false);
        setBookInviteCreateInviteEmailPreview(null);
        await onCreateBookInvite();
    };
    const onOpenResendBookInviteModal = (assignment: IBookInviteAssignmentItem): void => {
        if (bookInviteActionSubmitting) return;
        setBookInvitePendingResendAssignment(assignment);
    };
    const onCloseResendBookInviteModal = (): void => {
        setBookInvitePendingResendAssignment(null);
    };
    const onConfirmResendBookInvite = async (): Promise<void> => {
        const assignmentId = bookInvitePendingResendAssignment?.id;
        setBookInvitePendingResendAssignment(null);
        if (!assignmentId) return;
        await onResendBookInvite(assignmentId);
    };
    const onCancelBookInvite = async (assignmentId: string): Promise<void> => {
        setBookInviteActionSubmitting(true);
        try {
            const actionResult = await cancelBookInvite({
                test: isTestMode(),
                assignmentId,
            });
            applyBookInviteActionResultToUser(actionResult);
            if (actionResult.success) {
                recordProfileBookInviteAction(USER_ACTION_TYPE.revokeProfileBookInvite);
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_BOOK_INVITE_REVOKE_SUCCESS,
                    icon: CrossIcon,
                    hasSuccess: true,
                });
            }
            if (actionResult.success && hasSound && deleteClip.current) {
                deleteClip.current.pause();
                deleteClip.current.currentTime = 0;
                playAudio(deleteClip.current);
            }
        } catch (error) {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: getBookInviteActionErrorMessage({
                    error,
                    operation: BOOK_INVITE_ACTION_OPERATION.cancel,
                }),
                icon: CautionIcon,
                hasFailure: true,
            });
        } finally {
            setBookInviteActionSubmitting(false);
        }
    };

    const onOpenCancelBookInviteModal = (assignment: IBookInviteAssignmentItem): void => {
        if (bookInviteActionSubmitting) return;
        setBookInvitePendingCancelAssignment(assignment);
    };

    const onCloseCancelBookInviteModal = (): void => {
        setBookInvitePendingCancelAssignment(null);
    };

    const onConfirmCancelBookInvite = async (): Promise<void> => {
        const assignmentId = bookInvitePendingCancelAssignment?.id;
        setBookInvitePendingCancelAssignment(null);
        if (!assignmentId) return;
        await onCancelBookInvite(assignmentId);
    };

    const onOpenBookInviteCreateInvite = (): void => {
        if (!canOpenBookInviteCreateInvite) return;
        if (hasSound && createBookInviteClip.current) {
            createBookInviteClip.current.pause();
            createBookInviteClip.current.currentTime = 0;
            playAudio(createBookInviteClip.current);
        }
        setBookInvitePreviewAssignmentId(null);
        setShowBookInviteCreateInvitePreview(false);
        setHasLoggedCreateInvitePreviewOpen(false);
        setBookInviteCreateInviteDraftId(generateBookInviteCreateInviteDraftId());
        setShowBookInviteCreateInvite(true);
    };

    const closeBookInviteCreateInviteDraft = (): void => {
        setShowBookInviteCreateInvitePreview(false);
        setHasLoggedCreateInvitePreviewOpen(false);
        setBookInviteCreateInviteDraftId(generateBookInviteCreateInviteDraftId());
        setShowBookInviteCreateInvite(false);
        setBookInviteRecipientEmail('');
        setBookInviteRecipientName('');
        setBookInviteMessage('');
    };

    const onCancelBookInviteCreateInviteDraft = (): void => {
        const hasDraftInput = (
            bookInviteRecipientEmail.trim().length > 0
            || bookInviteRecipientName.trim().length > 0
            || bookInviteMessage.trim().length > 0
        );
        if (hasDraftInput) {
            setShowBookInviteCancelCreateInviteDraftModal(true);
            return;
        }
        closeBookInviteCreateInviteDraft();
        if (hasSound && deleteClip.current) {
            deleteClip.current.pause();
            deleteClip.current.currentTime = 0;
            playAudio(deleteClip.current);
        }
    };

    const onCloseCancelBookInviteCreateInviteDraftModal = (): void => {
        setShowBookInviteCancelCreateInviteDraftModal(false);
    };

    const onConfirmCancelBookInviteCreateInviteDraft = (): void => {
        setShowBookInviteCancelCreateInviteDraftModal(false);
        closeBookInviteCreateInviteDraft();
        if (hasSound && deleteClip.current) {
            deleteClip.current.pause();
            deleteClip.current.currentTime = 0;
            playAudio(deleteClip.current);
        }
    };

    const onToggleCreateInvitePreview = (): void => {
        setBookInvitePreviewAssignmentId(null);
        setShowBookInviteCreateInvitePreview((previous) => {
            const next = !previous;
            if (
                next
                && !hasLoggedCreateInvitePreviewOpen
            ) {
                recordProfileBookInviteAction(
                    USER_ACTION_TYPE.viewProfileBookInviteCreateEmailPreview,
                    {
                        createInviteDraftId: bookInviteCreateInviteDraftId,
                    },
                );
                setHasLoggedCreateInvitePreviewOpen(true);
            }
            return next;
        });
    };

    const onToggleAssignmentInvitePreview = (assignment: IBookInviteAssignmentItem): void => {
        setShowBookInviteCreateInvitePreview(false);
        const willShow = bookInvitePreviewAssignmentId !== assignment.id;
        setBookInvitePreviewAssignmentId((previous) => (
            previous === assignment.id ? null : assignment.id
        ));
        if (willShow) {
            fetchBookInvitePreviewByAssignment(assignment).catch(() => undefined);
        }
    };

    const addUploadingMedia = (mediaItem: IMediaItem): void => {
        setUploadingMedia(mediaItem);
    };

    const updateUploadingMedia = (mediaItem: IMediaItem): void => {
        setUploadingMedia(mediaItem);
    };

    const removeUploadingMedia = (): void => {
        setUploadingMedia(null);
    };

    const selectImage = (e: React.MouseEvent): void => {
        if (!uploadingMedia) {
            e.stopPropagation();
            openDropzone();
        } else {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_EXISTING_AVATAR_UPLOAD,
                icon: CautionIcon,
                hasFailure: true,
            });
        }
    };

    const handleAvatarReplace = (file: File): void => {
        if (!user) {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_AVATAR_MISSING_USER,
                icon: CautionIcon,
                hasFailure: true,
            });
            return;
        }
        const mediaId = uuidv4();
        const uniqueId = new ShortUniqueId({ length: 6 })(); // avoid file name collisions
        const mediaBucket = getMediaStorageBucket(MEDIA_TYPE.image);
        const fileName = file.name.toLowerCase().split('.')[0].replace(' ', '_');
        const fileExtension = mime.getExtension(file.type);
        if (fileExtension == null) {
            setSnackbarData({
                visible: true,
                duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                text: `Unable to determine file extension. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                icon: FileIcon,
                hasFailure: true,
            });
            return;
        }
        const storageEntity = isTestMode()
            ? STORAGE_ENTITY.stagingUsers
            : STORAGE_ENTITY.users;
        const filePath = `${storageEntity}/${user.id}/${mediaBucket}/${mediaId}/${fileName}-${uniqueId}.${fileExtension}`;
        const mediaItem: IMediaItem = {
            id: mediaId,
            type: MEDIA_TYPE.image,
            userId: user.id,
            file,
            filePath,
            uploadProgress: 0,
        };
        setUploadingMedia(mediaItem);

        // make db entry of media
        // execute before file upload so upload cloud function has a place to write to
        createMedia({
            test: isTestMode(),
            mediaId,
            type: MEDIA_TYPE.image,
            storageEntity,
            fileName: `${fileName}-${uniqueId}`,
            fileExtension: fileExtension.toString(),
        });

        // upload file to cloud storage
        // url will be set by cloud function
        const uploadTask = uploadToCloudStorage(
            file,
            filePath,
            user.id,
        );

        uploadTask.on(
            'state_changed',
            (snapshot: UploadTaskSnapshot) => {
                // Observe state change events such as progress, pause, and resume
                // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploadingStateChangeEvent({
                    mediaItem,
                    snapshot,
                    progress,
                });
            },
            (error: StorageError) => {
                if (currentSessionId) {
                    recordUserAction({
                        test: isTestMode(),
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            // eslint-disable-next-line max-len
                            error: getStorageErrorMessage(error.code as STORAGE_ERROR_CODE),
                        },
                    });
                }
                throw Error(getStorageErrorMessage(error.code as STORAGE_ERROR_CODE));
            },
            () => {
                setUploadingCompleteEvent({
                    mediaItem,
                    storageRef: uploadTask.snapshot.ref,
                });
            },
        );
    };

    const handleUploadingMediaStateChange = (
        mediaItem: IMediaItem,
        _snapshot: UploadTaskSnapshot,
        progress: number,
    ): void => {
        if (uploadingMedia) {
            updateUploadingMedia({
                ...uploadingMedia,
                uploadProgress: progress,
            });
        } else {
            addUploadingMedia(mediaItem);
        }
    };

    const handleUploadingMediaComplete = (): void => {
        // Add avatar file Path to user item
        if (uploadingMedia) {
            updateUser({
                test: isTestMode(),
                avatarFilePath: uploadingMedia.filePath,
            });
        }

        // Clear uploading state change event data
        setUploadingStateChangeEvent(null);

        // Clear uploading complete even flag
        setUploadingCompleteEvent(null);

        // Remove from uploading queue
        removeUploadingMedia();
    };

    /* An email firebase account associated with a social authentication method will
     * be upgraded to the social login
    */
    const isProviderAuthenticationType = (authenticationType: AUTHENTICATION_TYPE): boolean => (
        authenticationType === AUTHENTICATION_TYPE.google
        || authenticationType === AUTHENTICATION_TYPE.github
        || authenticationType === AUTHENTICATION_TYPE.facebook
        || authenticationType === AUTHENTICATION_TYPE.xTwitter
    );
    const onAuthentication = async (type: AUTHENTICATION_TYPE): Promise<void> => {
        // Ensures we firebase and firestore listeners are paused
        setSigningInOut(true);
        setSigningInMethod(type);
        logAuthDebug('user-profile-authentication-started', {
            type,
            isAnonymous: !!auth.currentUser?.isAnonymous,
            hasUser: !!user,
        });

        const isAnonymousUser = auth.currentUser?.isAnonymous;

        // Cache old user item to merge with authenticated user item
        let oldUser: IPartialUserItem | undefined;
        let oldUserEmail: string | undefined;
        let oldUserCart: ICartItem[] | undefined;
        let oldUserSessions: string[] | undefined;
        let oldUserUpdateTimestamps: number[] | undefined;
        if (user && isAnonymousUser && type !== AUTHENTICATION_TYPE.email) {
            oldUser = {
                ...user, // all basic data types
            };
            oldUserCart = userCartItems
                ? [...userCartItems]
                : [];
            // Keep provider auth launch in the original user-gesture call stack.
            // Waiting on optional context fetches here can cause redirect auth to be blocked.
        }

        if (isProviderAuthenticationType(type)) {
            setProviderAuthAwaitingBackend(true);
            logAuthDebug('user-profile-provider-auth-dispatched', {
                type,
                hasOldUser: !!oldUser,
                hasOldUserEmail: !!oldUserEmail,
                oldUserSessionsCount: oldUserSessions?.length || 0,
                oldUserUpdatedCount: oldUserUpdateTimestamps?.length || 0,
            });
            authenticateWithProvider({
                test: isTestMode(),
                type,
                hasSound,
                oldUserData: {
                    user: oldUser,
                    email: oldUserEmail,
                    oldUserCart,
                    userSessions: oldUserSessions,
                    userUpdateTimestamps: oldUserUpdateTimestamps,
                },
                onSuccess: () => {
                    // Popup-auth success path: shared setup has already completed.
                    // Redirect-auth finalization runs in restoreRedirectAuthState.
                    logAuthDebug('user-profile-provider-auth-success-callback', {
                        type,
                    });
                    setProviderAuthAwaitingBackend(false);
                    setSigningInOut(false);
                    resetAuth();
                },
                onError: (errorMessage) => {
                    logAuthDebug('user-profile-provider-auth-error-callback', {
                        type,
                        errorMessage,
                    });
                    setProviderAuthAwaitingBackend(false);
                    setSigningInOut(false);
                    setSigningInMethod(undefined);
                    setSnackbarData({
                        visible: true,
                        duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                        text: errorMessage,
                        icon: CautionIcon,
                        hasFailure: true,
                    });
                },
            });
            return;
        }

        // Email authentication mode.
        setProviderAuthAwaitingBackend(false);
        setShowEmailAuthFields(true);
        setSigningInOut(false);
        setSigningInMethod(undefined);
    };

    const onSubmitEmailAuthLatest = useLatest({
        currentSessionId,
    });
    const getResetPasswordErrorMessage = (error: unknown): string => {
        if (error instanceof FirebaseError) {
            if (error.code === RESET_PASSWORD_CALLABLE_ERROR_CODE.notFound) {
                return SNACKBAR_MESSAGE_RESET_EMAIL_ACCOUNT_NOT_FOUND;
            }
            if (error.code === RESET_PASSWORD_CALLABLE_ERROR_CODE.failedPrecondition) {
                const details = error as FirebaseError & {
                    details?: {
                        reason?: string,
                    },
                };
                if (details.details?.reason === RESET_PASSWORD_CALLABLE_ERROR_REASON.wrongAuthenticationType) {
                    return SNACKBAR_MESSAGE_RESET_EMAIL_WRONG_PROVIDER;
                }
                if (details.details?.reason === RESET_PASSWORD_CALLABLE_ERROR_REASON.missingEmail) {
                    return SNACKBAR_MESSAGE_RESET_EMAIL_MISSING_EMAIL;
                }
            }
            return getAuthErrorMessage(error.code as AUTHENTICATION_ERROR_CODE)
                || SNACKBAR_MESSAGE_RESET_EMAIL_FALLBACK_ERROR(error.message);
        }
        return SNACKBAR_MESSAGE_RESET_EMAIL_FALLBACK_ERROR('Unknown error');
    };
    const onSubmitEmailAuth = async (): Promise<void> => {
        const isAnonymousUser = auth.currentUser?.isAnonymous;
        const isAwaitingAuthenticatedProfile = !!(auth.currentUser && !auth.currentUser.isAnonymous && !signedIn);
        const isEmailAuthActionLoadingNow = signingInOut || isAwaitingAuthenticatedProfile;
        if (isEmailAuthActionLoadingNow) return;
        const normalizedEmailAuth = emailAuth.trim();

        if (hasForgotPassword && emailValid) {
            setSigningInOut(true);
            sendResetPasswordEmail({
                test: isTestMode(),
                email: normalizedEmailAuth,
            })
                .then(() => {
                    // Inform user about email
                    setSnackbarData({
                        visible: true,
                        duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                        text: SNACKBAR_MESSAGE_RESET_EMAIL_SENT(normalizedEmailAuth),
                        icon: EnvelopeIcon,
                        hasSuccess: true,
                    });
                    // Unlocks authentication listener to accept new user objects
                    setSigningInOut(false);
                    // Reset authentication elements
                    resetAuth();
                })
                .catch((error) => {
                    setSigningInOut(false);
                    setSigningInMethod(undefined);
                    setSnackbarData({
                        visible: true,
                        duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                        text: getResetPasswordErrorMessage(error),
                        icon: CautionIcon,
                        hasFailure: true,
                    });
                });
            return;
        }

        // Ensures we firebase and firestore listeners are paused
        setSigningInOut(true);
        setSigningInMethod(AUTHENTICATION_TYPE.email);

        // Cache old user item to merge with authenticated user item
        let oldUser: IPartialUserItem | undefined;
        let oldUserEmail: string | undefined;
        let oldUserCart: ICartItem[] | undefined;
        let oldUserSessions: string[] | undefined;
        let oldUserUpdateTimestamps: number[] | undefined;
        if (user && isSigningUp) {
            const inferredName = inferNameFromEmail(emailAuth.trim().toLowerCase());
            const resolvedName = resolvePreferredUserName({
                preferredFirstName: user.firstName,
                preferredLastName: user.lastName,
                fallbackFirstName: inferredName.firstName,
                fallbackLastName: inferredName.lastName,
            });
            oldUser = {
                ...user, // all basic data types
                firstName: resolvedName.firstName,
                lastName: resolvedName.lastName,
            };
            oldUserCart = userCartItems
                ? [...userCartItems]
                : [];
            oldUserEmail = normalizedEmailAuth;
        } else if (user && isAnonymousUser) {
            oldUser = {
                ...user, // all basic data types
            };
            oldUserCart = userCartItems
                ? [...userCartItems]
                : [];
        }

        if (
            (
                isSigningUp
                && emailValid
                && passwordValid
            )
            || (
                !isSigningUp
                && emailValid
                && passwordAuth.length > 0
            )
        ) {
            const mode = isSigningUp
                ? EMAIL_CREDENTIALS_AUTH_MODE.signUp
                : EMAIL_CREDENTIALS_AUTH_MODE.signIn;
            authenticateWithEmailCredentials({
                test: isTestMode(),
                mode,
                hasSound,
                email: normalizedEmailAuth,
                password: passwordAuth,
                oldUserData: {
                    user: oldUser,
                    email: oldUserEmail,
                    oldUserCart,
                    userSessions: oldUserSessions,
                    userUpdateTimestamps: oldUserUpdateTimestamps,
                },
            }).then((currentUser) => {
                onAuthenticationSuccess(
                    AUTHENTICATION_TYPE.email,
                    currentUser,
                    oldUser,
                    oldUserEmail,
                    oldUserCart,
                    oldUserSessions,
                    oldUserUpdateTimestamps,
                ).then(() => {
                    const cur = onSubmitEmailAuthLatest.current;
                    if (cur.currentSessionId) {
                        // Record user action
                        recordUserAction({
                            test: isTestMode(),
                            type: isSigningUp
                                ? USER_ACTION_TYPE.signUpProfile
                                : USER_ACTION_TYPE.signInProfile,
                            sessionId: cur.currentSessionId,
                            payload: isSigningUp
                                ? {
                                    authenticationType: AUTHENTICATION_TYPE.email,
                                }
                                : undefined,
                        });
                    }
                    // Verification is included in the new-account email path; avoid
                    // sending a second standalone verify email from this flow.
                    // Unlocks authentication listener to accept new user objects
                    setSigningInOut(false);
                    // Reset authentication elements
                    resetAuth();
                }).catch((error: Error) => {
                    setSigningInOut(false);
                    setSigningInMethod(undefined);
                    setSnackbarData({
                        visible: true,
                        duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                        text: error.message || SNACKBAR_MESSAGE_SIGN_IN_ERROR,
                        icon: CautionIcon,
                        hasFailure: true,
                    });
                    if (currentSessionId) {
                        recordUserAction({
                            test: isTestMode(),
                            type: USER_ACTION_TYPE.error,
                            sessionId: currentSessionId,
                            payload: {
                                error,
                            },
                        });
                    }
                });
            }).catch((error: Error) => {
                setSigningInOut(false);
                setSigningInMethod(undefined);
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: error.message || (
                        isSigningUp
                            ? EMAIL_SIGN_UP_ERROR
                            : EMAIL_SIGN_IN_ERROR
                    ),
                    icon: CautionIcon,
                    hasFailure: true,
                });
                if (isSigningUp && currentSessionId) {
                    recordUserAction({
                        test: isTestMode(),
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            error: error.message || EMAIL_SIGN_UP_ERROR,
                        },
                    });
                }
            });
        } else {
            setSigningInOut(false);
            setSigningInMethod(undefined);
        }
    };

    const onAuthenticationSuccess = async (
        authenticationType: AUTHENTICATION_TYPE,
        currentUser: User | null,
        oldUser?: IPartialUserItem, // We can't rely on prop because it will be overwritten by user item listener
        oldUserEmail?: string,
        oldUserCart?: ICartItem[],
        oldUserSessions?: string[],
        oldUserUpdateTimestamps?: number[],
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _accessToken?: string | undefined,
    ): Promise<boolean> => {
        let uid: string | null = null;
        if (currentUser) {
            uid = currentUser.uid;
        } else if (auth.currentUser) {
            uid = auth.currentUser.uid;
        }

        if (!uid) {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_AUTHENTICATION_ERROR,
                icon: CautionIcon,
                hasFailure: true,
            });
            return false;
        }

        if (auth.currentUser && auth.currentUser.uid === uid) {
            // Avoid forced token refresh on every auth success path.
            await auth.currentUser.getIdToken().catch(() => null);
        }

        // If annotation creation attempt flag active, deactivate it
        if (notifyUserToSignUpAfterAnnotationCreationAttempt) setNotifyUserToSignUpAfterAnnotationCreationAttempt(false);

        try {
            await completeAuthenticatedUserSetup({
                test: isTestMode(),
                oldUserData: {
                    user: oldUser,
                    email: oldUserEmail,
                    oldUserCart,
                    userSessions: oldUserSessions,
                    userUpdateTimestamps: oldUserUpdateTimestamps,
                },
            });
            // Hydrate the latest user doc after backend auth setup so the dialog
            // can render provider/profile fields from source-of-truth data.
            const refreshUserPromise = getUser({
                test: isTestMode(),
                userId: uid,
            }).catch(() => null);
            let refreshTimeout: ReturnType<typeof setTimeout> | undefined;
            // Bound how long this auth path can block on user hydration.
            // If Firestore/callables are slow we continue sign-in UX and reconcile later.
            const refreshedUser = await Promise.race<IPartialUserItem | null>([
                refreshUserPromise,
                new Promise<null>((resolve) => {
                    refreshTimeout = setTimeout(() => {
                        resolve(null);
                    }, AUTH_SETUP_USER_REFRESH_BLOCKING_TIMEOUT_MS);
                }),
            ]);
            if (refreshTimeout) clearTimeout(refreshTimeout);
            if (refreshedUser) {
                setUser({
                    ...refreshedUser,
                    authenticationType,
                });
                setSignedIn(authenticationType);
                return true;
            }

            // Keep auth transition responsive if user doc hydration is slow.
            // We mark signed-in immediately, then apply the hydrated user as soon as it resolves.
            setSignedIn(authenticationType);
            refreshUserPromise.then((resolvedUser) => {
                if (resolvedUser) {
                    setUser({
                        ...resolvedUser,
                        authenticationType,
                    });
                    return;
                }
                if (currentSessionId) {
                    recordUserAction({
                        test: isTestMode(),
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            // eslint-disable-next-line max-len
                            error: `Unable to load updated user profile. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                        },
                    });
                }
            });
            return true;
        } catch (error) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        error,
                    },
                });
            }
            throw error;
        }
    };

    const handleEmailChange = (): void => {
        if (emailAuthInputRef.current) setEmailAuth(emailAuthInputRef.current.value);
    };

    const handlePasswordChange = (): void => {
        if (passwordAuthInputRef.current) setPasswordAuth(passwordAuthInputRef.current.value);
    };

    const onInputMouseEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
        onCursorEnter(
            CURSOR_TARGET.input,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onInputMouseLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const onInputFocus = (): void => {
        setInputFocused(true);

        // Play Sound
        if (hasSound && inputClickClip.current) {
            inputClickClip.current.pause();
            inputClickClip.current.currentTime = 0;
            playAudio(inputClickClip.current);
        }
    };

    const onInputBlur = (): void => {
        setInputFocused(false);
    };

    const checkForEnter = (e: React.KeyboardEvent): void => {
        if (e.key === KEYCODE.enter) {
            // Cancel the default action, if needed
            e.preventDefault();
            const successButtonVisible = isExpanded
                && showEmailAuthFields
                && (
                    showEmailAuthSubmitButton
                    || (hasForgotPassword && emailAuth.length > 0 && emailValid)
                );

            if (
                emailAuthInputRef.current
                && emailAuthInputRef.current === document.activeElement
            ) {
                // Move from email field to password field
                passwordAuthInputRef.current?.focus();
            }  else if (
                firstNameInputRef.current
                && firstNameInputRef.current === document.activeElement
            ) {
                // Move from first name field to last name field
                lastNameInputRef.current?.focus();
            } else if (
                successButtonVisible
                && !isSigningUp
                && !hasForgotPassword
            ) {
                // Sign in
                onSubmitEmailAuth();
            } else if (
                successButtonVisible
                && !isSigningUp
                && (hasForgotPassword && emailAuth.length > 0 && emailValid)
            ) {
                // Forgot password
                onSubmitEmailAuth();
            }  else if (
                successButtonVisible
                && isSigningUp
            ) {
                // Sign up
                onSubmitEmailAuth();
            }
        } else if (e.key === KEYCODE.escape) {
            // Cancel the default action, if needed
            e.preventDefault();

            // Blur Focused Input
            (document.activeElement as HTMLInputElement).blur();
        }
    };

    const onEmailAuthBackButtonClick = (e: React.MouseEvent): void => {
        if (showEmailAuthSubmitButton) setShowEmailAuthSubmitButton(false);
        if (hasForgotPassword) setHasForgotPassword(false);
        setShowEmailAuthFields(false);
        setTransitioningEmailAuthFields(true);
        setEmailAuth('');
        setPasswordAuth('');
        setPasswordStrength(null);
        setPasswordErrors([]);
        setEmailValid(true);
        setPasswordValid(true);
        setMovingButtonCurrentIndex(MOVING_BUTTON_INITIAL_INDEX);
        setIsSigningUp(false);
        clearTimeoutAnimatingEmailAuthFields();
        timeoutAnimatingEmailAuthFields();
        onCursorLeave(e);
    };

    const onEmailAuthTabButtonClick = (
        index: number,
        e: React.MouseEvent,
    ): void => {
        e.stopPropagation();
        // set next moving button index used
        // in the timeout
        setMovingButtonNextIndex(index);
        // clear any revious delay
        clearDelayChangeMovingButtonIndex();
        // start new delay
        delayChangeMovingButtonIndex();
        // start moving the button by setting desired index
        setMovingButtonBackground({
            active: true,
            desiredIndex: index,
        });

        if (index === 1 && isSigningUp) {
            setIsSigningUp(false);
        } else if (index === SIGN_UP_INDEX && !isSigningUp) {
            setIsSigningUp(true);
        }

        if (hasForgotPassword) {
            setHasForgotPassword(false);
        }
    };

    const onForgotPassword = (): void => {
        setHasForgotPassword(true);
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const onEditName = (_e: React.MouseEvent): void => {
        setEditingUserName(true);
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const onSaveEditName = (e: React.MouseEvent): void => {
        onCursorLeave(e);
        const nameUpdates: Record<string, string> = {};
        const firstNameValue = firstNameInputRef.current?.value;
        const lastNameValue = lastNameInputRef.current?.value;
        if (
            user
            && firstNameInputRef.current
            && firstNameValue
            && firstNameValue !== user.firstName
        ) {
            if (firstNameValue.length === 0) {
                // error
            } else {
                nameUpdates.firstName = firstNameInputRef.current.value;
            }
        }
        if (
            user
            && lastNameInputRef.current
            && lastNameValue
            && lastNameValue !== user.lastName
        ) {
            if (lastNameValue.length === 0) {
                // error
            } else {
                nameUpdates.lastName = lastNameInputRef.current.value;
            }
        }

        if (user && Object.keys(nameUpdates).length > 0) {
            updateUser({
                test: isTestMode(),
                ...nameUpdates,
            });
        }

        setEditingUserName(false);
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const onCancelEditName = (e: React.MouseEvent): void => {
        onCursorLeave(e);
        setEditingUserName(false);
    };

    const handleMailingListSubscription = (subscribe: boolean): void => {
        if (user) {
            const mailingListSubscriptionToAdd: IMailingListSubscriptionValue[] = !user.mailingListSubscription
                ? [
                    {
                        subscribed: false,
                        timestamp: Date.now(),
                    },
                    {
                        subscribed: subscribe,
                        timestamp: Date.now(),
                    },
                ] : [
                    ...user.mailingListSubscription.history,
                    {
                        subscribed: subscribe,
                        timestamp: Date.now(),
                    },
                ];
            updateUser({
                test: isTestMode(),
                mailingListSubscriptionToAdd,
            });
        } else {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_TOGGLE_ERROR,
                icon: CautionIcon,
                hasFailure: true,
            });
        }

        if (subscribe) {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_TOGGLE_ON_MAILING_LIST,
                icon: MailboxIcon,
                hasSuccess: true,
            });
        } else {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_TOGGLE_OFF_MAILING_LIST,
                icon: MailboxIcon,
                hasSuccess: true,
            });
        }

        // Play Sound
        if (hasSound && toggleSnapClip.current) {
            toggleSnapClip.current.pause();
            toggleSnapClip.current.currentTime = 0;
            playAudio(toggleSnapClip.current);
        }

        if (currentSessionId && subscribe) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.subscribeMailingList,
                sessionId: currentSessionId,
            });
        } else if (currentSessionId && !subscribe) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.unsubscribeMailingList,
                sessionId: currentSessionId,
            });
        }
    };

    const handleCommentSubscription = (subscribe: boolean): void => {
        if (user) {
            const commentSubscriptionToAdd: IAnnotationCommentSubscriptionValue[] = !user.commentSubscription
                ? [
                    {
                        subscribed: false,
                        timestamp: Date.now(),
                    },
                    {
                        subscribed: subscribe,
                        timestamp: Date.now(),
                    },
                ] : [
                    ...user.commentSubscription.history,
                    {
                        subscribed: subscribe,
                        timestamp: Date.now(),
                    },
                ];

            updateUser({
                test: isTestMode(),
                commentSubscriptionToAdd,
            });
        } else {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_TOGGLE_ERROR,
                icon: CautionIcon,
                hasFailure: true,
            });
        }

        if (subscribe) {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_TOGGLE_ON_COMMENT_UPDATES,
                icon: BellIcon,
                hasSuccess: true,
            });
        } else {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_TOGGLE_OFF_COMMENT_UPDATES,
                icon: BellIcon,
                hasSuccess: true,
            });
        }

        // Play Sound
        if (hasSound && toggleSnapClip.current) {
            toggleSnapClip.current.pause();
            toggleSnapClip.current.currentTime = 0;
            playAudio(toggleSnapClip.current);
        }

        if (currentSessionId && subscribe) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.subscribeCommentUpdates,
                sessionId: currentSessionId,
            });
        } else if (currentSessionId && !subscribe) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.unsubscribeCommentUpdates,
                sessionId: currentSessionId,
            });
        }
    };

    const handleHideAnnotations = (hide: boolean): void => {
        if (user) {
            updateUser({
                test: isTestMode(),
                hideAnnotations: hide,
            });
            setUser({
                ...user,
                hideAnnotations: hide,
            });
        } else {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_TOGGLE_ERROR,
                icon: CautionIcon,
                hasFailure: true,
            });
        }

        if (hide) {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_TOGGLE_ON_HIDE_ANNOTATIONS,
                icon: HighlightIcon,
                hasSuccess: true,
            });
        } else {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_TOGGLE_OFF_HIDE_ANNOTATIONS,
                icon: HighlightIcon,
                hasSuccess: true,
            });
        }

        // Play Sound
        if (hasSound && toggleSnapClip.current) {
            toggleSnapClip.current.pause();
            toggleSnapClip.current.currentTime = 0;
            playAudio(toggleSnapClip.current);
        }

        if (currentSessionId && hide) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.hideAnnotations,
                sessionId: currentSessionId,
            });
        } else if (currentSessionId && !hide) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.unhideAnnotations,
                sessionId: currentSessionId,
            });
        }
    };

    const handleCustomCursorEnabled = (enabled: boolean): void => {
        if (user) {
            // Mirror the profile preference immediately so App can apply it
            // without waiting for the Firestore listener to return.
            updateUser({
                test: isTestMode(),
                customCursorEnabled: enabled,
            });
            setUser({
                ...user,
                customCursorEnabled: enabled,
            });
        } else {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_TOGGLE_ERROR,
                icon: CautionIcon,
                hasFailure: true,
            });
        }

        setSnackbarData({
            visible: true,
            duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
            text: enabled
                ? SNACKBAR_MESSAGE_TOGGLE_ON_CUSTOM_CURSOR
                : SNACKBAR_MESSAGE_TOGGLE_OFF_CUSTOM_CURSOR,
            icon: CursorIcon,
            hasSuccess: true,
        });

        // Play Sound
        if (hasSound && toggleSnapClip.current) {
            toggleSnapClip.current.pause();
            toggleSnapClip.current.currentTime = 0;
            playAudio(toggleSnapClip.current);
        }
    };

    const handleSaveReaderProgress = (save: boolean): void => {
        if (user) {
            // Persist preference immediately and optimistically mirror it in local user state
            // so ReaderView can react without waiting for Firestore round-trip.
            updateUser({
                test: isTestMode(),
                saveReaderProgress: save,
            });
            setUser({
                ...user,
                saveReaderProgress: save,
            });
        } else {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_TOGGLE_ERROR,
                icon: CautionIcon,
                hasFailure: true,
            });
        }

        setSnackbarData({
            visible: true,
            duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
            text: save
                ? SNACKBAR_MESSAGE_TOGGLE_ON_SAVE_READER_PROGRESS
                : SNACKBAR_MESSAGE_TOGGLE_OFF_SAVE_READER_PROGRESS,
            icon: BookmarkIcon,
            hasSuccess: true,
        });

        // Play Sound
        if (hasSound && toggleSnapClip.current) {
            toggleSnapClip.current.pause();
            toggleSnapClip.current.currentTime = 0;
            playAudio(toggleSnapClip.current);
        }
    };

    const restoreRedirectAuthState = (): void => {
        const restored = restoreStateAfterRedirect();
        logAuthDebug('user-profile-redirect-restore-started', {
            hasRestoredState: !!restored,
            hasAuthState: !!restored?.auth,
            hasOldUserData: !!restored?.oldUserData,
        });
        if (restored?.auth) {
            const { providerId } = restored.auth;
            logAuthDebug('user-profile-redirect-restore-auth-found', {
                providerId: providerId || null,
            });
            // Redirect-auth restore is only needed for social redirect providers.
            // Email/password or firebase bootstrap states should be ignored here.
            if (
                !providerId
                || providerId === 'password'
                || providerId === 'firebase'
            ) {
                logAuthDebug('user-profile-redirect-restore-skipped-non-provider', {
                    providerId: providerId || null,
                });
                setRedirectAuthStateRestored(true);
                return;
            }
            let authenticationType: AUTHENTICATION_TYPE;
            switch (providerId) {
            case GoogleAuthProvider.PROVIDER_ID:
                authenticationType = AUTHENTICATION_TYPE.google;
                break;
            case GithubAuthProvider.PROVIDER_ID:
                authenticationType = AUTHENTICATION_TYPE.github;
                break;
            case TwitterAuthProvider.PROVIDER_ID:
                authenticationType = AUTHENTICATION_TYPE.xTwitter;
                break;
            case FacebookAuthProvider.PROVIDER_ID:
                authenticationType = AUTHENTICATION_TYPE.facebook;
                break;
            default:
                logAuthDebug('user-profile-redirect-restore-unsupported-provider', {
                    providerId,
                });
                setRedirectAuthStateRestored(true);
                return;
            }
            logAuthDebug('user-profile-redirect-finalize-started', {
                providerId,
                authenticationType,
                hasOldUserData: !!restored.oldUserData,
                oldUserSessionsCount: restored.oldUserData?.userSessions?.length || 0,
                oldUserUpdatedCount: restored.oldUserData?.userUpdateTimestamps?.length || 0,
            });
            setSigningInMethod(authenticationType);
            setProviderAuthAwaitingBackend(true);
            completeAuthenticatedUserSetup({
                test: isTestMode(),
                oldUserData: {
                    user: restored.oldUserData?.user,
                    email: restored.oldUserData?.email,
                    oldUserCart: restored.oldUserData?.oldUserCart,
                    userSessions: restored.oldUserData?.userSessions,
                    userUpdateTimestamps: restored.oldUserData?.userUpdateTimestamps,
                },
            }).then(() => {
                // Unlock auth listeners and clear loading state after redirect completion.
                logAuthDebug('user-profile-redirect-finalize-succeeded', {
                    providerId,
                    authenticationType,
                });
                setProviderAuthAwaitingBackend(false);
                setSigningInOut(false);
                resetAuth();
                setRedirectAuthStateRestored(true);
            }).catch((error: Error) => {
                logAuthDebug('user-profile-redirect-finalize-failed', {
                    providerId,
                    authenticationType,
                    message: error.message,
                });
                setProviderAuthAwaitingBackend(false);
                setSigningInOut(false);
                setSigningInMethod(undefined);
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: `Encountered error while finishing sign-in: ${error.message}. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                    icon: CautionIcon,
                    hasFailure: true,
                });
                if (currentSessionId) {
                    recordUserAction({
                        test: isTestMode(),
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            error,
                        },
                    });
                }
                setRedirectAuthStateRestored(true);
            });
            return;
        }
        logAuthDebug('user-profile-redirect-restore-no-auth-state');
    };

    const resetAuth = (): void => {
        if (emailAuth) setEmailAuth('');
        if (passwordAuth) setPasswordAuth('');
        if (emailAuthInputRef.current) emailAuthInputRef.current.value = '';
        if (passwordAuthInputRef.current) passwordAuthInputRef.current.value = '';
        if (showEmailAuthFields) setShowEmailAuthFields(false);
        if (showEmailAuthSubmitButton) setShowEmailAuthSubmitButton(false);
        if (!emailValid) setEmailValid(true);
        if (!passwordValid) setPasswordValid(true);
        if (passwordErrors.length > 0) setPasswordErrors([]);
        if (passwordStrength) setPasswordStrength(null);
        if (hasForgotPassword) setHasForgotPassword(false);
        if (showMakeAnnotationTooltip) setShowMakeAnnotationTooltip(false);
        if (isSigningUp) setIsSigningUp(false);
        if (signingInMethod != null) setSigningInMethod(undefined);
        if (providerAuthAwaitingBackend) setProviderAuthAwaitingBackend(false);
        setRefreshNotifications(true);
    };

    const onVerifyEmail = async (): Promise<void> => {
        if (isProcessingEmailVerificationRequest) return;

        if (!user) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: SNACKBAR_MESSAGE_VERIFY_EMAIL_USER_ERROR,
                    },
                });
            }
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_VERIFY_EMAIL_USER_ERROR,
                icon: EnvelopeIcon,
                hasFailure: true,
            });
            return;
        }
        setIsProcessingEmailVerificationRequest(true);
        try {
            // Send verify email
            const verifyEmailResult = await sendVerifyAccountEmail({
                test: isTestMode(),
            });
            if (!verifyEmailResult.success) {
                if (currentSessionId) {
                    recordUserAction({
                        test: isTestMode(),
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            // eslint-disable-next-line max-len
                            error: SNACKBAR_MESSAGE_VERIFY_EMAIL_GENERAL_ERROR,
                        },
                    });
                }
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_VERIFY_EMAIL_GENERAL_ERROR,
                    icon: EnvelopeIcon,
                    hasFailure: true,
                });
                return;
            }
            const snackbarText = verifyEmailResult.status === SEND_VERIFY_ACCOUNT_EMAIL_STATUS.recentlySentInNewAccountEmail
                ? SNACKBAR_MESSAGE_VERIFY_EMAIL_RECENT_NEW_ACCOUNT
                : SNACKBAR_MESSAGE_VERIFY_EMAIL_SUCCESS;
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: snackbarText,
                icon: EnvelopeIcon,
                hasSuccess: true,
            });
        } catch (error) {
            const message = getVerifyEmailRequestErrorMessage(error);
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        error: message,
                    },
                });
            }
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: message,
                icon: EnvelopeIcon,
                hasFailure: true,
            });
        } finally {
            setIsProcessingEmailVerificationRequest(false);
        }
    };

    const onBuyBook = (): void => {
        onCursorLeave();
        setIsExpanded(false);
        setQueueRevealBuyButtons(true);
        if (location.pathname !== `/${PAGE_ROUTE.book}`) {
            if (currentSessionId) {
                // Record user action
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.viewReadPage,
                    sessionId: currentSessionId,
                    payload: {
                        route: PAGE_ROUTE.cart,
                    },
                });
            }

            navigate(
                `/${PAGE_ROUTE.book}`,
                {
                    state: location.state,
                },
            );
        }
    };

    const onSignOut = (): void => {
        if (currentSessionId) {
            stopUserSession({
                test: isTestMode(),
                sessionId: currentSessionId,
            });
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.signOutProfile,
                sessionId: currentSessionId,
            });
        }

        // Ensures we firebase and firestore listeners are paused
        setSigningInOut(true);
        forceSignOutToAnonymous()
            .then((success) => {
                if (!success) {
                    setSnackbarData({
                        visible: true,
                        duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                        text: SNACKBAR_MESSAGE_SIGN_OUT_ERROR,
                        icon: CautionIcon,
                        hasFailure: true,
                    });
                    return;
                }
                setAvatarURL(null);
                setAvatarPath(undefined);
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_SIGN_OUT_SUCCESS,
                    icon: UserIcon,
                    hasSuccess: true,
                });
            }).catch(() => {
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_SIGN_OUT_ERROR,
                    icon: CautionIcon,
                    hasFailure: true,
                });
            });
    };

    const determineNumUndeletedTopLevelAnnotations = (): void => {
        if (!user) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: `There was a problem determining number of active annotations. User data not found. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                    },
                });
            }
            // eslint-disable-next-line max-len
            throw Error(`There was a problem determining number of active annotations. User data not found. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
        }
        getUserUndeletedTopLevelAnnotationCount({
            test: isTestMode(),
        }).then((undeletedTopLevelAnnotationCount) => {
            setNumUndeletedTopLevelAnnotations(undeletedTopLevelAnnotationCount);
        });
    };

    // ===== Side Effects =====

    const { quality } = useMediaQuality({ preferHighQuality: false });

    /**
     * Manages restoring critical auth state lost when redirecting on sign-in
     */
    useEffect(() => {
        if (
            redirectStateChecked
            && !redirectAuthStateRestored
        ) {
            restoreRedirectAuthState();
        }
    }, [
        redirectStateChecked,
    ]);

    useEffect(() => {
        if (uploadingStateChangeEvent) {
            handleUploadingMediaStateChange(
                uploadingStateChangeEvent.mediaItem,
                uploadingStateChangeEvent.snapshot,
                uploadingStateChangeEvent.progress,
            );
        }
    }, [uploadingStateChangeEvent]);

    useEffect(() => {
        if (uploadingCompleteEvent) {
            handleUploadingMediaComplete();
        }
    }, [uploadingCompleteEvent]);

    useEffect(() => {
        if (user && user.avatarFilePath !== avatarPath) {
            setAvatarPath(user.avatarFilePath);
            const storage = getStorage();
            if (user && user.avatarFilePath) {
                const pathParts = user.avatarFilePath.split('.');
                const dynamicImagePath = `${pathParts[0]}_${quality}.${pathParts[1]}`;
                getDownloadURL(ref(storage, dynamicImagePath)).then((imageURL) => {
                    setAvatarURL(imageURL);
                }).catch((error) => {
                    // We assume cloud function has not yet generated medium image yet
                    if (error.code === STORAGE_ERROR_CODE.objectNotFound) {
                        getDownloadURL(ref(storage, user.avatarFilePath)).then((imageURL) => {
                            setAvatarURL(imageURL);
                        }).catch((err) => {
                            if (currentSessionId) {
                                recordUserAction({
                                    test: isTestMode(),
                                    type: USER_ACTION_TYPE.error,
                                    sessionId: currentSessionId,
                                    payload: {
                                        // eslint-disable-next-line max-len
                                        error: getStorageErrorMessage(err.code as STORAGE_ERROR_CODE),
                                    },
                                });
                            }
                            throw Error(getStorageErrorMessage(err.code as STORAGE_ERROR_CODE));
                        });
                    } else {
                        if (currentSessionId) {
                            recordUserAction({
                                test: isTestMode(),
                                type: USER_ACTION_TYPE.error,
                                sessionId: currentSessionId,
                                payload: {
                                    // eslint-disable-next-line max-len
                                    error: getStorageErrorMessage(error.code as STORAGE_ERROR_CODE),
                                },
                            });
                        }
                        throw Error(getStorageErrorMessage(error.code as STORAGE_ERROR_CODE));
                    }
                });
            } else {
                setAvatarURL(null);
                setAvatarPath(undefined);
            }
        }
    }, [user?.avatarFilePath]);

    useEffect(() => {
        if (emailAuth.length > 0 && !validateEmail(emailAuth)) {
            setEmailValid(false);
            if (showEmailAuthSubmitButton) setShowEmailAuthSubmitButton(false);
        } else {
            setEmailValid(true);
            if (
                (
                    (
                        passwordAuth
                        && emailAuth
                        && passwordValid
                        // email will be valid valid
                    ) || !isSigningUp
                )
                && passwordAuth.length > 0
                && emailAuth.length > 0
            ) setShowEmailAuthSubmitButton(true);
        }
    }, [
        emailAuth,
        isSigningUp,
    ]);

    useEffect(() => {
        if (passwordAuth.length > 0) {
            const valid = passwordSchema.validate(passwordAuth, { details: true });
            if (typeof valid === 'boolean') {
                setPasswordValid(valid);
                setPasswordErrors([]);
            } else {
                const errors = [...valid];
                const isValid = errors.length === 0;
                setPasswordValid(isValid);
                setPasswordErrors([
                    ...errors,
                ]);
            }

            if (
                (
                    (
                        (
                            (typeof valid === 'boolean' && valid)
                            || ((valid as any[]).length === 0)
                        )
                        && passwordAuth
                        && emailAuth
                        && emailValid
                    ) || !isSigningUp
                )
                && emailAuth.length > 0
                && passwordAuth.length > 0
            ) {
                setShowEmailAuthSubmitButton(true);
            } else if (showEmailAuthSubmitButton) {
                setShowEmailAuthSubmitButton(false);
            }

            if (!isSigningUp) return;

            const strengthTester = new TaiPasswordStrength.PasswordStrength();
            // Add in extra files for additional checks and better results
            strengthTester.addCommonPasswords(TaiPasswordStrength.commonPasswords);
            strengthTester.addTrigraphMap(TaiPasswordStrength.trigraphs);
            const strengthTestResult = strengthTester.check(passwordAuth);

            if (strengthTestResult.strengthCode.indexOf(PASSWORD_STRENGTH.veryWeak) >= 0) {
                setPasswordStrength(PASSWORD_STRENGTH.veryWeak);
            } else if (strengthTestResult.strengthCode.indexOf(PASSWORD_STRENGTH.weak) >= 0) {
                setPasswordStrength(PASSWORD_STRENGTH.weak);
            } else if (strengthTestResult.strengthCode.indexOf(PASSWORD_STRENGTH.reasonable) >= 0) {
                setPasswordStrength(PASSWORD_STRENGTH.reasonable);
            } else if (strengthTestResult.strengthCode.indexOf(PASSWORD_STRENGTH.strong) >= 0) {
                setPasswordStrength(PASSWORD_STRENGTH.strong);
            } else if (strengthTestResult.strengthCode.indexOf(PASSWORD_STRENGTH.veryStrong) >= 0) {
                setPasswordStrength(PASSWORD_STRENGTH.veryStrong);
            }
        } else {
            if (!passwordValid) setPasswordValid(true);
            if (passwordErrors.length > 0) setPasswordErrors([]);
            if (passwordStrength) setPasswordStrength(null);
            if (showEmailAuthSubmitButton) setShowEmailAuthSubmitButton(false);
        }
    }, [
        passwordAuth,
        isSigningUp,
    ]);

    useEffect(() => {
        const isAnonymousUser = auth.currentUser?.isAnonymous;
        if (user && !isAnonymousUser && numUndeletedTopLevelAnnotations == null) {
            determineNumUndeletedTopLevelAnnotations();
        } else if (user && isAnonymousUser && numUndeletedTopLevelAnnotations !== null) {
            setNumUndeletedTopLevelAnnotations(null);
        }
    }, [user]);

    useEffect(() => {
        if (
            notifyUserToSignUpAfterAnnotationCreationAttempt
            && !showMakeAnnotationTooltip
        ) {
            if (!isExpanded) setIsExpanded(true);
            setShowMakeAnnotationTooltip(true);
            clearTimeoutHideSignUpTooltip();
            timeoutHideSignUpTooltip();

            if (currentSessionId) {
                // Record user action
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.presentedCreateAccountAnnotationNudgeMessage,
                    sessionId: currentSessionId,
                });
            }
        }
    }, [notifyUserToSignUpAfterAnnotationCreationAttempt]);

    const {
        start: delayChangeMovingButtonIndex,
        clear: clearDelayChangeMovingButtonIndex,
    } = useTimeout(
        useCallback(() => {
            setMovingButtonBackground({
                active: true,
                desiredIndex: movingButtonNextIndex,
            });
            // Set the new index
            setMovingButtonCurrentIndex(movingButtonNextIndex);
        }, [
            movingButtonNextIndex,
        ]),
        Math.min(
            Math.abs(movingButtonBackground.desiredIndex - movingButtonCurrentIndex) * MOVING_BUTTON_BACKGROUND_TRANSITION_DURATION,
            MOVING_BUTTON_BACKGROUND_TRANSITION_MAX_DURATION,
        ),
    );

    const {
        start: timeoutAnimatingEmailAuthFields,
        clear: clearTimeoutAnimatingEmailAuthFields,
    } = useTimeout(() => {
        setTransitioningEmailAuthFields(false);
    }, Math.min(
        MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
        ((NUM_EMAIL_AUTH_FIELDS - Math.max(EMAIL_AUTH_TABS_INDEX - 1, 0)) * FADE_IN_STAGGER_OFFSET_DURATION)
        + FADE_IN_STAGGER_TRANSITION_DURATION,
    ));

    /**
     * Ensure email-auth UI unmounts as soon as sign-in succeeds so
     * authenticated blocks don't overlap with auth fields during transitions.
     */
    useEffect(() => {
        if (signedIn) {
            if (showEmailAuthFields) setShowEmailAuthFields(false);
            if (transitioningEmailAuthFields) setTransitioningEmailAuthFields(false);
            if (hasForgotPassword) setHasForgotPassword(false);
        }
    }, [
        signedIn,
        showEmailAuthFields,
        transitioningEmailAuthFields,
        hasForgotPassword,
    ]);

    const {
        start: timeoutHideSignUpTooltip,
        clear: clearTimeoutHideSignUpTooltip,
    } = useTimeout(() => {
        setShowMakeAnnotationTooltip(false);
        setNotifyUserToSignUpAfterAnnotationCreationAttempt(false);
    }, HIDE_SIGN_UP_TOOLTIP_DURATION);

    const hasAuthenticatedProfile = useMemo(() => (
        !!user && user.authenticationType !== AUTHENTICATION_TYPE.anonymous
    ), [
        user?.authenticationType,
    ]);
    // Provider/email auth can complete before user doc listeners fully reconcile.
    // Prefer the persisted user.authenticationType, but fall back to signedIn so
    // auth-specific UI rows render immediately without needing a full page reload.
    const resolvedAuthenticatedType = useMemo((): AUTHENTICATION_TYPE | null => {
        if (
            user?.authenticationType
            && user.authenticationType !== AUTHENTICATION_TYPE.anonymous
        ) {
            return user.authenticationType;
        }
        if (
            signedIn
            && signedIn !== AUTHENTICATION_TYPE.anonymous
        ) {
            return signedIn;
        }
        return null;
    }, [
        user?.authenticationType,
        signedIn,
    ]);

    const showAuthenticatedDetails = useMemo(() => {
        if (providerAuthAwaitingBackend) {
            // For existing authenticated users, allow details to render once
            // profile state is already available, even if backend sync is still finishing.
            return !!(
                hasAuthenticatedProfile
                || (
                    !!signedIn
                    && signedIn !== AUTHENTICATION_TYPE.anonymous
                )
            );
        }
        return (
            (
                !!signedIn
                && signedIn !== AUTHENTICATION_TYPE.anonymous
            )
            || hasAuthenticatedProfile
        );
    }, [
        providerAuthAwaitingBackend,
        signedIn,
        hasAuthenticatedProfile,
    ]);
    const showUnauthenticatedAuthControls = !showAuthenticatedDetails;
    // Deep-link contract:
    // ?openProfile=true opens the dialog; optional ?panel=book-invites targets the Book Invites panel.
    const deepLinkRequestsProfileDialog = (
        searchParams.get(BOOK_INVITE_PROFILE_QUERY_PARAM_OPEN_PROFILE) === 'true'
    );
    const deepLinkRequestedPanel = searchParams.get(BOOK_INVITE_PROFILE_QUERY_PARAM_PANEL);
    const deepLinkRequestsBookInvitesPanel = (
        deepLinkRequestedPanel === BOOK_INVITE_PROFILE_PANEL_BOOK_INVITES
    );
    const clearProfileDeepLinkSearchParams = useCallback((): void => {
        // Consume profile deep-link params once so refresh/back doesn't reopen unexpectedly.
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.delete(BOOK_INVITE_PROFILE_QUERY_PARAM_OPEN_PROFILE);
        nextSearchParams.delete(BOOK_INVITE_PROFILE_QUERY_PARAM_PANEL);
        setSearchParams(nextSearchParams, { replace: true });
    }, [
        searchParams,
        setSearchParams,
    ]);

    useEffect(() => {
        if (!deepLinkRequestsProfileDialog) {
            return;
        }
        if (!isExpanded) {
            // Open the dialog first; panel routing runs on the next pass once expanded.
            setIsExpanded(true);
            return;
        }
        if (!deepLinkRequestsBookInvitesPanel) {
            clearProfileDeepLinkSearchParams();
            return;
        }
        if (showAuthenticatedDetails) {
            // Only authenticated profiles can render/manage Book Invites content.
            setShowBookInvitesPage(true);
            clearProfileDeepLinkSearchParams();
            return;
        }
        if (redirectStateChecked) {
            // Redirect bootstrap finished and user is still unauthenticated; drop stale deep-link params.
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_BOOK_INVITE_DEEP_LINK_REQUIRES_SIGN_IN,
                icon: CautionIcon,
                hasFailure: true,
            });
            clearProfileDeepLinkSearchParams();
        }
    }, [
        deepLinkRequestsProfileDialog,
        deepLinkRequestsBookInvitesPanel,
        isExpanded,
        showAuthenticatedDetails,
        redirectStateChecked,
        setIsExpanded,
        setSnackbarData,
        clearProfileDeepLinkSearchParams,
    ]);

    useEffect(() => {
        if (!isExpanded || !showAuthenticatedDetails) {
            setShowBookInvitesPage(false);
        }
    }, [
        isExpanded,
        showAuthenticatedDetails,
    ]);

    useEffect(() => {
        if (!isExpanded || !showAuthenticatedDetails) {
            setActiveInfoDisclosureKey(null);
        }
    }, [
        isExpanded,
        showAuthenticatedDetails,
    ]);

    useEffect(() => {
        setActiveInfoDisclosureKey(null);
    }, [
        showBookInvitesPage,
    ]);

    const measureAuthDisclosureLabelColumnWidth = useCallback((): void => {
        const authUserDetailsElement = authUserDetailsRef.current;
        if (!authUserDetailsElement) {
            return;
        }

        const labelElements = authUserDetailsElement.querySelectorAll<HTMLElement>('[data-auth-disclosure-label="true"]');

        if (labelElements.length === 0) {
            setAuthDisclosureLabelColumnWidth(0);
            return;
        }

        let maxLabelWidth = 0;
        const disclosureLabelSideMargin = 0.3125 * BODY_FONT_SIZE;

        labelElements.forEach((labelElement) => {
            maxLabelWidth = Math.max(
                maxLabelWidth,
                Math.ceil(labelElement.getBoundingClientRect().width + (2 * disclosureLabelSideMargin)),
            );
        });

        setAuthDisclosureLabelColumnWidth((currentWidth) => (
            Math.abs(currentWidth - maxLabelWidth) > 0.5
                ? maxLabelWidth
                : currentWidth
        ));
    }, []);

    useEffect(() => {
        if (!isExpanded || !showAuthenticatedDetails || showBookInvitesPage) {
            return undefined;
        }

        let animationFrameId = 0;
        let delayedMeasureTimeoutId = 0;

        const runMeasuredLayout = (): void => {
            if (animationFrameId) cancelAnimationFrame(animationFrameId);
            animationFrameId = requestAnimationFrame(() => {
                measureAuthDisclosureLabelColumnWidth();
            });
        };

        runMeasuredLayout();
        delayedMeasureTimeoutId = window.setTimeout(runMeasuredLayout, 180);

        const authUserDetailsElement = authUserDetailsRef.current;
        let resizeObserver: ResizeObserver | null = null;

        if (authUserDetailsElement && typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(() => {
                runMeasuredLayout();
            });
            resizeObserver.observe(authUserDetailsElement);
        }

        window.addEventListener('resize', runMeasuredLayout);

        return () => {
            if (animationFrameId) cancelAnimationFrame(animationFrameId);
            if (delayedMeasureTimeoutId) window.clearTimeout(delayedMeasureTimeoutId);
            window.removeEventListener('resize', runMeasuredLayout);
            if (resizeObserver) resizeObserver.disconnect();
        };
    }, [
        isExpanded,
        showAuthenticatedDetails,
        showBookInvitesPage,
        measureAuthDisclosureLabelColumnWidth,
    ]);

    useEffect(() => {
        const wasVisible = previousShowBookInvitesPageRef.current;
        if (!wasVisible && showBookInvitesPage) {
            recordProfileBookInviteAction(USER_ACTION_TYPE.viewProfileBookInvitesPanel);
        } else if (wasVisible && !showBookInvitesPage) {
            recordProfileBookInviteAction(USER_ACTION_TYPE.hideProfileBookInvitesPanel);
        }
        previousShowBookInvitesPageRef.current = showBookInvitesPage;
    }, [
        recordProfileBookInviteAction,
        showBookInvitesPage,
    ]);

    useEffect(() => {
        setUserInviteState(null);
    }, [
        user?.id,
    ]);

    useEffect(() => {
        if (!isExpanded || !showAuthenticatedDetails || !user?.id) {
            return;
        }
        refreshBookInviteAssignments().catch(() => undefined);
    }, [
        isExpanded,
        user?.id,
        showAuthenticatedDetails,
        refreshBookInviteAssignments,
    ]);

    /**
     * Measures live dialog-body content height while expanded so the container can
     * resize smoothly for content changes (including Book Invites panel toggles).
     */
    useEffect(() => {
        if (!isExpanded) {
            // Skip measurement work while collapsed; closed state uses fixed avatar sizing.
            return undefined;
        }

        let animationFrameId = 0;
        const updateMeasuredDialogHeights = (): void => {
            const dialogBodyElement = dialogBodyRef.current;

            const nextDialogBodyContentHeight = dialogBodyElement
                ? Math.ceil(dialogBodyRef.current.scrollHeight)
                : 0;

            setMeasuredDialogBodyContentHeight((previousHeight) => (
                previousHeight === nextDialogBodyContentHeight
                    ? previousHeight
                    : nextDialogBodyContentHeight
            ));
        };

        // Prime measurement on open / dependency changes before observer callbacks fire.
        updateMeasuredDialogHeights();

        if (typeof ResizeObserver === 'undefined') {
            return undefined;
        }

        const resizeObserver = new ResizeObserver(() => {
            // Batch resize-driven updates to the next frame to avoid layout thrash/jank.
            cancelAnimationFrame(animationFrameId);
            animationFrameId = requestAnimationFrame(updateMeasuredDialogHeights);
        });

        if (dialogBodyRef.current) {
            resizeObserver.observe(dialogBodyRef.current);
        }

        return () => {
            // Clear any queued frame and detach observer to prevent stale updates/leaks.
            cancelAnimationFrame(animationFrameId);
            resizeObserver.disconnect();
        };
    }, [
        isExpanded,
        showBookInvitesPage,
        editingUserName,
    ]);

    const gifterDisplayName = useMemo(() => {
        const fullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
        if (fullName.length > 0) return fullName;
        const fallbackEmail = (userEmail || '').trim().toLowerCase();
        if (fallbackEmail.length > 0) {
            const inferred = inferNameFromEmail(fallbackEmail);
            const inferredFullName = `${inferred.firstName || ''} ${inferred.lastName || ''}`.trim();
            if (inferredFullName.length > 0) return inferredFullName;
        }
        return 'Someone';
    }, [user?.firstName, user?.lastName, userEmail]);
    const createInvitePreviewForDisplay = useMemo(() => (
        bookInviteCreateInviteEmailPreview || BUILD_BOOK_INVITE_PREVIEW({
            recipientEmail: normalizeEmail(bookInviteRecipientEmail) || 'recipient@example.com',
            recipientName: bookInviteRecipientName,
            inviteMessage: bookInviteMessage,
            gifterDisplayName,
            gifterEmail: userEmail || undefined,
        })
    ), [
        bookInviteCreateInviteEmailPreview,
        bookInviteRecipientEmail,
        bookInviteRecipientName,
        bookInviteMessage,
        gifterDisplayName,
        userEmail,
    ]);

    useEffect(() => {
        if (!showBookInviteCreateInvitePreview) return;
        let isCanceled = false;
        setBookInviteCreateInviteEmailPreview(null);
        setBookInviteCreateInviteEmailPreviewLoading(true);
        getBookInvitePreview({
            recipientEmail: normalizeEmail(bookInviteRecipientEmail) || 'recipient@example.com',
            recipientName: bookInviteRecipientName,
            inviteMessage: bookInviteMessage,
            gifterDisplayName,
            gifterEmail: userEmail || undefined,
        })
            .then((preview: IBookInvitePreview) => {
                if (isCanceled) return;
                setBookInviteCreateInviteEmailPreview(preview);
            })
            .catch(() => {
                if (isCanceled) return;
                setBookInviteCreateInviteEmailPreview(null);
            })
            .finally(() => {
                if (isCanceled) return;
                setBookInviteCreateInviteEmailPreviewLoading(false);
            });
        return () => {
            isCanceled = true;
        };
    }, [
        showBookInviteCreateInvitePreview,
        bookInviteRecipientEmail,
        bookInviteRecipientName,
        bookInviteMessage,
        gifterDisplayName,
        userEmail,
    ]);

    // ===== Memoization =====

    const dialogBodyContainerHeight = useMemo(() => {
        const presentedContentHeight = measuredDialogBodyContentHeight;
        const dialogBodyBottomBuffer = showBookInvitesPage
            ? (0.35 * BODY_FONT_SIZE)
            : (DIALOG_BODY_CONTENT_BOTTOM_BUFFER_MULTIPLIER * BODY_FONT_SIZE);
        const measuredDialogContainerHeight = presentedContentHeight > 0
            ? presentedContentHeight + dialogBodyBottomBuffer
            : 0;
        return Math.max(
            USER_PROFILE_AVATAR_LENGTH_MULTIPLIER * BODY_FONT_SIZE,
            measuredDialogContainerHeight,
        );
    }, [
        showBookInvitesPage,
        measuredDialogBodyContentHeight,
    ]);

    const bookInviteCancelBookInviteModalMessage: IModalMessage = useMemo(() => {
        const normalizedRecipientName = `${bookInvitePendingCancelAssignment?.recipientName || ''}`.trim();
        const recipientEmail = bookInvitePendingCancelAssignment?.recipientEmail || 'this recipient';
        const recipientLabel = normalizedRecipientName.length > 0
            ? `${normalizedRecipientName} (${recipientEmail})`
            : recipientEmail;
        return {
            title: BOOK_INVITE_CANCEL_BOOK_INVITE_MODAL_TITLE,
            message: BOOK_INVITE_CANCEL_BOOK_INVITE_MODAL_MESSAGE(
                recipientLabel,
            ),
        };
    }, [
        bookInvitePendingCancelAssignment?.recipientName,
        bookInvitePendingCancelAssignment?.recipientEmail,
    ]);

    const bookInviteSendBookInviteModalMessage: IModalMessage = useMemo(() => ({
        title: BOOK_INVITE_SEND_BOOK_INVITE_MODAL_TITLE,
        message: BOOK_INVITE_SEND_BOOK_INVITE_MODAL_MESSAGE(
            bookInvitePendingSendInviteRecipientLabel
            || bookInvitePendingSendInviteRecipientEmail
            || 'this recipient',
        ),
    }), [
        bookInvitePendingSendInviteRecipientLabel,
        bookInvitePendingSendInviteRecipientEmail,
    ]);

    const bookInviteResendBookInviteModalMessage: IModalMessage = useMemo(() => {
        const normalizedRecipientName = `${bookInvitePendingResendAssignment?.recipientName || ''}`.trim();
        const recipientEmail = bookInvitePendingResendAssignment?.recipientEmail || 'this recipient';
        const recipientLabel = normalizedRecipientName.length > 0
            ? `${normalizedRecipientName} (${recipientEmail})`
            : recipientEmail;
        return {
            title: BOOK_INVITE_RESEND_BOOK_INVITE_MODAL_TITLE,
            message: BOOK_INVITE_RESEND_BOOK_INVITE_MODAL_MESSAGE(
                recipientLabel,
            ),
        };
    }, [
        bookInvitePendingResendAssignment?.recipientName,
        bookInvitePendingResendAssignment?.recipientEmail,
    ]);

    const bookInviteCancelCreateInviteDraftModalMessage: IModalMessage = useMemo(() => ({
        title: BOOK_INVITE_CANCEL_CREATE_INVITE_DRAFT_MODAL_TITLE,
        message: BOOK_INVITE_CANCEL_CREATE_INVITE_DRAFT_MODAL_MESSAGE,
    }), []);
    const postAccessMap = useMemo(() => userPostAccess || new Map(), [userPostAccess]);

    const hasBookAccess: IPostAccessItem = useMemo(() => {
        if (postAccessMap != null) {
            return postAccessMap.get(BOOK_POST_ID);
        }
        return false;
    }, [postAccessMap]);

    const bookLimitedAccessItems = useMemo(() => {
        if (
            hasBookAccess
            && hasBookAccess.limitedAccessItems
        ) {
            return new Map(parseSpecialNumbers<Map<string, number>>(hasBookAccess.limitedAccessItems)) as Map<string, number>;
        }
        return null;
    }, [hasBookAccess]);

    const bookAccessText = useMemo(() => {
        if (hasBookAccess || userIsAdmin) {
            const bookAccess = postAccessMap.get(BOOK_POST_ID);
            if (
                (
                    bookAccess
                    && bookAccess.scope === POST_ACCESS_ITEM_SCOPE.full
                ) || userIsAdmin
            ) {
                // User has purchased full access to the book
                return 'Full access';
            }

            if (
                bookAccess
                && bookAccess.scope === POST_ACCESS_ITEM_SCOPE.full
                && bookAccess.expires != null
            ) {
                // User has purchased full access to the book for a limited time
                const timeUntil = formatTimeToFutureTimestamp(bookAccess.expires);
                return `${timeUntil} left`;
            }

            if (
                bookAccess
                && bookAccess.scope === POST_ACCESS_ITEM_SCOPE.limited
                && !bookAccess.expires
                && bookLimitedAccessItems
            ) {
                // User has purchased access to certain chapters of the book
                const chapterAccessCount = bookLimitedAccessItems.size;
                return `${chapterAccessCount} ${chapterAccessCount === 1 ? 'chapter' : 'chapters'}`;
            }
        }
        // User doesn't have
        return 'Paywalled';
    }, [
        userIsAdmin,
        postAccessMap,
        hasBookAccess,
        bookLimitedAccessItems,
    ]);

    const parseSerializedBookInviteMap = useCallback((serializedMap?: string): Map<string, unknown> => {
        if (!serializedMap || serializedMap.trim().length === 0) {
            return new Map<string, unknown>();
        }

        try {
            const parsed = parseSpecialNumbers<unknown>(serializedMap);
            if (Array.isArray(parsed)) {
                return new Map<string, unknown>(parsed as [string, unknown][]);
            }
            if (parsed && typeof parsed === 'object') {
                return new Map<string, unknown>(Object.entries(parsed as Record<string, unknown>));
            }
        } catch {
            return new Map<string, unknown>();
        }

        return new Map<string, unknown>();
    }, []);

    const parsedBookInvitePoolMap = useMemo(() => (
        parseSerializedBookInviteMap(userInviteState?.invitePool) as Map<string, IBookInvitePoolItem>
    ), [
        parseSerializedBookInviteMap,
        userInviteState?.invitePool,
    ]);

    const parsedBookInviteAssignmentMap = useMemo(() => (
        parseSerializedBookInviteMap(userInviteState?.inviteAssignments) as Map<string, IBookInviteAssignmentItem>
    ), [
        parseSerializedBookInviteMap,
        userInviteState?.inviteAssignments,
    ]);

    const bookInvitePoolMap = parsedBookInvitePoolMap;
    const bookInviteAssignmentMap = parsedBookInviteAssignmentMap;
    const bookInviteAssignments = useMemo(() => (
        Array.from(bookInviteAssignmentMap.values())
            .sort((left, right) => (
                (right.updated || right.claimedAt || right.invitedAt || 0)
                - (left.updated || left.claimedAt || left.invitedAt || 0)
            ))
    ), [bookInviteAssignmentMap]);
    const bookInviteAssignmentsForDisplay = useMemo(() => (
        bookInviteAssignments.filter((assignment) => (
            assignment.status !== BOOK_INVITE_ASSIGNMENT_STATUS.unassigned
        ))
    ), [bookInviteAssignments]);
    const fallbackBookInviteSummary = useMemo<IUserInviteState>(() => {
        const poolItems = Array.from(bookInvitePoolMap.values());
        const total = poolItems.reduce((sum, poolItem) => sum + (poolItem.total || 0), 0);
        const remaining = poolItems.reduce((sum, poolItem) => sum + (poolItem.unassigned || 0), 0);
        const pending = bookInviteAssignmentsForDisplay
            .filter((assignment) => assignment.status === BOOK_INVITE_ASSIGNMENT_STATUS.pending)
            .length;
        const claimed = bookInviteAssignmentsForDisplay
            .filter((assignment) => assignment.status === BOOK_INVITE_ASSIGNMENT_STATUS.claimed)
            .length;
        return {
            total,
            remaining,
            pending,
            claimed,
        };
    }, [
        bookInvitePoolMap,
        bookInviteAssignmentsForDisplay,
    ]);
    const bookInviteSummary = userInviteState?.inviteSummary || fallbackBookInviteSummary;
    const normalizedBookInviteRecipientEmail = normalizeEmail(bookInviteRecipientEmail);
    const bookInviteRecipientInputContainsDelimiters = (
        bookInviteRecipientEmail.includes(',')
        || bookInviteRecipientEmail.includes('\n')
    );
    const bookInviteRecipientEmailInvalid = (
        !bookInviteRecipientInputContainsDelimiters
        && bookInviteRecipientEmail.trim().length > 0
        && !validateEmail(normalizedBookInviteRecipientEmail)
    );
    const bookInviteRecipientEmailValidationError = (
        (
            bookInviteRecipientInputContainsDelimiters
            || bookInviteRecipientEmailInvalid
        ) ? SNACKBAR_MESSAGE_BOOK_INVITE_INVALID_RECIPIENT_EMAIL : ''
    );
    const showBookInviteCreateInviteSubmitButton = (
        normalizedBookInviteRecipientEmail.length > 0
        && bookInviteRecipientEmailValidationError.length === 0
    );
    const remainingSummaryAccent: BOOK_INVITE_SUMMARY_ACCENT = bookInviteSummary.remaining > 0
        ? BOOK_INVITE_SUMMARY_ACCENT.orange
        : BOOK_INVITE_SUMMARY_ACCENT.red;
    const canOpenBookInviteCreateInvite = bookInviteSummary.remaining > 0 && !showBookInviteCreateInvite;

    const fetchBookInvitePreviewByAssignment = useCallback(async (
        assignment: IBookInviteAssignmentItem,
    ): Promise<void> => {
        if (
            bookInviteAssignmentEmailPreviews[assignment.id]
            || bookInviteAssignmentEmailPreviewLoadingIds[assignment.id]
        ) {
            return;
        }
        setBookInviteAssignmentEmailPreviewLoadingIds((previous) => ({
            ...previous,
            [assignment.id]: true,
        }));
        try {
            const preview = await getBookInvitePreview({
                recipientEmail: assignment.recipientEmail,
                inviteMessage: assignment.inviteMessage,
                gifterDisplayName,
                gifterEmail: userEmail || undefined,
            });
            setBookInviteAssignmentEmailPreviews((previous) => ({
                ...previous,
                [assignment.id]: preview,
            }));
        } finally {
            setBookInviteAssignmentEmailPreviewLoadingIds((previous) => ({
                ...previous,
                [assignment.id]: false,
            }));
        }
    }, [
        bookInviteAssignmentEmailPreviews,
        bookInviteAssignmentEmailPreviewLoadingIds,
        gifterDisplayName,
        userEmail,
    ]);

    const updateBookInviteAssignmentsScrollHint = useCallback((): void => {
        const list = bookInviteAssignmentsListRef.current;
        if (!list) {
            setShowBookInviteAssignmentsScrollHint(false);
            return;
        }

        const hasOverflow = list.scrollHeight - list.clientHeight > 1;
        const isAtBottom = (list.scrollTop + list.clientHeight) >= (list.scrollHeight - 2);

        setShowBookInviteAssignmentsScrollHint(hasOverflow && !isAtBottom);
    }, []);

    const onBookInviteAssignmentsListScroll = useCallback((): void => {
        updateBookInviteAssignmentsScrollHint();
    }, [updateBookInviteAssignmentsScrollHint]);

    useEffect(() => {
        if (bookInviteSummary.remaining <= 0 && showBookInviteCreateInvite) {
            setShowBookInviteCreateInvite(false);
        }
    }, [
        bookInviteSummary.remaining,
        showBookInviteCreateInvite,
    ]);

    useEffect(() => {
        if (!showBookInvitesPage) {
            setShowBookInviteAssignmentsScrollHint(false);
            return;
        }
        if (bookInviteAssignmentsForDisplay.length === 0) {
            setShowBookInviteAssignmentsScrollHint(false);
            return;
        }

        const timeoutId = window.setTimeout(() => {
            updateBookInviteAssignmentsScrollHint();
        }, 0);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [
        showBookInvitesPage,
        showBookInviteCreateInvite,
        bookInviteAssignmentsForDisplay.length,
        updateBookInviteAssignmentsScrollHint,
    ]);

    const getBookInviteAssignmentStatusText = (
        status: BOOK_INVITE_ASSIGNMENT_STATUS,
    ): string => {
        switch (status) {
        case BOOK_INVITE_ASSIGNMENT_STATUS.pending:
            return 'Pending';
        case BOOK_INVITE_ASSIGNMENT_STATUS.claimed:
            return 'Claimed';
        case BOOK_INVITE_ASSIGNMENT_STATUS.revoked:
            return 'Revoked';
        case BOOK_INVITE_ASSIGNMENT_STATUS.expired:
            return 'Expired';
        default:
            return 'Unassigned';
        }
    };

    const getBookInviteAssignmentStatusAccent = (
        status: BOOK_INVITE_ASSIGNMENT_STATUS,
    ): BOOK_INVITE_ASSIGNMENT_ACCENT => {
        switch (status) {
        case BOOK_INVITE_ASSIGNMENT_STATUS.pending:
            return BOOK_INVITE_ASSIGNMENT_ACCENT.purple;
        case BOOK_INVITE_ASSIGNMENT_STATUS.claimed:
            return BOOK_INVITE_ASSIGNMENT_ACCENT.green;
        case BOOK_INVITE_ASSIGNMENT_STATUS.revoked:
        case BOOK_INVITE_ASSIGNMENT_STATUS.expired:
            return BOOK_INVITE_ASSIGNMENT_ACCENT.red;
        default:
            return BOOK_INVITE_ASSIGNMENT_ACCENT.neutral;
        }
    };

    const getBookInviteAssignmentStatusTooltipText = (
        status: BOOK_INVITE_ASSIGNMENT_STATUS,
    ): string => {
        switch (status) {
        case BOOK_INVITE_ASSIGNMENT_STATUS.pending:
            return BOOK_INVITE_STATUS_PENDING_TOOLTIP_TEXT;
        case BOOK_INVITE_ASSIGNMENT_STATUS.claimed:
            return BOOK_INVITE_STATUS_CLAIMED_TOOLTIP_TEXT;
        case BOOK_INVITE_ASSIGNMENT_STATUS.revoked:
            return BOOK_INVITE_STATUS_REVOKED_TOOLTIP_TEXT;
        default:
            return '';
        }
    };

    const getBookInviteAssignmentHeading = (
        assignment: IBookInviteAssignmentItem,
    ): string => {
        const normalizedRecipientName = assignment.recipientName?.trim();
        if (normalizedRecipientName && normalizedRecipientName.length > 0) {
            return normalizedRecipientName;
        }
        return assignment.recipientEmail;
    };

    const getBookInviteAssignmentStatusMeta = (
        assignment: IBookInviteAssignmentItem,
    ): string => {
        const normalizedRecipientName = assignment.recipientName?.trim();
        const headerUsesRecipientName = !!(normalizedRecipientName && normalizedRecipientName.length > 0);
        const getTimestampLabel = (
            prefix: string,
            timestamp?: number,
        ): string => (
            typeof timestamp === 'number' && timestamp > 0
                ? `${prefix} ${moment(timestamp).format('MMM Do, YYYY')}`
                : prefix.trim()
        );
        const formatMeta = (
            prefix: string,
            timestamp?: number,
        ): string => {
            const timestampLabel = getTimestampLabel(prefix, timestamp);
            if (!headerUsesRecipientName) {
                return timestampLabel;
            }
            return `${assignment.recipientEmail} • ${timestampLabel}`;
        };
        switch (assignment.status) {
        case BOOK_INVITE_ASSIGNMENT_STATUS.claimed:
            return formatMeta('Claimed', assignment.claimedAt);
        case BOOK_INVITE_ASSIGNMENT_STATUS.pending:
            return formatMeta('Invited', assignment.invitedAt);
        case BOOK_INVITE_ASSIGNMENT_STATUS.revoked:
            return formatMeta('Revoked', assignment.revokedAt);
        case BOOK_INVITE_ASSIGNMENT_STATUS.expired:
            return formatMeta('Expired', assignment.expiredAt);
        default:
            return headerUsesRecipientName ? assignment.recipientEmail : '';
        }
    };

    // ===== Rendering =====

    const authMethods: IAuthMethod[] = [
        {
            type: AUTHENTICATION_TYPE.google,
            text: 'Sign in with Google',
            icon: GoogleIcon,
            background: '#EA4335',
            onClick: () => onAuthentication(AUTHENTICATION_TYPE.google),
        },
        {
            type: AUTHENTICATION_TYPE.github,
            text: 'Sign in with GitHub',
            icon: GithubIcon,
            background: '#6E7681',
            onClick: () => onAuthentication(AUTHENTICATION_TYPE.github),
        },
        {
            type: AUTHENTICATION_TYPE.facebook,
            text: 'Sign in with Facebook',
            icon: FacebookIcon,
            background: '#1877F2',
            onClick: () => onAuthentication(AUTHENTICATION_TYPE.facebook),
        },
        {
            type: AUTHENTICATION_TYPE.xTwitter,
            text: 'Sign in with X',
            icon: XTwitterIcon,
            background: '#000000',
            onClick: () => onAuthentication(AUTHENTICATION_TYPE.xTwitter),
        },
        {
            type: AUTHENTICATION_TYPE.email,
            text: 'Sign in with Email',
            icon: EnvelopeIcon,
            background: theme.verascopeColor.green200,
            onClick: () => onAuthentication(AUTHENTICATION_TYPE.email),
        },
    ];

    const buttonAnimations = useMemo(() => {
        let animations: Map<string, Keyframes> = new Map();
        for (let i = 1; i < NUM_EMAIL_AUTH_TABS; i += 1) {
            const moveIndexLeft = getMoveIndexLeftKeyframe(
                EMAIL_AUTH_TAB_WIDTH_MULTIPLIER * BODY_FONT_SIZE,
                i,
                EMAIL_AUTH_TAB_MARGIN,
                KEYFRAME_ANCHOR_TYPE.left,
            );
            const moveIndexRight = getMoveIndexRightKeyframe(
                EMAIL_AUTH_TAB_WIDTH_MULTIPLIER * BODY_FONT_SIZE,
                i,
                EMAIL_AUTH_TAB_MARGIN,
                KEYFRAME_ANCHOR_TYPE.left,
            );
            animations = animations.set(
                `move${i}Right`,
                moveIndexRight,
            );
            animations = animations.set(
                `move${i}Left`,
                moveIndexLeft,
            );
        }

        return animations;
    }, [
        movingButtonCurrentIndex,
    ]);

    let animation: FlattenSimpleInterpolation | null = null;
    if (movingButtonBackground.active) {
        const keyframe = buttonAnimations.get(
            `move${
                Math.abs(movingButtonBackground.desiredIndex - movingButtonCurrentIndex)
            }${
                movingButtonBackground.desiredIndex > movingButtonCurrentIndex
                    ? 'Right'
                    : 'Left'
            }`,
        );

        // Error: https://stackoverflow.com/questions/67601508/why-do-my-styled-component-keyframes-error-with-ts-styled-plugin9999-in-react
        // eslint-disable-next-line max-len
        animation = css`${keyframe} ${Math.min(Math.abs(movingButtonBackground.desiredIndex - movingButtonCurrentIndex) * MOVING_BUTTON_BACKGROUND_TRANSITION_DURATION, MOVING_BUTTON_BACKGROUND_TRANSITION_MAX_DURATION)}ms${` ${theme.motion.delayEasing}`}`;
    }

    const AuthTypeIcon = useMemo(() => {
        if (showAuthenticatedDetails && resolvedAuthenticatedType) {
            switch (resolvedAuthenticatedType) {
            case AUTHENTICATION_TYPE.google:
                return GoogleIcon;
            case AUTHENTICATION_TYPE.github:
                return GithubIcon;
            case AUTHENTICATION_TYPE.facebook:
                return FacebookIcon;
            case AUTHENTICATION_TYPE.xTwitter:
                return XTwitterIcon;
            case AUTHENTICATION_TYPE.email:
                return EnvelopeIcon;
            default:
                // We shouldn't hit here
                // If we do we have a problem
                // Anonymouse users shouldn't need this
            }
        }

        return null;
    }, [
        resolvedAuthenticatedType,
        showAuthenticatedDetails,
    ]);

    const authTypeText = useMemo(() => {
        if (showAuthenticatedDetails && resolvedAuthenticatedType) {
            switch (resolvedAuthenticatedType) {
            case AUTHENTICATION_TYPE.google:
                return 'Google Sign-in';
            case AUTHENTICATION_TYPE.github:
                return 'Github Sign-in';
            case AUTHENTICATION_TYPE.facebook:
                return 'Facebook Sign-in';
            case AUTHENTICATION_TYPE.xTwitter:
                return 'X/Twitter Sign-in';
            case AUTHENTICATION_TYPE.email:
                return 'Email Sign-in';
            default:
                // We shouldn't hit here
                // If we do we have a problem
                // Anonymouse users shouldn't need this
            }
        }

        return null;
    }, [
        resolvedAuthenticatedType,
        showAuthenticatedDetails,
    ]);
    // If account is social-linked, verification is managed by provider identity.
    // Hide email verify CTA to avoid showing irrelevant email-only action.
    const hasLinkedNonEmailProvider = (auth.currentUser?.providerData || []).some((provider) => (
        !!provider.providerId
        && provider.providerId !== FIREBASE_AUTH_SIGN_IN_PROVIDER_ID_TYPE.email
        && provider.providerId !== FIREBASE_AUTH_SIGN_IN_PROVIDER_ID_TYPE.anonymous
    ));
    const shouldShowEmailVerificationAction = !!(
        user
        && resolvedAuthenticatedType === AUTHENTICATION_TYPE.email
        && !hasLinkedNonEmailProvider
    );
    const emailAuthActionIsLoading = signingInOut || !!(auth.currentUser && !auth.currentUser.isAnonymous && !signedIn);
    const providerAuthActionIsLoading = providerAuthAwaitingBackend || signingInOut;

    const numAuthUserDetails = NUM_AUTH_USER_DETAILS;
    const BOOK_INVITEES_DIALOG_WIDTH_MULTIPLIER = 1.2;
    const PROFILE_DETAILS_DIALOG_WIDTH_MULTIPLIER = 1.1;
    const dialogBodyExpandedWidth = useMemo(() => (
        USER_PROFILE_DIALOG_BODY_WIDTH_EXPANDED_MULTIPLIER
        * BODY_FONT_SIZE
        * (showBookInvitesPage ? BOOK_INVITEES_DIALOG_WIDTH_MULTIPLIER : PROFILE_DETAILS_DIALOG_WIDTH_MULTIPLIER)
    ), [showBookInvitesPage]);
    const isExtraSmallViewport = viewportDimensions.width <= MEDIA_QUERY_SIZE.extraSmall.max;
    const isMobileBookInvitesFullscreen = isExtraSmallViewport
        && isExpanded
        && showBookInvitesPage;
    const avatarContainerExpandedRight = (
        AVATAR_CONTAINER_OFFSET_RIGHT_EXPANDED_MULTIPLIER * BODY_FONT_SIZE
    ) + dialogBodyExpandedWidth;
    const getInfoDisclosureId = useCallback((disclosureKey: USER_PROFILE_INFO_DISCLOSURE_KEY): string => (
        `user-profile-info-disclosure-${disclosureKey}`
    ), []);
    const renderInfoDisclosureButton = useCallback((
        disclosureKey: USER_PROFILE_INFO_DISCLOSURE_KEY,
        buttonLabel: USER_PROFILE_INFO_DISCLOSURE_BUTTON_LABEL,
        accent?: BOOK_INVITE_SUMMARY_ACCENT,
        bookInvitesSummaryTopRight?: boolean,
    ): JSX.Element => (
        <InfoDisclosureButtonContainer $bookInvitesSummaryTopRight={bookInvitesSummaryTopRight}>
            <InfoDisclosureButton
                className={HOVER_TARGET_CLASSNAME}
                customCursorActive={customCursorActive}
                aria-label={buttonLabel}
                aria-expanded={activeInfoDisclosureKey === disclosureKey}
                aria-controls={getInfoDisclosureId(disclosureKey)}
                {...getTouchMouseEventHandlers({
                    onMouseEnter: onButtonMouseEnter,
                    onMouseLeave: onButtonMouseLeave,
                })}
                {...getPressEventHandlers({
                    onMouseDown: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onInfoDisclosureButtonClick(disclosureKey);
                    },
                })}
            >
                <InfoDisclosureIcon src={InfoIcon} $accent={accent} />
            </InfoDisclosureButton>
        </InfoDisclosureButtonContainer>
    ), [
        customCursorActive,
        activeInfoDisclosureKey,
        getInfoDisclosureId,
        onButtonMouseEnter,
        onButtonMouseLeave,
        onInfoDisclosureButtonClick,
    ]);

    return (
        <Container
            isExpanded={isExpanded}
            lightBackground={lightBackground}
            transitionDuration={HEADER_BUTTON_TRANSITION_DURATION}
            style={style}
            {...(!isExpanded
                ? {
                    onClick: onToggleExpand,
                }
                : {}
            )}
        >
            <AvatarContainer
                data-testid="user-profile-avatar-trigger"
                {...getRootProps({
                    className: HOVER_TARGET_CLASSNAME,
                    onMouseEnter: onAvatarMouseEnter,
                    onMouseLeave: onAvatarMouseLeave,
                    ...(isExpanded ? { onClick: selectImage } : {}),
                })}
                length={USER_PROFILE_AVATAR_LENGTH_MULTIPLIER * BODY_FONT_SIZE}
                isExpanded={isExpanded}
                contractedTop={USER_PROFILE_DIALOG_TOP_CONTRACTED_MULTIPLIER * BODY_FONT_SIZE}
                contractedRight={USER_PROFILE_DIALOG_RIGHT_CONTRACTED_MULTIPLIER * BODY_FONT_SIZE}
                expandedTop={AVATAR_CONTAINER_OFFSET_TOP_EXPANDED_MULTIPLIER * BODY_FONT_SIZE}
                expandedRight={avatarContainerExpandedRight}
                translateDuration={AVATAR_TRANSLATE_DURATION}
                anonIconId={ANONYMOUS_AVATAR_ICON_ID}
                avatarLength={USER_PROFILE_AVATAR_LENGTH_MULTIPLIER * BODY_FONT_SIZE}
                dialogBodyPadding={DIALOG_BODY_PADDING_MULTIPLIER * BODY_FONT_SIZE}
                customCursorActive={customCursorActive}
            >
                {!uploadingMedia
                && isExpanded
                && (<DropzoneInput {...getInputProps()} />)}
                {avatarURL || (user && !!user.photoURL)
                    ? (
                        <UserAvatar
                            url={avatarURL || user!.photoURL!} // Has to be present if avatarURL is false
                            length={USER_PROFILE_AVATAR_LENGTH_MULTIPLIER * BODY_FONT_SIZE}
                        />
                    ) : (
                        <ReactSVG
                            id={ANONYMOUS_AVATAR_ICON_ID}
                            src={SmileyIcon}
                        />
                    )}
                <AvatarOverlay
                    length={USER_PROFILE_AVATAR_LENGTH_MULTIPLIER * BODY_FONT_SIZE}
                    uploadingImage={!!uploadingMedia}
                    transitionDuration={OVERLAY_TRANSITION_DURATION}
                >
                    {uploadingMedia
                        ? (
                            <AvatarUploadProgressBarContainer
                                length={USER_PROFILE_AVATAR_LENGTH_MULTIPLIER * BODY_FONT_SIZE}
                            >
                                <AvatarUploadProgressBar
                                    progress={uploadingMedia.uploadProgress}
                                />
                            </AvatarUploadProgressBarContainer>
                        ) : (
                            <ReactSVG
                                src={CameraIcon}
                            />
                        )}
                </AvatarOverlay>
            </AvatarContainer>
            <DialogBodyContainer
                contractedTop={USER_PROFILE_DIALOG_TOP_CONTRACTED_MULTIPLIER * BODY_FONT_SIZE}
                contractedRight={USER_PROFILE_DIALOG_RIGHT_CONTRACTED_MULTIPLIER * BODY_FONT_SIZE}
                expandedTop={DIALOG_OFFSET_TOP_EXPANDED_MULTIPLIER * BODY_FONT_SIZE}
                expandedRight={DIALOG_OFFSET_RIGHT_EXPANDED_MULTIPLIER * BODY_FONT_SIZE}
                avatarLength={USER_PROFILE_AVATAR_LENGTH_MULTIPLIER * BODY_FONT_SIZE}
                closeButtonContainerLength={USER_PROFILE_CLOSE_BUTTON_CONTAINER_LENGTH_MULTIPLIER * BODY_FONT_SIZE}
                expandedWidth={dialogBodyExpandedWidth}
                expandedHeight={dialogBodyContainerHeight}
                isExpanded={isExpanded}
                isMobileBookInvitesFullscreen={isMobileBookInvitesFullscreen}
                padding={DIALOG_BODY_PADDING_MULTIPLIER * BODY_FONT_SIZE}
                transitionDuration={DIALOG_BODY_CONTAINER_TRANSITION_DURATION}
            >
                {viewportDimensions.width >= MEDIA_QUERY_SIZE.small.min
                && (
                    <CornerCurve
                        isExpanded={isExpanded}
                        closeButtonContainerLength={USER_PROFILE_CLOSE_BUTTON_CONTAINER_LENGTH_MULTIPLIER * BODY_FONT_SIZE}
                        transitionDuration={DIALOG_BODY_CONTAINER_TRANSITION_DURATION}
                    >
                        <ReactSVG
                            src={DialogCornerCurve}
                        />
                    </CornerCurve>
                )}
                {user && (
                    <DialogBody
                        ref={dialogBodyRef}
                        isExpanded={isExpanded}
                        fillHeight={isMobileBookInvitesFullscreen}
                        opacityDuration={DIALOG_BODY_OPACITY_DURATION}
                        transitionDelayDuration={DIALOG_BODY_OPACITY_DELAY_DURATION}
                    >
                        {editingUserName
                            ? (
                                <NameInputContainer
                                    padding={NAME_CONTAINER_PADDING_MULTIPLIER * BODY_FONT_SIZE}
                                >
                                    <NameInput
                                        data-testid="user-profile-first-name-input"
                                        type="text"
                                        ref={firstNameInputRef}
                                        className={HOVER_TARGET_CLASSNAME}
                                        defaultValue={user.firstName}
                                        placeholder="First"
                                        onKeyUp={checkForEnter}
                                        width={NAME_INPUT_WIDTH_MULTIPLIER * BODY_FONT_SIZE}
                                        height={USER_PROFILE_AVATAR_LENGTH_MULTIPLIER * BODY_FONT_SIZE}
                                        {...getTouchMouseEventHandlers({
                                            onMouseEnter: onInputMouseEnter,
                                            onMouseLeave: onInputMouseLeave,
                                        })}
                                        onFocus={onInputFocus}
                                        onBlur={onInputBlur}
                                    />
                                    <NameInput
                                        data-testid="user-profile-last-name-input"
                                        type="text"
                                        ref={lastNameInputRef}
                                        className={HOVER_TARGET_CLASSNAME}
                                        defaultValue={user.lastName}
                                        placeholder="Last"
                                        onKeyUp={checkForEnter}
                                        width={NAME_INPUT_WIDTH_MULTIPLIER * BODY_FONT_SIZE}
                                        height={USER_PROFILE_AVATAR_LENGTH_MULTIPLIER * BODY_FONT_SIZE}
                                        {...getTouchMouseEventHandlers({
                                            onMouseEnter: onInputMouseEnter,
                                            onMouseLeave: onInputMouseLeave,
                                        })}
                                        onFocus={onInputFocus}
                                        onBlur={onInputBlur}
                                    />
                                    <NameButtonContainer
                                        width={2 * NAME_BUTTON_LENGTH + NAME_BUTTON_SPACING_WIDTH}
                                    >
                                        <Button
                                            className={HOVER_TARGET_CLASSNAME}
                                            customCursorActive={customCursorActive}
                                            type={BUTTON_TYPE.solid}
                                            background={theme.verascopeColor.green300}
                                            height={NAME_BUTTON_LENGTH}
                                            icon={CheckmarkIcon}
                                            {...getTouchMouseEventHandlers({
                                                onMouseEnter: onButtonMouseEnter,
                                                onMouseLeave: onButtonMouseLeave,
                                            })}
                                            {...getPressEventHandlers({ onMouseDown: onSaveEditName })}
                                        >
                                            <Tooltip
                                                text="Save"
                                                side={TOOLTIP_TYPE.bottom}
                                            />
                                        </Button>
                                        <Button
                                            className={HOVER_TARGET_CLASSNAME}
                                            customCursorActive={customCursorActive}
                                            type={BUTTON_TYPE.solid}
                                            background={theme.verascopeColor.red300}
                                            height={NAME_BUTTON_LENGTH}
                                            icon={CrossIcon}
                                            {...getTouchMouseEventHandlers({
                                                onMouseEnter: onButtonMouseEnter,
                                                onMouseLeave: onButtonMouseLeave,
                                            })}
                                            {...getPressEventHandlers({ onMouseDown: onCancelEditName })}
                                        >
                                            <Tooltip
                                                text="Cancel"
                                                side={TOOLTIP_TYPE.bottom}
                                            />
                                        </Button>
                                    </NameButtonContainer>
                                </NameInputContainer>
                            ) : (
                                <NameContainer
                                    padding={showAuthenticatedDetails ? NAME_CONTAINER_PADDING_MULTIPLIER * BODY_FONT_SIZE : 0}
                                >
                                    <NameText>
                                        {`${user?.firstName} ${user?.lastName}`}
                                    </NameText>
                                    <NameButtonContainer
                                        width={NAME_BUTTON_LENGTH}
                                    >
                                        <Button
                                            className={HOVER_TARGET_CLASSNAME}
                                            customCursorActive={customCursorActive}
                                            type={BUTTON_TYPE.secret}
                                            background={theme.color.neutral600}
                                            height={NAME_BUTTON_LENGTH}
                                            icon={PencilIcon}
                                            {...getTouchMouseEventHandlers({
                                                onMouseEnter: onButtonMouseEnter,
                                                onMouseLeave: onButtonMouseLeave,
                                            })}
                                            {...getPressEventHandlers({ onMouseDown: onEditName })}
                                        >
                                            <Tooltip
                                                text="Edit"
                                                side={TOOLTIP_TYPE.bottom}
                                            />
                                        </Button>
                                    </NameButtonContainer>
                                </NameContainer>
                            )}
                        {isExpanded
                        && user
                        && user.authenticationType === AUTHENTICATION_TYPE.anonymous
                        && showUnauthenticatedAuthControls
                        && (
                            <AuthenticationMessage>
                                {GENERIC_SIGN_IN_BOOK_ACCESS_MESSAGE}
                            </AuthenticationMessage>
                        )}
                        <AuthMethodsContainer
                            isExpanded={isExpanded && showUnauthenticatedAuthControls}
                        >
                            {authMethods.map((authMethod, index, arr) => (
                                <Transition
                                    key={authMethod.type}
                                    in={isExpanded
                                        && user.authenticationType === AUTHENTICATION_TYPE.anonymous
                                        && !showEmailAuthFields
                                        && !transitioningEmailAuthFields
                                        && showUnauthenticatedAuthControls}
                                    timeout={{
                                        enter: Math.min(
                                            MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                            (index * FADE_IN_STAGGER_OFFSET_DURATION)
                                            + FADE_IN_STAGGER_TRANSITION_DURATION
                                            + DIALOG_BODY_OPACITY_DELAY_DURATION,
                                        ),
                                        exit: 0,
                                    }}
                                    appear
                                    mountOnEnter
                                    unmountOnExit
                                >
                                    {(state) => (
                                        <AuthMethod
                                            width={AUTH_METHOD_BUTTON_WIDTH_MULTIPLIER * BODY_FONT_SIZE}
                                            buttonHeight={AUTH_METHOD_BUTTON_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                                            $loading={providerAuthActionIsLoading && signingInMethod === authMethod.type}
                                            style={{
                                                ...FADE_IN_STAGGER_DEFAULT_STYLE({
                                                    direction: 'left',
                                                    duration: AUTH_METHOD_TRANSITION_DURATION,
                                                    offset: 10,
                                                }),
                                                ...FADE_IN_STAGGER_TRANSITION_STYLES({
                                                    direction: 'left',
                                                    offset: 10,
                                                    numItems: arr.length,
                                                    index,
                                                })[state],
                                            }}
                                        >
                                            <Button
                                                className={HOVER_TARGET_CLASSNAME}
                                                customCursorActive={customCursorActive}
                                                type={BUTTON_TYPE.solid}
                                                background={authMethod.background}
                                                height={AUTH_METHOD_BUTTON_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                                                width={AUTH_METHOD_BUTTON_WIDTH_MULTIPLIER * BODY_FONT_SIZE}
                                                icon={authMethod.icon}
                                                text={authMethod.text}
                                                loading={providerAuthActionIsLoading && signingInMethod === authMethod.type}
                                                disabled={providerAuthActionIsLoading}
                                                {...getTouchMouseEventHandlers({
                                                    onMouseEnter: onButtonMouseEnter,
                                                    onMouseLeave: onButtonMouseLeave,
                                                })}
                                                {...(
                                                    providerAuthActionIsLoading ? {} : {
                                                        ...getPressEventHandlers({ onMouseDown: (e) => authMethod.onClick(e) }),
                                                    }
                                                )}
                                            />
                                        </AuthMethod>
                                    )}
                                </Transition>
                            ))}
                        </AuthMethodsContainer>
                        {showUnauthenticatedAuthControls && (
                            <EmailAuthFieldsContainer
                                isVisible={isExpanded && (showEmailAuthFields || transitioningEmailAuthFields)}
                            >
                                <Transition
                                    in={isExpanded && showUnauthenticatedAuthControls && showEmailAuthFields}
                                    timeout={{
                                        enter: Math.min(
                                            MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                            (EMAIL_AUTH_TABS_INDEX * FADE_IN_STAGGER_OFFSET_DURATION)
                                        + FADE_IN_STAGGER_TRANSITION_DURATION,
                                        ),
                                        exit: Math.min(
                                            MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                            ((NUM_EMAIL_AUTH_FIELDS - Math.max(EMAIL_AUTH_TABS_INDEX - 1, 0)) * FADE_IN_STAGGER_OFFSET_DURATION)
                                        + FADE_IN_STAGGER_TRANSITION_DURATION,
                                        ),
                                    }}
                                    appear
                                    mountOnEnter
                                    unmountOnExit
                                >
                                    {(state) => (
                                        <EmailAuthTabsContainer
                                            height={EMAIL_AUTH_TAB_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                                            style={{
                                                ...FADE_IN_STAGGER_DEFAULT_STYLE({
                                                    direction: 'down',
                                                    duration: EMAIL_AUTH_FIELD_TRANSITION_DURATION,
                                                    offset: 10,
                                                }),
                                                ...FADE_IN_STAGGER_TRANSITION_STYLES({
                                                    direction: 'down',
                                                    offset: 10,
                                                    numItems: NUM_EMAIL_AUTH_FIELDS,
                                                    index: EMAIL_AUTH_EMAIL_FIELD_INDEX,
                                                })[state],
                                            }}
                                        >
                                            <MovingButtonBackground
                                                solid
                                                visible
                                                color={EMAIL_AUTH_TAB_BACKGROUND_COLOR}
                                                top={0}
                                                buttonHeight={EMAIL_AUTH_TAB_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                                                buttonWidth={EMAIL_AUTH_TAB_WIDTH_MULTIPLIER * BODY_FONT_SIZE}
                                                animation={animation}
                                                spacing={EMAIL_AUTH_TAB_MARGIN}
                                                xOffset={0}
                                                index={movingButtonCurrentIndex - 1}
                                                transitionDuration={MOVING_BUTTON_BACKGROUND_TRANSITION_DURATION}
                                            />
                                            <EmailAuthTabButtonContainer
                                                data-testid="user-profile-email-tab-sign-in"
                                                index={SIGN_IN_INDEX - 1}
                                                movingButtonCurrentIndex={movingButtonCurrentIndex - 1}
                                                width={EMAIL_AUTH_TAB_WIDTH_MULTIPLIER * BODY_FONT_SIZE}
                                                margin={EMAIL_AUTH_TAB_MARGIN}
                                                background={EMAIL_AUTH_TAB_BACKGROUND_COLOR}
                                            >
                                                <Button
                                                    center
                                                    className={HOVER_TARGET_CLASSNAME}
                                                    customCursorActive={customCursorActive}
                                                    type={BUTTON_TYPE.secret}
                                                    background={theme.color.neutral200}
                                                    height={EMAIL_AUTH_TAB_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                                                    width={EMAIL_AUTH_TAB_WIDTH_MULTIPLIER * BODY_FONT_SIZE}
                                                    text="Sign In"
                                                    {...(isExpanded
                                                        ? {
                                                            onMouseEnter: onButtonMouseEnter,
                                                            onMouseLeave: onButtonMouseLeave,
                                                            onClick: (e) => onEmailAuthTabButtonClick(SIGN_IN_INDEX, e),
                                                        } : {}
                                                    )}
                                                />
                                            </EmailAuthTabButtonContainer>
                                            <EmailAuthTabButtonContainer
                                                data-testid="user-profile-email-tab-sign-up"
                                                index={SIGN_UP_INDEX - 1}
                                                movingButtonCurrentIndex={movingButtonCurrentIndex - 1}
                                                width={EMAIL_AUTH_TAB_WIDTH_MULTIPLIER * BODY_FONT_SIZE}
                                                margin={EMAIL_AUTH_TAB_MARGIN}
                                                background={EMAIL_AUTH_TAB_BACKGROUND_COLOR}
                                            >
                                                <Button
                                                    center
                                                    className={HOVER_TARGET_CLASSNAME}
                                                    customCursorActive={customCursorActive}
                                                    type={BUTTON_TYPE.secret}
                                                    background={theme.color.neutral200}
                                                    height={EMAIL_AUTH_TAB_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                                                    width={EMAIL_AUTH_TAB_WIDTH_MULTIPLIER * BODY_FONT_SIZE}
                                                    text="Sign Up"
                                                    {...(isExpanded
                                                        ? {
                                                            onMouseEnter: onButtonMouseEnter,
                                                            onMouseLeave: onButtonMouseLeave,
                                                            onClick: (e) => onEmailAuthTabButtonClick(SIGN_UP_INDEX, e),
                                                        } : {}
                                                    )}
                                                />
                                            </EmailAuthTabButtonContainer>
                                        </EmailAuthTabsContainer>
                                    )}
                                </Transition>
                                <Transition
                                    in={isExpanded && showUnauthenticatedAuthControls && !!user && showEmailAuthFields}
                                    timeout={{
                                        enter: Math.min(
                                            MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                            (EMAIL_AUTH_EMAIL_FIELD_INDEX * FADE_IN_STAGGER_OFFSET_DURATION)
                                        + FADE_IN_STAGGER_TRANSITION_DURATION,
                                        ),
                                        exit: Math.min(
                                            MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                            ((NUM_EMAIL_AUTH_FIELDS - Math.max(EMAIL_AUTH_EMAIL_FIELD_INDEX - 1, 0)) * FADE_IN_STAGGER_OFFSET_DURATION)
                                        + FADE_IN_STAGGER_TRANSITION_DURATION,
                                        ),
                                    }}
                                    appear
                                    mountOnEnter
                                    unmountOnExit
                                >
                                    {(state) => (
                                        <EmailAuthInputContainer
                                            style={{
                                                ...FADE_IN_STAGGER_DEFAULT_STYLE({
                                                    direction: 'down',
                                                    duration: EMAIL_AUTH_FIELD_TRANSITION_DURATION,
                                                    offset: 10,
                                                }),
                                                ...FADE_IN_STAGGER_TRANSITION_STYLES({
                                                    direction: 'down',
                                                    offset: 10,
                                                    numItems: NUM_EMAIL_AUTH_FIELDS,
                                                    index: EMAIL_AUTH_EMAIL_FIELD_INDEX,
                                                })[state],
                                            }}
                                        >
                                            {!emailValid
                                        && (isSigningUp || hasForgotPassword)
                                        && (
                                            <Tooltip
                                                permanent
                                                text="Please enter a valid email address"
                                                side={TOOLTIP_TYPE.top}
                                                background={theme.verascopeColor.red100}
                                            />
                                        )}
                                            <EmailAuthInput
                                                type="email"
                                                ref={emailAuthInputRef}
                                                className={HOVER_TARGET_CLASSNAME}
                                                defaultValue={userEmail || undefined} // shouldn't be visible if user email is null
                                                placeholder="Email"
                                                onChange={handleEmailChange}
                                                onKeyUp={checkForEnter}
                                                isInvalid={!emailValid && (isSigningUp || hasForgotPassword)}
                                                height={EMAIL_AUTH_INPUT_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                                                {...getTouchMouseEventHandlers({
                                                    onMouseEnter: onInputMouseEnter,
                                                    onMouseLeave: onInputMouseLeave,
                                                })}
                                                onFocus={onInputFocus}
                                                onBlur={onInputBlur}
                                            />
                                        </EmailAuthInputContainer>
                                    )}
                                </Transition>
                                <Transition
                                    in={isExpanded && showUnauthenticatedAuthControls && showEmailAuthFields && !hasForgotPassword}
                                    timeout={{
                                        enter: Math.min(
                                            MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                            (EMAIL_AUTH_PASSWORD_FIELD_INDEX * FADE_IN_STAGGER_OFFSET_DURATION)
                                        + FADE_IN_STAGGER_TRANSITION_DURATION,
                                        ),
                                        exit: Math.min(
                                            MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                            ((NUM_EMAIL_AUTH_FIELDS - Math.max(EMAIL_AUTH_PASSWORD_FIELD_INDEX - 1, 0)) * FADE_IN_STAGGER_OFFSET_DURATION)
                                        + FADE_IN_STAGGER_TRANSITION_DURATION,
                                        ),
                                    }}
                                    appear
                                    mountOnEnter
                                    unmountOnExit
                                >
                                    {(state) => (
                                        <EmailAuthInputContainer
                                            style={{
                                                ...FADE_IN_STAGGER_DEFAULT_STYLE({
                                                    direction: 'down',
                                                    duration: EMAIL_AUTH_FIELD_TRANSITION_DURATION,
                                                    offset: 10,
                                                }),
                                                ...FADE_IN_STAGGER_TRANSITION_STYLES({
                                                    direction: 'down',
                                                    offset: 10,
                                                    numItems: NUM_EMAIL_AUTH_FIELDS,
                                                    index: EMAIL_AUTH_PASSWORD_FIELD_INDEX,
                                                })[state],
                                            }}
                                        >
                                            {passwordErrors.length > 0
                                        && isSigningUp
                                        && (
                                            <Tooltip
                                                permanent
                                                text={passwordErrors[0].message}
                                                side={TOOLTIP_TYPE.top}
                                                background={theme.verascopeColor.red100}
                                            />
                                        )}
                                            <EmailAuthInput
                                                type="password"
                                                ref={passwordAuthInputRef}
                                                className={HOVER_TARGET_CLASSNAME}
                                                placeholder="Password"
                                                onChange={handlePasswordChange}
                                                onKeyUp={checkForEnter}
                                                isInvalid={!passwordValid && isSigningUp}
                                                height={EMAIL_AUTH_INPUT_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                                                {...getTouchMouseEventHandlers({
                                                    onMouseEnter: onInputMouseEnter,
                                                    onMouseLeave: onInputMouseLeave,
                                                })}
                                                onFocus={onInputFocus}
                                                onBlur={onInputBlur}
                                            />
                                            <Transition
                                                in={!!passwordStrength && isSigningUp}
                                                timeout={{
                                                    enter: PASSWORD_STRENGTH_CONTAINER_TRANSITION_DURATION,
                                                    exit: PASSWORD_STRENGTH_CONTAINER_TRANSITION_DURATION,
                                                }}
                                                appear
                                                mountOnEnter
                                                unmountOnExit
                                            >
                                                {(transitionState) => (
                                                    <PasswordStrengthContainer
                                                        style={{
                                                            ...FADE_IN_DEFAULT_STYLE({
                                                                direction: 'down',
                                                                offset: 10,
                                                                duration: PASSWORD_STRENGTH_CONTAINER_TRANSITION_DURATION,
                                                                easing: theme.motion.eagerEasing,
                                                            }),
                                                            ...FADE_IN_TRANSITION_STYLES({
                                                                direction: 'down',
                                                                offset: 10,
                                                            })[transitionState],
                                                        }}
                                                    >
                                                        <PasswordStrengthBar
                                                            hasErrors={passwordErrors.length > 0}
                                                            active={
                                                                passwordStrength === PASSWORD_STRENGTH.veryWeak
                                                            || passwordStrength === PASSWORD_STRENGTH.weak
                                                            || passwordStrength === PASSWORD_STRENGTH.reasonable
                                                            || passwordStrength === PASSWORD_STRENGTH.strong
                                                            || passwordStrength === PASSWORD_STRENGTH.veryStrong
                                                            }
                                                        />
                                                        <PasswordStrengthBar
                                                            hasErrors={passwordErrors.length > 0}
                                                            active={
                                                                passwordStrength === PASSWORD_STRENGTH.weak
                                                            || passwordStrength === PASSWORD_STRENGTH.reasonable
                                                            || passwordStrength === PASSWORD_STRENGTH.strong
                                                            || passwordStrength === PASSWORD_STRENGTH.veryStrong
                                                            }
                                                        />
                                                        <PasswordStrengthBar
                                                            hasErrors={passwordErrors.length > 0}
                                                            active={
                                                                passwordStrength === PASSWORD_STRENGTH.reasonable
                                                            || passwordStrength === PASSWORD_STRENGTH.strong
                                                            || passwordStrength === PASSWORD_STRENGTH.veryStrong
                                                            }
                                                        />
                                                        <PasswordStrengthBar
                                                            hasErrors={passwordErrors.length > 0}
                                                            active={
                                                                passwordStrength === PASSWORD_STRENGTH.strong
                                                            || passwordStrength === PASSWORD_STRENGTH.veryStrong
                                                            }
                                                        />
                                                        <PasswordStrengthBar
                                                            hasErrors={passwordErrors.length > 0}
                                                            active={passwordStrength === PASSWORD_STRENGTH.veryStrong}
                                                        />
                                                    </PasswordStrengthContainer>
                                                )}
                                            </Transition>
                                        </EmailAuthInputContainer>
                                    )}
                                </Transition>
                                <Transition
                                    in={isExpanded && showUnauthenticatedAuthControls && showEmailAuthFields}
                                    timeout={{
                                        enter: Math.min(
                                            MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                            (EMAIL_AUTH_BACK_BUTTON_INDEX * FADE_IN_STAGGER_OFFSET_DURATION)
                                        + FADE_IN_STAGGER_TRANSITION_DURATION,
                                        ),
                                        exit: Math.min(
                                            MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                            ((NUM_EMAIL_AUTH_FIELDS - Math.max(EMAIL_AUTH_BACK_BUTTON_INDEX - 1, 0)) * FADE_IN_STAGGER_OFFSET_DURATION)
                                        + FADE_IN_STAGGER_TRANSITION_DURATION,
                                        ),
                                    }}
                                    appear
                                    mountOnEnter
                                    unmountOnExit
                                >
                                    {(state) => (
                                        <EmailAuthBackButtonContainer
                                            data-testid="user-profile-email-auth-back"
                                            style={{
                                                ...FADE_IN_STAGGER_DEFAULT_STYLE({
                                                    direction: 'left',
                                                    duration: EMAIL_AUTH_FIELD_TRANSITION_DURATION,
                                                    offset: 10,
                                                }),
                                                ...FADE_IN_STAGGER_TRANSITION_STYLES({
                                                    direction: 'left',
                                                    offset: 10,
                                                    numItems: NUM_EMAIL_AUTH_FIELDS,
                                                    index: EMAIL_AUTH_EMAIL_FIELD_INDEX,
                                                })[state],
                                            }}
                                        >
                                            <Button
                                                className={HOVER_TARGET_CLASSNAME}
                                                customCursorActive={customCursorActive}
                                                type={BUTTON_TYPE.solid}
                                                background={theme.color.neutral200}
                                                height={EMAIL_AUTH_BACK_BUTTON_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                                                icon={ArrowIcon}
                                                {...(isExpanded
                                                    ? {
                                                        onMouseEnter: onButtonMouseEnter,
                                                        onMouseLeave: onButtonMouseLeave,
                                                        onClick: onEmailAuthBackButtonClick,
                                                    } : {}
                                                )}
                                                style={{
                                                    transform: 'rotate(180deg)',
                                                }}
                                            />
                                        </EmailAuthBackButtonContainer>
                                    )}
                                </Transition>
                                <Transition
                                    in={
                                        isExpanded
                                    && showUnauthenticatedAuthControls
                                    && showEmailAuthFields
                                    && !hasForgotPassword
                                    && !isSigningUp
                                    }
                                    timeout={{
                                        enter: Math.min(
                                            MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                            (EMAIL_AUTH_FORGOT_PASSWORD_INDEX * FADE_IN_STAGGER_OFFSET_DURATION)
                                        + FADE_IN_STAGGER_TRANSITION_DURATION,
                                        ),
                                        exit: Math.min(
                                            MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                            ((NUM_EMAIL_AUTH_FIELDS - Math.max(EMAIL_AUTH_FORGOT_PASSWORD_INDEX - 1, 0)) * FADE_IN_STAGGER_OFFSET_DURATION)
                                        + FADE_IN_STAGGER_TRANSITION_DURATION,
                                        ),
                                    }}
                                    appear
                                    mountOnEnter
                                    unmountOnExit
                                >
                                    {(state) => (
                                        <ForgotPasswordText
                                            className={HOVER_TARGET_CLASSNAME}
                                            transitionDuration={FORGOT_PASSWORD_TRANSITION_DURATION}
                                            style={{
                                                ...FADE_IN_STAGGER_DEFAULT_STYLE({
                                                    direction: 'down',
                                                    duration: EMAIL_AUTH_FIELD_TRANSITION_DURATION,
                                                    offset: 10,
                                                }),
                                                ...FADE_IN_STAGGER_TRANSITION_STYLES({
                                                    direction: 'down',
                                                    offset: 10,
                                                    numItems: NUM_EMAIL_AUTH_FIELDS,
                                                    index: EMAIL_AUTH_EMAIL_FIELD_INDEX,
                                                })[state],
                                            }}
                                            {...getTouchMouseEventHandlers({
                                                onMouseEnter: onButtonMouseEnter,
                                                onMouseLeave: onButtonMouseLeave,
                                            })}
                                            {...getPressEventHandlers({ onMouseDown: onForgotPassword })}
                                        >
                                        Forgot Password
                                        </ForgotPasswordText>
                                    )}
                                </Transition>
                                <Transition
                                    in={isExpanded
                                    && showUnauthenticatedAuthControls
                                    && showEmailAuthFields
                                    && (
                                        showEmailAuthSubmitButton
                                        || (hasForgotPassword && emailAuth.length > 0 && emailValid)
                                    )}
                                    timeout={{
                                        enter: EMAIL_AUTH_SUBMIT_BUTTON_TRANSITION_DURATION,
                                        exit: EMAIL_AUTH_SUBMIT_BUTTON_TRANSITION_DURATION,
                                    }}
                                    appear
                                    mountOnEnter
                                    unmountOnExit
                                >
                                    {(state) => (
                                        <SubmitEmailAuthButtonContainer
                                            data-testid="user-profile-email-submit"
                                            style={{
                                                ...FADE_IN_DEFAULT_STYLE({
                                                    direction: 'down',
                                                    offset: 10,
                                                    duration: EMAIL_AUTH_SUBMIT_BUTTON_TRANSITION_DURATION,
                                                    easing: theme.motion.eagerEasing,
                                                }),
                                                ...FADE_IN_TRANSITION_STYLES({
                                                    direction: 'down',
                                                    offset: 10,
                                                })[state],
                                            }}
                                        >
                                            <Button
                                                center
                                                className={HOVER_TARGET_CLASSNAME}
                                                customCursorActive={customCursorActive}
                                                type={BUTTON_TYPE.solid}
                                                background={theme.verascopeColor.purple200}
                                                height={EMAIL_AUTH_SUBMIT_BUTTON_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                                                width={EMAIL_AUTH_TAB_WIDTH_MULTIPLIER * BODY_FONT_SIZE}
                                                text={hasForgotPassword
                                                    ? 'Reset'
                                                    : `${isSigningUp ? 'Sign Up' : 'Sign In'}`}
                                                disabled={emailAuthActionIsLoading}
                                                loading={emailAuthActionIsLoading}
                                                {...(isExpanded && !emailAuthActionIsLoading
                                                    ? {
                                                        onMouseEnter: onButtonMouseEnter,
                                                        onMouseLeave: onButtonMouseLeave,
                                                        onClick: onSubmitEmailAuth,
                                                    } : {}
                                                )}
                                            />
                                        </SubmitEmailAuthButtonContainer>
                                    )}
                                </Transition>
                            </EmailAuthFieldsContainer>
                        )}
                        <AuthUserDetails
                            ref={authUserDetailsRef}
                            isVisible={showAuthenticatedDetails && !showBookInvitesPage}
                            padding={DIALOG_BODY_PADDING_MULTIPLIER * BODY_FONT_SIZE}
                            paddingDuration={showBookInvitesPage ? 0 : AUTH_USER_DETAILS_ENTER_DURATION}
                        >
                            {/* eslint-disable indent */}
                            {!showBookInvitesPage && (
                                <>
                            <Transition
                                in={showAuthenticatedDetails}
                                timeout={{
                                    enter: Math.min(
                                        MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                        (authUserDetailsTransitionIndexByRow.authType * FADE_IN_STAGGER_OFFSET_DURATION)
                                        + FADE_IN_STAGGER_TRANSITION_DURATION
                                        + DIALOG_BODY_OPACITY_DELAY_DURATION,
                                    ),
                                    exit: 0,
                                }}
                                appear
                                mountOnEnter
                                unmountOnExit
                            >
                                {(state) => (
                                    <AuthUserContainer
                                        style={{
                                            ...FADE_IN_STAGGER_DEFAULT_STYLE({
                                                direction: 'down',
                                                duration: AUTH_USER_DETAILS_TRANSITION_DURATION,
                                                offset: 10,
                                            }),
                                            ...FADE_IN_STAGGER_TRANSITION_STYLES({
                                                direction: 'down',
                                                offset: 10,
                                                numItems: numAuthUserDetails,
                                                index: authUserDetailsTransitionIndexByRow.authType,
                                            })[state],
                                        }}
                                    >
                                        <AuthUserIcon>
                                            <ReactSVG
                                                src={AuthTypeIcon}
                                            />
                                        </AuthUserIcon>
                                        <AuthUserText>
                                            {authTypeText}
                                        </AuthUserText>
                                        {shouldShowEmailVerificationAction && (
                                            <AuthVerificationContainer>
                                                {userEmailVerified ? (
                                                    <AuthEmailVerificationBadge
                                                        height={AUTH_VERIFY_EMAIL_BUTTON_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                                                    >
                                                        Email verified
                                                    </AuthEmailVerificationBadge>
                                                ) : (
                                                    <Button
                                                        className={HOVER_TARGET_CLASSNAME}
                                                        customCursorActive={customCursorActive}
                                                        type={BUTTON_TYPE.solid}
                                                        background={theme.verascopeColor.orange200}
                                                        height={AUTH_VERIFY_EMAIL_BUTTON_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                                                        text={USER_VERIFY_EMAIL_BUTTON_TEXT}
                                                        {...getTouchMouseEventHandlers({
                                                            onMouseEnter: onButtonMouseEnter,
                                                            onMouseLeave: onButtonMouseLeave,
                                                        })}
                                                        {...getPressEventHandlers({ onMouseDown: onVerifyEmail })}
                                                        loading={isProcessingEmailVerificationRequest}
                                                        disabled={isProcessingEmailVerificationRequest}
                                                        style={{
                                                            fontSize: `${0.55 * BODY_FONT_SIZE}px`,
                                                        }}
                                                    />
                                                )}
                                            </AuthVerificationContainer>
                                        )}
                                    </AuthUserContainer>
                                )}
                            </Transition>
                            <Transition
                                in={showAuthenticatedDetails && !!user}
                                timeout={{
                                    enter: Math.min(
                                        MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                        (authUserDetailsTransitionIndexByRow.membership * FADE_IN_STAGGER_OFFSET_DURATION)
                                        + FADE_IN_STAGGER_TRANSITION_DURATION
                                        + DIALOG_BODY_OPACITY_DELAY_DURATION,
                                    ),
                                    exit: 0,
                                }}
                                appear
                                mountOnEnter
                                unmountOnExit
                            >
                                {(state) => (
                                    <AuthUserContainer
                                        style={{
                                            ...FADE_IN_STAGGER_DEFAULT_STYLE({
                                                direction: 'down',
                                                duration: AUTH_USER_DETAILS_TRANSITION_DURATION,
                                                offset: 10,
                                            }),
                                            ...FADE_IN_STAGGER_TRANSITION_STYLES({
                                                direction: 'down',
                                                offset: 10,
                                                numItems: numAuthUserDetails,
                                                index: authUserDetailsTransitionIndexByRow.membership,
                                            })[state],
                                        }}
                                    >
                                        <AuthUserIcon>
                                            <ReactSVG
                                                src={HistoryIcon}
                                            />
                                        </AuthUserIcon>
                                        <AuthUserText>
                                            {/* shouldn't be visible if user undefined */}
                                            {`Since ${moment.utc(user.joined).format('MMMM YYYY')}`}
                                        </AuthUserText>
                                    </AuthUserContainer>
                                )}
                            </Transition>
                            <Transition
                                in={showAuthenticatedDetails && !!user && numUndeletedTopLevelAnnotations !== null && numUndeletedTopLevelAnnotations > 0}
                                timeout={{
                                    enter: Math.min(
                                        MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                        (authUserDetailsTransitionIndexByRow.annotations * FADE_IN_STAGGER_OFFSET_DURATION)
                                        + FADE_IN_STAGGER_TRANSITION_DURATION
                                        + DIALOG_BODY_OPACITY_DELAY_DURATION,
                                    ),
                                    exit: 0,
                                }}
                                appear
                                mountOnEnter
                                unmountOnExit
                            >
                                {(state) => (
                                    <AuthUserContainer
                                        style={{
                                            ...FADE_IN_STAGGER_DEFAULT_STYLE({
                                                direction: 'down',
                                                duration: AUTH_USER_DETAILS_TRANSITION_DURATION,
                                                offset: 10,
                                            }),
                                            ...FADE_IN_STAGGER_TRANSITION_STYLES({
                                                direction: 'down',
                                                offset: 10,
                                                numItems: numAuthUserDetails,
                                                index: authUserDetailsTransitionIndexByRow.annotations,
                                            })[state],
                                        }}
                                    >
                                        <AuthUserIcon>
                                            <ReactSVG
                                                src={HighlightIcon}
                                            />
                                        </AuthUserIcon>
                                        <AuthUserText>
                                            {/* shouldn't be visible if user undefined */}
                                            {`${numUndeletedTopLevelAnnotations || 0} annotation${(numUndeletedTopLevelAnnotations || 0) > 1 ? 's' : ''}`}
                                        </AuthUserText>
                                    </AuthUserContainer>
                                )}
                            </Transition>
                            <Transition
                                in={showAuthenticatedDetails && !!user}
                                timeout={{
                                    enter: Math.min(
                                        MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                        (authUserDetailsTransitionIndexByRow.bookAccess * FADE_IN_STAGGER_OFFSET_DURATION)
                                        + FADE_IN_STAGGER_TRANSITION_DURATION
                                        + DIALOG_BODY_OPACITY_DELAY_DURATION,
                                    ),
                                    exit: 0,
                                }}
                                appear
                                mountOnEnter
                                unmountOnExit
                            >
                                {(state) => (
                                    <AuthUserContainer
                                        style={{
                                            ...FADE_IN_STAGGER_DEFAULT_STYLE({
                                                direction: 'down',
                                                duration: AUTH_USER_DETAILS_TRANSITION_DURATION,
                                                offset: 10,
                                            }),
                                            ...FADE_IN_STAGGER_TRANSITION_STYLES({
                                                direction: 'down',
                                                offset: 10,
                                                numItems: numAuthUserDetails,
                                                index: authUserDetailsTransitionIndexByRow.bookAccess,
                                            })[state],
                                        }}
                                    >
                                        <AuthUserDisclosureContainer
                                            data-testid="user-profile-book-invites-row"
                                            $labelColumnWidth={authDisclosureLabelColumnWidth}
                                        >
                                            <AuthUserDisclosureIcon>
                                                <ReactSVG
                                                    src={ShelfIcon}
                                                />
                                            </AuthUserDisclosureIcon>
                                            <AuthUserDisclosureLabel>
                                                Book Access
                                            </AuthUserDisclosureLabel>
                                            <AuthUserDisclosureInfoButtonContainer>
                                                {renderInfoDisclosureButton(
                                                    USER_PROFILE_INFO_DISCLOSURE_KEY.bookAccess,
                                                    USER_PROFILE_INFO_DISCLOSURE_BUTTON_LABEL.bookAccess,
                                                )}
                                            </AuthUserDisclosureInfoButtonContainer>
                                            <AuthUserDisclosureControl>
                                                <AuthVerificationContainer>
                                                    <AuthEmailVerificationBadge
                                                        height={AUTH_USER_ROW_PILL_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                                                    >
                                                        {bookAccessText}
                                                    </AuthEmailVerificationBadge>
                                                    {!hasBookAccess
                                                    && !userIsAdmin
                                                    && (
                                                        <Button
                                                            className={HOVER_TARGET_CLASSNAME}
                                                            customCursorActive={customCursorActive}
                                                            type={BUTTON_TYPE.solid}
                                                            background={theme.verascopeColor.orange200}
                                                            height={AUTH_USER_ROW_PILL_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                                                            text="Buy"
                                                            {...getTouchMouseEventHandlers({
                                                                onMouseEnter: onButtonMouseEnter,
                                                                onMouseLeave: onButtonMouseLeave,
                                                            })}
                                                            {...getPressEventHandlers({ onMouseDown: onBuyBook })}
                                                            style={{
                                                                marginLeft: `${0.2 * BODY_FONT_SIZE}px`,
                                                                fontSize: `${0.55 * BODY_FONT_SIZE}px`,
                                                            }}
                                                        />
                                                    )}
                                                </AuthVerificationContainer>
                                            </AuthUserDisclosureControl>
                                            {activeInfoDisclosureKey === USER_PROFILE_INFO_DISCLOSURE_KEY.bookAccess && (
                                                <AuthUserDisclosureText
                                                    id={getInfoDisclosureId(USER_PROFILE_INFO_DISCLOSURE_KEY.bookAccess)}
                                                >
                                                    {AUTH_USER_DETAILS_BOOK_ACCESS_TOOLTIP_TEXT}
                                                </AuthUserDisclosureText>
                                            )}
                                        </AuthUserDisclosureContainer>
                                    </AuthUserContainer>
                                )}
                            </Transition>
                            <Transition
                                in={showAuthenticatedDetails && !!user}
                                timeout={{
                                    enter: Math.min(
                                        MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                        (authUserDetailsTransitionIndexByRow.bookInvites * FADE_IN_STAGGER_OFFSET_DURATION)
                                        + FADE_IN_STAGGER_TRANSITION_DURATION
                                        + DIALOG_BODY_OPACITY_DELAY_DURATION,
                                    ),
                                    exit: 0,
                                }}
                                appear
                                mountOnEnter
                                unmountOnExit
                            >
                                {(state) => (
                                    <AuthUserContainer
                                        style={{
                                            ...FADE_IN_STAGGER_DEFAULT_STYLE({
                                                direction: 'down',
                                                duration: AUTH_USER_DETAILS_TRANSITION_DURATION,
                                                offset: 10,
                                            }),
                                            ...FADE_IN_STAGGER_TRANSITION_STYLES({
                                                direction: 'down',
                                                offset: 10,
                                                numItems: numAuthUserDetails,
                                                index: authUserDetailsTransitionIndexByRow.bookInvites,
                                            })[state],
                                        }}
                                    >
                                        <AuthUserDisclosureContainer
                                            $labelColumnWidth={authDisclosureLabelColumnWidth}
                                        >
                                            <AuthUserDisclosureIcon>
                                                <ReactSVG
                                                    src={GiftIcon}
                                                />
                                            </AuthUserDisclosureIcon>
                                            <AuthUserDisclosureLabel>
                                                Book Invites
                                            </AuthUserDisclosureLabel>
                                            <AuthUserDisclosureInfoButtonContainer>
                                                {renderInfoDisclosureButton(
                                                    USER_PROFILE_INFO_DISCLOSURE_KEY.bookInvites,
                                                    USER_PROFILE_INFO_DISCLOSURE_BUTTON_LABEL.bookInvites,
                                                )}
                                            </AuthUserDisclosureInfoButtonContainer>
                                            <AuthUserDisclosureControl>
                                                <BookInviteRemainingButton
                                                    data-testid="user-profile-book-invites-open"
                                                    className={HOVER_TARGET_CLASSNAME}
                                                    customCursorActive={customCursorActive}
                                                    height={AUTH_USER_ROW_PILL_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                                                    {...getTouchMouseEventHandlers({
                                                        onMouseEnter: onButtonMouseEnter,
                                                        onMouseLeave: onButtonMouseLeave,
                                                    })}
                                                    {...getPressEventHandlers({ onMouseDown: onOpenBookInvitesPage })}
                                                >
                                                    <BookInviteRemainingLabel>
                                                        {BOOK_INVITEES_TRIGGER_LABEL_TEXT}
                                                    </BookInviteRemainingLabel>
                                                    <BookInviteRemainingValue>
                                                        {bookInviteSummary.remaining}
                                                    </BookInviteRemainingValue>
                                                </BookInviteRemainingButton>
                                            </AuthUserDisclosureControl>
                                            {activeInfoDisclosureKey === USER_PROFILE_INFO_DISCLOSURE_KEY.bookInvites && (
                                                <AuthUserDisclosureText
                                                    id={getInfoDisclosureId(USER_PROFILE_INFO_DISCLOSURE_KEY.bookInvites)}
                                                >
                                                    {AUTH_USER_DETAILS_BOOK_INVITEES_TOOLTIP_TEXT}
                                                </AuthUserDisclosureText>
                                            )}
                                        </AuthUserDisclosureContainer>
                                    </AuthUserContainer>
                                )}
                            </Transition>
                            <Transition
                                in={showAuthenticatedDetails && !!user}
                                timeout={{
                                    enter: Math.min(
                                        MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                        (authUserDetailsTransitionIndexByRow.mailingList * FADE_IN_STAGGER_OFFSET_DURATION)
                                        + FADE_IN_STAGGER_TRANSITION_DURATION
                                        + DIALOG_BODY_OPACITY_DELAY_DURATION,
                                    ),
                                    exit: 0,
                                }}
                                appear
                                mountOnEnter
                                unmountOnExit
                            >
                                {(state) => (
                                    <AuthUserContainer
                                        style={{
                                            ...FADE_IN_STAGGER_DEFAULT_STYLE({
                                                direction: 'down',
                                                duration: AUTH_USER_DETAILS_TRANSITION_DURATION,
                                                offset: 10,
                                            }),
                                            ...FADE_IN_STAGGER_TRANSITION_STYLES({
                                                direction: 'down',
                                                offset: 10,
                                                numItems: numAuthUserDetails,
                                                index: authUserDetailsTransitionIndexByRow.mailingList,
                                            })[state],
                                        }}
                                    >
                                        <AuthUserDisclosureContainer
                                            data-testid="user-profile-mailing-toggle-row"
                                            $labelColumnWidth={authDisclosureLabelColumnWidth}
                                        >
                                            <AuthUserDisclosureIcon>
                                                <ReactSVG
                                                    src={MailboxIcon}
                                                />
                                            </AuthUserDisclosureIcon>
                                            <AuthUserDisclosureLabel>
                                                Mailing List
                                            </AuthUserDisclosureLabel>
                                            <AuthUserDisclosureInfoButtonContainer>
                                                {renderInfoDisclosureButton(
                                                    USER_PROFILE_INFO_DISCLOSURE_KEY.mailingList,
                                                    USER_PROFILE_INFO_DISCLOSURE_BUTTON_LABEL.mailingList,
                                                )}
                                            </AuthUserDisclosureInfoButtonContainer>
                                            <AuthUserDisclosureControl>
                                                <ToggleButton
                                                    customCursorActive={customCursorActive}
                                                    disabled={!isExpanded}
                                                    hasSound={hasSound}
                                                    background={theme.color.neutral300}
                                                    selectedIndex={user.mailingListSubscription!.history[
                                                        user.mailingListSubscription!.history.length - 1
                                                    ].subscribed
                                                        ? 0
                                                        : 1}
                                                    items={[
                                                        {
                                                            text: 'Yes',
                                                            handleClick: () => handleMailingListSubscription(true),
                                                        },
                                                        {
                                                            text: 'No',
                                                            handleClick: () => handleMailingListSubscription(false),
                                                        },
                                                    ]}
                                                    buttonWidth={MAILING_SUBSCRIPTION_TOGGLE_BUTTON_WIDTH}
                                                    buttonHeight={MAILING_SUBSCRIPTION_TOGGLE_BUTTON_HEIGHT}
                                                    buttonMargin={MAILING_SUBSCRIPTION_TOGGLE_BUTTON_MARGIN}
                                                    transitionDuration={MAILING_SUBSCRIPTION_TOGGLE_BUTTON_HOVER_TRANSITION_DURATION}
                                                    onCursorEnter={onCursorEnter}
                                                    onCursorLeave={onCursorLeave}
                                                />
                                            </AuthUserDisclosureControl>
                                            {activeInfoDisclosureKey === USER_PROFILE_INFO_DISCLOSURE_KEY.mailingList && (
                                                <AuthUserDisclosureText
                                                    id={getInfoDisclosureId(USER_PROFILE_INFO_DISCLOSURE_KEY.mailingList)}
                                                >
                                                    {AUTH_USER_DETAILS_MAILING_LIST_TOOLTIP_TEXT}
                                                </AuthUserDisclosureText>
                                            )}
                                        </AuthUserDisclosureContainer>
                                    </AuthUserContainer>
                                )}
                            </Transition>
                            <Transition
                                in={showAuthenticatedDetails && !!user}
                                timeout={{
                                    enter: Math.min(
                                        MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                        (authUserDetailsTransitionIndexByRow.annotationUpdates * FADE_IN_STAGGER_OFFSET_DURATION)
                                        + FADE_IN_STAGGER_TRANSITION_DURATION
                                        + DIALOG_BODY_OPACITY_DELAY_DURATION,
                                    ),
                                    exit: 0,
                                }}
                                appear
                                mountOnEnter
                                unmountOnExit
                            >
                                {(state) => (
                                    <AuthUserContainer
                                        style={{
                                            ...FADE_IN_STAGGER_DEFAULT_STYLE({
                                                direction: 'down',
                                                duration: AUTH_USER_DETAILS_TRANSITION_DURATION,
                                                offset: 10,
                                            }),
                                            ...FADE_IN_STAGGER_TRANSITION_STYLES({
                                                direction: 'down',
                                                offset: 10,
                                                numItems: numAuthUserDetails,
                                                index: authUserDetailsTransitionIndexByRow.annotationUpdates,
                                            })[state],
                                        }}
                                    >
                                        <AuthUserDisclosureContainer
                                            data-testid="user-profile-comment-mail-toggle-row"
                                            $labelColumnWidth={authDisclosureLabelColumnWidth}
                                        >
                                            <AuthUserDisclosureIcon>
                                                <ReactSVG
                                                    src={BellIcon}
                                                />
                                            </AuthUserDisclosureIcon>
                                            <AuthUserDisclosureLabel>
                                                Comment Mail
                                            </AuthUserDisclosureLabel>
                                            <AuthUserDisclosureInfoButtonContainer>
                                                {renderInfoDisclosureButton(
                                                    USER_PROFILE_INFO_DISCLOSURE_KEY.commentMail,
                                                    USER_PROFILE_INFO_DISCLOSURE_BUTTON_LABEL.commentMail,
                                                )}
                                            </AuthUserDisclosureInfoButtonContainer>
                                            <AuthUserDisclosureControl>
                                                <ToggleButton
                                                    customCursorActive={customCursorActive}
                                                    disabled={!isExpanded}
                                                    hasSound={hasSound}
                                                    background={theme.color.neutral300}
                                                    selectedIndex={user.commentSubscription && user.commentSubscription.history[
                                                        user.commentSubscription.history.length - 1
                                                    ].subscribed
                                                        ? 0
                                                        : 1}
                                                    items={[
                                                        {
                                                            text: 'Yes',
                                                            handleClick: () => handleCommentSubscription(true),
                                                        },
                                                        {
                                                            text: 'No',
                                                            handleClick: () => handleCommentSubscription(false),
                                                        },
                                                    ]}
                                                    buttonWidth={MAILING_SUBSCRIPTION_TOGGLE_BUTTON_WIDTH}
                                                    buttonHeight={MAILING_SUBSCRIPTION_TOGGLE_BUTTON_HEIGHT}
                                                    buttonMargin={MAILING_SUBSCRIPTION_TOGGLE_BUTTON_MARGIN}
                                                    transitionDuration={MAILING_SUBSCRIPTION_TOGGLE_BUTTON_HOVER_TRANSITION_DURATION}
                                                    onCursorEnter={onCursorEnter}
                                                    onCursorLeave={onCursorLeave}
                                                />
                                            </AuthUserDisclosureControl>
                                            {activeInfoDisclosureKey === USER_PROFILE_INFO_DISCLOSURE_KEY.commentMail && (
                                                <AuthUserDisclosureText
                                                    id={getInfoDisclosureId(USER_PROFILE_INFO_DISCLOSURE_KEY.commentMail)}
                                                >
                                                    {AUTH_USER_DETAILS_ANNOTATION_UPDATES_TOOLTIP_TEXT}
                                                </AuthUserDisclosureText>
                                            )}
                                        </AuthUserDisclosureContainer>
                                    </AuthUserContainer>
                                )}
                            </Transition>
                            <Transition
                                in={showAuthenticatedDetails && !!user}
                                timeout={{
                                    enter: Math.min(
                                        MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                        (authUserDetailsTransitionIndexByRow.hideAnnotations * FADE_IN_STAGGER_OFFSET_DURATION)
                                        + FADE_IN_STAGGER_TRANSITION_DURATION
                                        + DIALOG_BODY_OPACITY_DELAY_DURATION,
                                    ),
                                    exit: 0,
                                }}
                                appear
                                mountOnEnter
                                unmountOnExit
                            >
                                {(state) => (
                                    <AuthUserContainer
                                        style={{
                                            ...FADE_IN_STAGGER_DEFAULT_STYLE({
                                                direction: 'down',
                                                duration: AUTH_USER_DETAILS_TRANSITION_DURATION,
                                                offset: 10,
                                            }),
                                            ...FADE_IN_STAGGER_TRANSITION_STYLES({
                                                direction: 'down',
                                                offset: 10,
                                                numItems: numAuthUserDetails,
                                                index: authUserDetailsTransitionIndexByRow.hideAnnotations,
                                            })[state],
                                        }}
                                    >
                                        <AuthUserDisclosureContainer
                                            data-testid="user-profile-annotations-toggle-row"
                                            $labelColumnWidth={authDisclosureLabelColumnWidth}
                                        >
                                            <AuthUserDisclosureIcon>
                                                <ReactSVG
                                                    src={HighlightIcon}
                                                />
                                            </AuthUserDisclosureIcon>
                                            <AuthUserDisclosureLabel>
                                                Annotations
                                            </AuthUserDisclosureLabel>
                                            <AuthUserDisclosureInfoButtonContainer>
                                                {renderInfoDisclosureButton(
                                                    USER_PROFILE_INFO_DISCLOSURE_KEY.annotations,
                                                    USER_PROFILE_INFO_DISCLOSURE_BUTTON_LABEL.annotations,
                                                )}
                                            </AuthUserDisclosureInfoButtonContainer>
                                            <AuthUserDisclosureControl>
                                                <ToggleButton
                                                    customCursorActive={customCursorActive}
                                                    disabled={!isExpanded}
                                                    hasSound={hasSound}
                                                    background={theme.color.neutral300}
                                                    selectedIndex={user.hideAnnotations != null && user.hideAnnotations
                                                        ? 1
                                                        : 0}
                                                    items={[
                                                        {
                                                            text: 'Show',
                                                            handleClick: () => handleHideAnnotations(false),
                                                        },
                                                        {
                                                            text: 'Hide',
                                                            handleClick: () => handleHideAnnotations(true),
                                                        },
                                                    ]}
                                                    buttonWidth={MAILING_SUBSCRIPTION_TOGGLE_BUTTON_WIDTH}
                                                    buttonHeight={MAILING_SUBSCRIPTION_TOGGLE_BUTTON_HEIGHT}
                                                    buttonMargin={MAILING_SUBSCRIPTION_TOGGLE_BUTTON_MARGIN}
                                                    transitionDuration={MAILING_SUBSCRIPTION_TOGGLE_BUTTON_HOVER_TRANSITION_DURATION}
                                                    onCursorEnter={onCursorEnter}
                                                    onCursorLeave={onCursorLeave}
                                                />
                                            </AuthUserDisclosureControl>
                                            {activeInfoDisclosureKey === USER_PROFILE_INFO_DISCLOSURE_KEY.annotations && (
                                                <AuthUserDisclosureText
                                                    id={getInfoDisclosureId(USER_PROFILE_INFO_DISCLOSURE_KEY.annotations)}
                                                >
                                                    {AUTH_USER_DETAILS_HIDE_ANNOTATIONS_TOOLTIP_TEXT}
                                                </AuthUserDisclosureText>
                                            )}
                                        </AuthUserDisclosureContainer>
                                    </AuthUserContainer>
                                )}
                            </Transition>
                            <Transition
                                in={showAuthenticatedDetails && !!user}
                                timeout={{
                                    enter: Math.min(
                                        MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                        (authUserDetailsTransitionIndexByRow.saveReaderProgress * FADE_IN_STAGGER_OFFSET_DURATION)
                                        + FADE_IN_STAGGER_TRANSITION_DURATION
                                        + DIALOG_BODY_OPACITY_DELAY_DURATION,
                                    ),
                                    exit: 0,
                                }}
                                appear
                                mountOnEnter
                                unmountOnExit
                            >
                                {(state) => (
                                    <AuthUserContainer
                                        style={{
                                            ...FADE_IN_STAGGER_DEFAULT_STYLE({
                                                direction: 'down',
                                                duration: AUTH_USER_DETAILS_TRANSITION_DURATION,
                                                offset: 10,
                                            }),
                                            ...FADE_IN_STAGGER_TRANSITION_STYLES({
                                                direction: 'down',
                                                offset: 10,
                                                numItems: numAuthUserDetails,
                                                index: authUserDetailsTransitionIndexByRow.saveReaderProgress,
                                            })[state],
                                        }}
                                    >
                                        <AuthUserDisclosureContainer
                                            data-testid="user-profile-bookmarking-toggle-row"
                                            $labelColumnWidth={authDisclosureLabelColumnWidth}
                                        >
                                            <AuthUserDisclosureIcon>
                                                <ReactSVG
                                                    src={BookmarkIcon}
                                                />
                                            </AuthUserDisclosureIcon>
                                            <AuthUserDisclosureLabel>
                                                Bookmarking
                                            </AuthUserDisclosureLabel>
                                            <AuthUserDisclosureInfoButtonContainer>
                                                {renderInfoDisclosureButton(
                                                    USER_PROFILE_INFO_DISCLOSURE_KEY.bookmarking,
                                                    USER_PROFILE_INFO_DISCLOSURE_BUTTON_LABEL.bookmarking,
                                                )}
                                            </AuthUserDisclosureInfoButtonContainer>
                                            <AuthUserDisclosureControl>
                                                <ToggleButton
                                                    customCursorActive={customCursorActive}
                                                    disabled={!isExpanded}
                                                    hasSound={hasSound}
                                                    background={theme.color.neutral300}
                                                    selectedIndex={user.saveReaderProgress == null || user.saveReaderProgress
                                                        ? 0
                                                        : 1}
                                                    items={[
                                                        {
                                                            text: 'Yes',
                                                            handleClick: () => handleSaveReaderProgress(true),
                                                        },
                                                        {
                                                            text: 'No',
                                                            handleClick: () => handleSaveReaderProgress(false),
                                                        },
                                                    ]}
                                                    buttonWidth={MAILING_SUBSCRIPTION_TOGGLE_BUTTON_WIDTH}
                                                    buttonHeight={MAILING_SUBSCRIPTION_TOGGLE_BUTTON_HEIGHT}
                                                    buttonMargin={MAILING_SUBSCRIPTION_TOGGLE_BUTTON_MARGIN}
                                                    transitionDuration={MAILING_SUBSCRIPTION_TOGGLE_BUTTON_HOVER_TRANSITION_DURATION}
                                                    onCursorEnter={onCursorEnter}
                                                    onCursorLeave={onCursorLeave}
                                                />
                                            </AuthUserDisclosureControl>
                                            {activeInfoDisclosureKey === USER_PROFILE_INFO_DISCLOSURE_KEY.bookmarking && (
                                                <AuthUserDisclosureText
                                                    id={getInfoDisclosureId(USER_PROFILE_INFO_DISCLOSURE_KEY.bookmarking)}
                                                >
                                                    {AUTH_USER_DETAILS_SAVE_READER_PROGRESS_TOOLTIP_TEXT}
                                                </AuthUserDisclosureText>
                                            )}
                                        </AuthUserDisclosureContainer>
                                    </AuthUserContainer>
                                )}
                            </Transition>
                            <Transition
                                in={showAuthenticatedDetails && !!user}
                                timeout={{
                                    enter: Math.min(
                                        MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                        (authUserDetailsTransitionIndexByRow.customCursor * FADE_IN_STAGGER_OFFSET_DURATION)
                                        + FADE_IN_STAGGER_TRANSITION_DURATION
                                        + DIALOG_BODY_OPACITY_DELAY_DURATION,
                                    ),
                                    exit: 0,
                                }}
                                appear
                                mountOnEnter
                                unmountOnExit
                            >
                                {(state) => (
                                    <AuthUserContainer
                                        style={{
                                            ...FADE_IN_STAGGER_DEFAULT_STYLE({
                                                direction: 'down',
                                                duration: AUTH_USER_DETAILS_TRANSITION_DURATION,
                                                offset: 10,
                                            }),
                                            ...FADE_IN_STAGGER_TRANSITION_STYLES({
                                                direction: 'down',
                                                offset: 10,
                                                numItems: numAuthUserDetails,
                                                index: authUserDetailsTransitionIndexByRow.customCursor,
                                            })[state],
                                        }}
                                    >
                                        <AuthUserDisclosureContainer
                                            data-testid="user-profile-custom-cursor-toggle-row"
                                            $labelColumnWidth={authDisclosureLabelColumnWidth}
                                        >
                                            <AuthUserDisclosureIcon>
                                                <ReactSVG
                                                    src={CursorIcon}
                                                />
                                            </AuthUserDisclosureIcon>
                                            <AuthUserDisclosureLabel>
                                                Custom cursor
                                            </AuthUserDisclosureLabel>
                                            <AuthUserDisclosureInfoButtonContainer>
                                                {renderInfoDisclosureButton(
                                                    USER_PROFILE_INFO_DISCLOSURE_KEY.customCursor,
                                                    USER_PROFILE_INFO_DISCLOSURE_BUTTON_LABEL.customCursor,
                                                )}
                                            </AuthUserDisclosureInfoButtonContainer>
                                            <AuthUserDisclosureControl>
                                                <ToggleButton
                                                    customCursorActive={customCursorActive}
                                                    disabled={!isExpanded}
                                                    hasSound={hasSound}
                                                    background={theme.color.neutral300}
                                                    selectedIndex={user.customCursorEnabled === false
                                                        ? 1
                                                        : 0}
                                                    items={[
                                                        {
                                                            text: 'Yes',
                                                            handleClick: () => handleCustomCursorEnabled(true),
                                                        },
                                                        {
                                                            text: 'No',
                                                            handleClick: () => handleCustomCursorEnabled(false),
                                                        },
                                                    ]}
                                                    buttonWidth={MAILING_SUBSCRIPTION_TOGGLE_BUTTON_WIDTH}
                                                    buttonHeight={MAILING_SUBSCRIPTION_TOGGLE_BUTTON_HEIGHT}
                                                    buttonMargin={MAILING_SUBSCRIPTION_TOGGLE_BUTTON_MARGIN}
                                                    transitionDuration={MAILING_SUBSCRIPTION_TOGGLE_BUTTON_HOVER_TRANSITION_DURATION}
                                                    onCursorEnter={onCursorEnter}
                                                    onCursorLeave={onCursorLeave}
                                                />
                                            </AuthUserDisclosureControl>
                                            {activeInfoDisclosureKey === USER_PROFILE_INFO_DISCLOSURE_KEY.customCursor && (
                                                <AuthUserDisclosureText
                                                    id={getInfoDisclosureId(USER_PROFILE_INFO_DISCLOSURE_KEY.customCursor)}
                                                >
                                                    {AUTH_USER_DETAILS_CUSTOM_CURSOR_TOOLTIP_TEXT}
                                                </AuthUserDisclosureText>
                                            )}
                                        </AuthUserDisclosureContainer>
                                    </AuthUserContainer>
                                )}
                            </Transition>
                            <Transition
                                in={showAuthenticatedDetails}
                                timeout={{
                                    enter: Math.min(
                                        MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                        (authUserDetailsTransitionIndexByRow.signOutButton * FADE_IN_STAGGER_OFFSET_DURATION)
                                        + FADE_IN_STAGGER_TRANSITION_DURATION,
                                    ),
                                    exit: 0,
                                }}
                                appear
                                mountOnEnter
                                unmountOnExit
                            >
                                {(state) => (
                                    <AuthUserSignOutButtonContainer
                                        style={{
                                            ...FADE_IN_STAGGER_DEFAULT_STYLE({
                                                direction: 'down',
                                                duration: AUTH_USER_DETAILS_TRANSITION_DURATION,
                                                offset: 10,
                                            }),
                                            ...FADE_IN_STAGGER_TRANSITION_STYLES({
                                                direction: 'down',
                                                offset: 10,
                                                numItems: numAuthUserDetails,
                                                index: authUserDetailsTransitionIndexByRow.signOutButton,
                                            })[state],
                                        }}
                                    >
                                        <Button
                                            className={HOVER_TARGET_CLASSNAME}
                                            customCursorActive={customCursorActive}
                                            type={BUTTON_TYPE.solid}
                                            background={theme.verascopeColor.purple200}
                                            height={USER_AUTH_SUBMIT_BUTTON_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                                            text="Sign Out"
                                            {...getTouchMouseEventHandlers({
                                                onMouseEnter: onButtonMouseEnter,
                                                onMouseLeave: onButtonMouseLeave,
                                            })}
                                            {...getPressEventHandlers({ onMouseDown: onSignOut })}
                                        />
                                    </AuthUserSignOutButtonContainer>
                                )}
                            </Transition>
                                </>
                            )}
                            {/* eslint-enable indent */}
                            {showAuthenticatedDetails && showBookInvitesPage && (
                                <BookInvitesOverlay data-testid="user-profile-book-invites-overlay">
                                    <BookInvitesHeader>
                                        <BookInvitesTitle>
                                            Book Invites
                                        </BookInvitesTitle>
                                        <BookInvitesBackButtonContainer data-testid="user-profile-book-invites-back">
                                            <Button
                                                className={HOVER_TARGET_CLASSNAME}
                                                customCursorActive={customCursorActive}
                                                type={BUTTON_TYPE.solid}
                                                background={theme.color.neutral200}
                                                height={EMAIL_AUTH_BACK_BUTTON_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                                                icon={ArrowIcon}
                                                {...getTouchMouseEventHandlers({
                                                    onMouseEnter: onButtonMouseEnter,
                                                    onMouseLeave: onButtonMouseLeave,
                                                })}
                                                {...getPressEventHandlers({ onMouseDown: onCloseBookInvitesPage })}
                                                style={{
                                                    transform: 'rotate(180deg)',
                                                }}
                                            />
                                        </BookInvitesBackButtonContainer>
                                    </BookInvitesHeader>
                                    <BookInvitesSummaryGrid>
                                        <BookInvitesSummaryItem
                                            $accent={remainingSummaryAccent}
                                            $hero
                                        >
                                            <BookInvitesSummaryLabel $accent={remainingSummaryAccent}>
                                                Remaining
                                            </BookInvitesSummaryLabel>
                                            <BookInvitesSummaryValue $accent={remainingSummaryAccent} $hero>
                                                {bookInviteSummary.remaining}
                                            </BookInvitesSummaryValue>
                                        </BookInvitesSummaryItem>
                                        <BookInvitesSummaryItem $accent={BOOK_INVITE_SUMMARY_ACCENT.purple}>
                                            {renderInfoDisclosureButton(
                                                USER_PROFILE_INFO_DISCLOSURE_KEY.bookInvitePending,
                                                USER_PROFILE_INFO_DISCLOSURE_BUTTON_LABEL.bookInvitePending,
                                                BOOK_INVITE_SUMMARY_ACCENT.purple,
                                                true,
                                            )}
                                            <BookInvitesSummaryLabel $accent={BOOK_INVITE_SUMMARY_ACCENT.purple}>
                                                <BookInvitesSummaryLabelRow>
                                                    Pending
                                                </BookInvitesSummaryLabelRow>
                                                {activeInfoDisclosureKey === USER_PROFILE_INFO_DISCLOSURE_KEY.bookInvitePending && (
                                                    <BookInvitesSummaryInfoDisclosureText
                                                        $accent={BOOK_INVITE_SUMMARY_ACCENT.purple}
                                                        id={getInfoDisclosureId(USER_PROFILE_INFO_DISCLOSURE_KEY.bookInvitePending)}
                                                    >
                                                        {BOOK_INVITE_STATUS_PENDING_TOOLTIP_TEXT}
                                                    </BookInvitesSummaryInfoDisclosureText>
                                                )}
                                            </BookInvitesSummaryLabel>
                                            <BookInvitesSummaryValue $accent={BOOK_INVITE_SUMMARY_ACCENT.purple}>
                                                {bookInviteSummary.pending}
                                            </BookInvitesSummaryValue>
                                        </BookInvitesSummaryItem>
                                        <BookInvitesSummaryItem $accent={BOOK_INVITE_SUMMARY_ACCENT.green}>
                                            {renderInfoDisclosureButton(
                                                USER_PROFILE_INFO_DISCLOSURE_KEY.bookInviteClaimed,
                                                USER_PROFILE_INFO_DISCLOSURE_BUTTON_LABEL.bookInviteClaimed,
                                                BOOK_INVITE_SUMMARY_ACCENT.green,
                                                true,
                                            )}
                                            <BookInvitesSummaryLabel $accent={BOOK_INVITE_SUMMARY_ACCENT.green}>
                                                <BookInvitesSummaryLabelRow>
                                                    Claimed
                                                </BookInvitesSummaryLabelRow>
                                                {activeInfoDisclosureKey === USER_PROFILE_INFO_DISCLOSURE_KEY.bookInviteClaimed && (
                                                    <BookInvitesSummaryInfoDisclosureText
                                                        $accent={BOOK_INVITE_SUMMARY_ACCENT.green}
                                                        id={getInfoDisclosureId(USER_PROFILE_INFO_DISCLOSURE_KEY.bookInviteClaimed)}
                                                    >
                                                        {BOOK_INVITE_STATUS_CLAIMED_TOOLTIP_TEXT}
                                                    </BookInvitesSummaryInfoDisclosureText>
                                                )}
                                            </BookInvitesSummaryLabel>
                                            <BookInvitesSummaryValue $accent={BOOK_INVITE_SUMMARY_ACCENT.green}>
                                                {bookInviteSummary.claimed}
                                            </BookInvitesSummaryValue>
                                        </BookInvitesSummaryItem>
                                    </BookInvitesSummaryGrid>
                                    {canOpenBookInviteCreateInvite && (
                                        <BookInvitesSummaryHeroAction>
                                            <Button
                                                className={HOVER_TARGET_CLASSNAME}
                                                customCursorActive={customCursorActive}
                                                type={BUTTON_TYPE.line}
                                                background={remainingSummaryAccent === BOOK_INVITE_SUMMARY_ACCENT.red
                                                    ? theme.verascopeColor.red200
                                                    : theme.verascopeColor.orange100}
                                                color={remainingSummaryAccent === BOOK_INVITE_SUMMARY_ACCENT.red
                                                    ? theme.verascopeColor.red200
                                                    : theme.verascopeColor.orange100}
                                                height={AUTH_USER_ROW_PILL_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                                                text="Gift"
                                                icon={PlusIcon}
                                                {...getTouchMouseEventHandlers({
                                                    onMouseEnter: onButtonMouseEnter,
                                                    onMouseLeave: onButtonMouseLeave,
                                                })}
                                                {...getPressEventHandlers({ onMouseDown: onOpenBookInviteCreateInvite })}
                                                style={{
                                                    fontSize: `${0.5 * BODY_FONT_SIZE}px`,
                                                }}
                                            />
                                        </BookInvitesSummaryHeroAction>
                                    )}
                                    {bookInviteSummary.remaining > 0 && showBookInviteCreateInvite && (
                                        <BookInvitesCreateInviteContainer>
                                            <BookInvitesCreateInviteField>
                                                <BookInvitesCreateInviteLabel
                                                    $active={
                                                        bookInviteRecipientEmailFocused
                                                        || bookInviteRecipientEmail.trim().length > 0
                                                    }
                                                    $invalid={bookInviteRecipientEmailValidationError.length > 0}
                                                >
                                                    Email
                                                </BookInvitesCreateInviteLabel>
                                                <BookInvitesCreateInviteInput
                                                    data-testid="user-profile-book-invite-email-input"
                                                    className={HOVER_TARGET_CLASSNAME}
                                                    type="email"
                                                    inputMode="email"
                                                    autoComplete="email"
                                                    autoCapitalize="none"
                                                    autoCorrect="off"
                                                    spellCheck={false}
                                                    value={bookInviteRecipientEmail}
                                                    $invalid={bookInviteRecipientEmailValidationError.length > 0}
                                                    $active={
                                                        bookInviteRecipientEmailFocused
                                                        || bookInviteRecipientEmail.trim().length > 0
                                                    }
                                                    placeholder=" "
                                                    {...getTouchMouseEventHandlers({
                                                        onMouseEnter: onButtonMouseEnter,
                                                        onMouseLeave: onButtonMouseLeave,
                                                    })}
                                                    onFocus={() => setBookInviteRecipientEmailFocused(true)}
                                                    onBlur={() => setBookInviteRecipientEmailFocused(false)}
                                                    onChange={(e) => setBookInviteRecipientEmail(e.target.value)}
                                                />
                                                {bookInviteRecipientEmailValidationError.length > 0 && (
                                                    <BookInvitesCreateInviteError>
                                                        {bookInviteRecipientEmailValidationError}
                                                    </BookInvitesCreateInviteError>
                                                )}
                                            </BookInvitesCreateInviteField>
                                            <BookInvitesCreateInviteField>
                                                <BookInvitesCreateInviteLabel
                                                    $active={
                                                        bookInviteRecipientNameFocused
                                                        || bookInviteRecipientName.trim().length > 0
                                                    }
                                                >
                                                    Name (Optional)
                                                </BookInvitesCreateInviteLabel>
                                                <BookInvitesCreateInviteInput
                                                    data-testid="user-profile-book-invite-name-input"
                                                    className={HOVER_TARGET_CLASSNAME}
                                                    type="text"
                                                    inputMode="text"
                                                    autoComplete="name"
                                                    autoCapitalize="words"
                                                    autoCorrect="on"
                                                    spellCheck={false}
                                                    value={bookInviteRecipientName}
                                                    $active={
                                                        bookInviteRecipientNameFocused
                                                        || bookInviteRecipientName.trim().length > 0
                                                    }
                                                    placeholder=" "
                                                    {...getTouchMouseEventHandlers({
                                                        onMouseEnter: onButtonMouseEnter,
                                                        onMouseLeave: onButtonMouseLeave,
                                                    })}
                                                    onFocus={() => setBookInviteRecipientNameFocused(true)}
                                                    onBlur={() => setBookInviteRecipientNameFocused(false)}
                                                    onChange={(e) => setBookInviteRecipientName(e.target.value)}
                                                />
                                            </BookInvitesCreateInviteField>
                                            <BookInvitesCreateInviteField>
                                                <BookInvitesCreateInviteLabel
                                                    $active={
                                                        bookInviteMessageFocused
                                                        || bookInviteMessage.trim().length > 0
                                                    }
                                                >
                                                    Invite Message (Optional)
                                                </BookInvitesCreateInviteLabel>
                                                <BookInvitesCreateInviteMessageInput
                                                    data-testid="user-profile-book-invite-message-input"
                                                    className={HOVER_TARGET_CLASSNAME}
                                                    rows={1}
                                                    autoComplete="off"
                                                    autoCapitalize="sentences"
                                                    autoCorrect="on"
                                                    spellCheck
                                                    value={bookInviteMessage}
                                                    $active={
                                                        bookInviteMessageFocused
                                                        || bookInviteMessage.trim().length > 0
                                                    }
                                                    placeholder=" "
                                                    {...getTouchMouseEventHandlers({
                                                        onMouseEnter: onButtonMouseEnter,
                                                        onMouseLeave: onButtonMouseLeave,
                                                    })}
                                                    onFocus={() => setBookInviteMessageFocused(true)}
                                                    onBlur={() => setBookInviteMessageFocused(false)}
                                                    onChange={(e) => setBookInviteMessage(e.target.value)}
                                                />
                                            </BookInvitesCreateInviteField>
                                            <BookInvitesCreateInviteActions>
                                                {showBookInviteCreateInviteSubmitButton && (
                                                    <BookInvitesCreateInviteActionButtonContainer>
                                                        <Button
                                                            className={HOVER_TARGET_CLASSNAME}
                                                            customCursorActive={customCursorActive}
                                                            type={BUTTON_TYPE.solid}
                                                            background={theme.verascopeColor.orange200}
                                                            height={AUTH_VERIFY_BOOK_ACCESS_BUTTON_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                                                            text="Send"
                                                            icon={EnvelopeIcon}
                                                            disabled={bookInviteActionSubmitting}
                                                            loading={bookInviteActionSubmitting}
                                                            {...getTouchMouseEventHandlers({
                                                                onMouseEnter: onButtonMouseEnter,
                                                                onMouseLeave: onButtonMouseLeave,
                                                            })}
                                                            {...getPressEventHandlers({ onMouseDown: onOpenSendBookInviteModal })}
                                                            style={{
                                                                fontSize: `${0.48 * BODY_FONT_SIZE}px`,
                                                            }}
                                                        />
                                                    </BookInvitesCreateInviteActionButtonContainer>
                                                )}
                                                <BookInvitesCreateInviteActionButtonContainer>
                                                    <Button
                                                        className={HOVER_TARGET_CLASSNAME}
                                                        customCursorActive={customCursorActive}
                                                        type={BUTTON_TYPE.line}
                                                        background={theme.color.neutral200}
                                                        iconFillColor={theme.color.neutral700}
                                                        height={AUTH_VERIFY_BOOK_ACCESS_BUTTON_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                                                        text={showBookInviteCreateInvitePreview ? 'Hide' : 'Preview'}
                                                        icon={EyeIcon}
                                                        disabled={bookInviteActionSubmitting}
                                                        loading={bookInviteActionSubmitting}
                                                        {...getTouchMouseEventHandlers({
                                                            onMouseEnter: onButtonMouseEnter,
                                                            onMouseLeave: onButtonMouseLeave,
                                                        })}
                                                        {...getPressEventHandlers({ onMouseDown: onToggleCreateInvitePreview })}
                                                        style={{
                                                            fontSize: `${0.48 * BODY_FONT_SIZE}px`,
                                                        }}
                                                    />
                                                </BookInvitesCreateInviteActionButtonContainer>
                                                <BookInvitesCreateInviteActionButtonContainer>
                                                    <Button
                                                        className={HOVER_TARGET_CLASSNAME}
                                                        customCursorActive={customCursorActive}
                                                        type={BUTTON_TYPE.line}
                                                        background={theme.color.neutral200}
                                                        iconFillColor={theme.color.neutral700}
                                                        height={AUTH_VERIFY_BOOK_ACCESS_BUTTON_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                                                        text="Cancel"
                                                        icon={CrossIcon}
                                                        disabled={bookInviteActionSubmitting}
                                                        loading={bookInviteActionSubmitting}
                                                        {...getTouchMouseEventHandlers({
                                                            onMouseEnter: onButtonMouseEnter,
                                                            onMouseLeave: onButtonMouseLeave,
                                                        })}
                                                        {...getPressEventHandlers({ onMouseDown: onCancelBookInviteCreateInviteDraft })}
                                                        style={{
                                                            fontSize: `${0.48 * BODY_FONT_SIZE}px`,
                                                        }}
                                                    />
                                                </BookInvitesCreateInviteActionButtonContainer>
                                            </BookInvitesCreateInviteActions>
                                            {showBookInviteCreateInvitePreview && (
                                                <BookInvitesEmailPreviewContainer>
                                                    <BookInvitesEmailPreviewHeader>
                                                        <ReactSVG src={EyeIcon} />
                                                        <BookInvitesEmailPreviewHeaderText>
                                                            Email Preview
                                                        </BookInvitesEmailPreviewHeaderText>
                                                    </BookInvitesEmailPreviewHeader>
                                                    {bookInviteCreateInviteEmailPreviewLoading ? (
                                                        <BookInvitesEmailPreviewLoadingContainer>
                                                            <BookInvitesEmailPreviewSpinner
                                                                src={Spinner}
                                                            />
                                                        </BookInvitesEmailPreviewLoadingContainer>
                                                    ) : (
                                                        <>
                                                            <BookInvitesEmailPreviewSubject>
                                                                {createInvitePreviewForDisplay.subject || 'Unknown subject'}
                                                            </BookInvitesEmailPreviewSubject>
                                                            <BookInvitesEmailPreviewBody>
                                                                {createInvitePreviewForDisplay
                                                                    ? `${[
                                                                        createInvitePreviewForDisplay.toLine,
                                                                        createInvitePreviewForDisplay.ccLine,
                                                                    ]
                                                                        .filter((line) => !!line)
                                                                        .join('\n')}\n\n${createInvitePreviewForDisplay.body}`
                                                                    : 'Preview unavailable.'}
                                                            </BookInvitesEmailPreviewBody>
                                                        </>
                                                    )}
                                                </BookInvitesEmailPreviewContainer>
                                            )}
                                        </BookInvitesCreateInviteContainer>
                                    )}
                                    <BookInvitesAssignmentsContainer
                                        $isEmpty={bookInviteAssignmentsForDisplay.length === 0}
                                        $fillAvailableHeight={isMobileBookInvitesFullscreen}
                                    >
                                        <BookInvitesAssignmentsList
                                            $showScrollHint={showBookInviteAssignmentsScrollHint}
                                            $fillAvailableHeight={isMobileBookInvitesFullscreen}
                                            ref={bookInviteAssignmentsListRef}
                                            onScroll={onBookInviteAssignmentsListScroll}
                                        >
                                            {bookInviteAssignmentsForDisplay.length === 0 && (
                                                <BookInvitesEmptyState>
                                                    <BookInvitesEmptyIcon>
                                                        <ReactSVG
                                                            src={GiftIcon}
                                                        />
                                                    </BookInvitesEmptyIcon>
                                                    <BookInvitesEmptyText>
                                                        {BOOK_INVITEES_EMPTY_TEXT}
                                                    </BookInvitesEmptyText>
                                                    <BookInvitesEmptyHelpText>
                                                        {BOOK_INVITEES_EMPTY_HELP_TEXT}
                                                    </BookInvitesEmptyHelpText>
                                                    <BookInvitesEmptyActionContainer>
                                                        <Button
                                                            className={HOVER_TARGET_CLASSNAME}
                                                            customCursorActive={customCursorActive}
                                                            type={BUTTON_TYPE.solid}
                                                            background={theme.verascopeColor.orange200}
                                                            height={AUTH_VERIFY_BOOK_ACCESS_BUTTON_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                                                            text={BOOK_INVITEES_EMPTY_BUTTON_TEXT}
                                                            {...getTouchMouseEventHandlers({
                                                                onMouseEnter: onButtonMouseEnter,
                                                                onMouseLeave: onButtonMouseLeave,
                                                            })}
                                                            {...getPressEventHandlers({ onMouseDown: onBuyBook })}
                                                            style={{
                                                                fontSize: `${0.55 * BODY_FONT_SIZE}px`,
                                                            }}
                                                        />
                                                    </BookInvitesEmptyActionContainer>
                                                </BookInvitesEmptyState>
                                            )}
                                            {bookInviteAssignmentsForDisplay.map((assignment) => (
                                                <BookInvitesAssignmentItem
                                                    key={assignment.id}
                                                    $accent={getBookInviteAssignmentStatusAccent(assignment.status)}
                                                >
                                                    <BookInvitesAssignmentTop>
                                                        <BookInvitesAssignmentRecipient
                                                            $accent={getBookInviteAssignmentStatusAccent(assignment.status)}
                                                        >
                                                            {getBookInviteAssignmentHeading(assignment)}
                                                        </BookInvitesAssignmentRecipient>
                                                        <BookInvitesAssignmentStatus
                                                            $accent={getBookInviteAssignmentStatusAccent(assignment.status)}
                                                            title={getBookInviteAssignmentStatusTooltipText(assignment.status) || undefined}
                                                        >
                                                            {getBookInviteAssignmentStatusText(assignment.status)}
                                                        </BookInvitesAssignmentStatus>
                                                    </BookInvitesAssignmentTop>
                                                    <BookInvitesAssignmentMeta
                                                        $accent={getBookInviteAssignmentStatusAccent(assignment.status)}
                                                    >
                                                        {getBookInviteAssignmentStatusMeta(assignment)}
                                                    </BookInvitesAssignmentMeta>
                                                    {assignment.status === BOOK_INVITE_ASSIGNMENT_STATUS.pending && (
                                                        <BookInvitesAssignmentActions>
                                                            <BookInvitesAssignmentActionButtonContainer>
                                                                <Button
                                                                    className={HOVER_TARGET_CLASSNAME}
                                                                    customCursorActive={customCursorActive}
                                                                    type={BUTTON_TYPE.line}
                                                                    background={theme.color.neutral200}
                                                                    iconFillColor={theme.color.neutral700}
                                                                    height={AUTH_VERIFY_BOOK_ACCESS_BUTTON_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                                                                    text="Resend"
                                                                    icon={EnvelopeIcon}
                                                                    disabled={bookInviteActionSubmitting}
                                                                    loading={bookInviteActionSubmitting}
                                                                    {...getTouchMouseEventHandlers({
                                                                        onMouseEnter: onButtonMouseEnter,
                                                                        onMouseLeave: onButtonMouseLeave,
                                                                    })}
                                                                    {...getPressEventHandlers({ onMouseDown: () => onOpenResendBookInviteModal(assignment) })}
                                                                    style={{
                                                                        fontSize: `${0.5 * BODY_FONT_SIZE}px`,
                                                                    }}
                                                                />
                                                            </BookInvitesAssignmentActionButtonContainer>
                                                            <BookInvitesAssignmentActionButtonContainer>
                                                                <Button
                                                                    className={HOVER_TARGET_CLASSNAME}
                                                                    customCursorActive={customCursorActive}
                                                                    type={BUTTON_TYPE.line}
                                                                    background={theme.color.neutral200}
                                                                    iconFillColor={theme.color.neutral700}
                                                                    height={AUTH_VERIFY_BOOK_ACCESS_BUTTON_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                                                                    text={bookInvitePreviewAssignmentId === assignment.id ? 'Hide' : 'Preview'}
                                                                    icon={EyeIcon}
                                                                    disabled={bookInviteActionSubmitting}
                                                                    loading={bookInviteActionSubmitting}
                                                                    {...getTouchMouseEventHandlers({
                                                                        onMouseEnter: onButtonMouseEnter,
                                                                        onMouseLeave: onButtonMouseLeave,
                                                                    })}
                                                                    {...getPressEventHandlers({ onMouseDown: () => onToggleAssignmentInvitePreview(assignment) })}
                                                                    style={{
                                                                        fontSize: `${0.5 * BODY_FONT_SIZE}px`,
                                                                    }}
                                                                />
                                                            </BookInvitesAssignmentActionButtonContainer>
                                                            <BookInvitesAssignmentActionButtonContainer>
                                                                <Button
                                                                    className={HOVER_TARGET_CLASSNAME}
                                                                    customCursorActive={customCursorActive}
                                                                    type={BUTTON_TYPE.line}
                                                                    background={theme.color.neutral200}
                                                                    iconFillColor={theme.color.neutral700}
                                                                    height={AUTH_VERIFY_BOOK_ACCESS_BUTTON_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                                                                    text="Link"
                                                                    icon={ClipboardIcon}
                                                                    disabled={bookInviteActionSubmitting}
                                                                    loading={bookInviteActionSubmitting}
                                                                    {...getTouchMouseEventHandlers({
                                                                        onMouseEnter: onButtonMouseEnter,
                                                                        onMouseLeave: onButtonMouseLeave,
                                                                    })}
                                                                    {...getPressEventHandlers({ onMouseDown: () => onCopyBookInviteClaimLink(assignment.id) })}
                                                                    style={{
                                                                        fontSize: `${0.5 * BODY_FONT_SIZE}px`,
                                                                    }}
                                                                />
                                                            </BookInvitesAssignmentActionButtonContainer>
                                                            <BookInvitesAssignmentIconButtonContainer>
                                                                <Button
                                                                    className={HOVER_TARGET_CLASSNAME}
                                                                    customCursorActive={customCursorActive}
                                                                    type={BUTTON_TYPE.line}
                                                                    background={theme.color.neutral200}
                                                                    iconFillColor={theme.color.neutral700}
                                                                    height={AUTH_VERIFY_BOOK_ACCESS_BUTTON_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                                                                    text="Revoke"
                                                                    icon={CrossIcon}
                                                                    disabled={bookInviteActionSubmitting}
                                                                    loading={bookInviteActionSubmitting}
                                                                    {...getTouchMouseEventHandlers({
                                                                        onMouseEnter: onButtonMouseEnter,
                                                                        onMouseLeave: onButtonMouseLeave,
                                                                    })}
                                                                    {...getPressEventHandlers({ onMouseDown: () => onOpenCancelBookInviteModal(assignment) })}
                                                                    style={{
                                                                        fontSize: `${0.5 * BODY_FONT_SIZE}px`,
                                                                    }}
                                                                />
                                                            </BookInvitesAssignmentIconButtonContainer>
                                                        </BookInvitesAssignmentActions>
                                                    )}
                                                    {assignment.status === BOOK_INVITE_ASSIGNMENT_STATUS.pending
                                                    && bookInvitePreviewAssignmentId === assignment.id && (
                                                        <BookInvitesAssignmentEmailPreviewContainer>
                                                            <BookInvitesEmailPreviewHeader>
                                                                <ReactSVG src={EyeIcon} />
                                                                <BookInvitesEmailPreviewHeaderText>
                                                                    Email Preview
                                                                </BookInvitesEmailPreviewHeaderText>
                                                            </BookInvitesEmailPreviewHeader>
                                                            {bookInviteAssignmentEmailPreviewLoadingIds[assignment.id] ? (
                                                                <BookInvitesEmailPreviewLoadingContainer>
                                                                    <BookInvitesEmailPreviewSpinner
                                                                        src={Spinner}
                                                                    />
                                                                </BookInvitesEmailPreviewLoadingContainer>
                                                            ) : (
                                                                <>
                                                                    <BookInvitesEmailPreviewSubject>
                                                                        {bookInviteAssignmentEmailPreviews[assignment.id]?.subject || 'Unknown subject'}
                                                                    </BookInvitesEmailPreviewSubject>
                                                                    <BookInvitesEmailPreviewBody>
                                                                        {bookInviteAssignmentEmailPreviews[assignment.id]
                                                                            ? `${[
                                                                                bookInviteAssignmentEmailPreviews[assignment.id].toLine,
                                                                                bookInviteAssignmentEmailPreviews[assignment.id].ccLine,
                                                                            ]
                                                                                .filter((line) => !!line)
                                                                                .join('\n')}\n\n${bookInviteAssignmentEmailPreviews[assignment.id].body}`
                                                                            : 'Preview unavailable.'}
                                                                    </BookInvitesEmailPreviewBody>
                                                                </>
                                                            )}
                                                        </BookInvitesAssignmentEmailPreviewContainer>
                                                    )}
                                                </BookInvitesAssignmentItem>
                                            ))}
                                        </BookInvitesAssignmentsList>
                                        {showBookInviteAssignmentsScrollHint
                                        && bookInviteAssignmentsForDisplay.length > 0 && (
                                            <BookInvitesAssignmentsScrollHint>
                                                <BookInvitesAssignmentsScrollHintText>
                                                    Scroll for more
                                                    <BookInvitesAssignmentsScrollHintChevron>
                                                        ↓
                                                    </BookInvitesAssignmentsScrollHintChevron>
                                                </BookInvitesAssignmentsScrollHintText>
                                            </BookInvitesAssignmentsScrollHint>
                                        )}
                                    </BookInvitesAssignmentsContainer>
                                </BookInvitesOverlay>
                            )}
                        </AuthUserDetails>
                        {showMakeAnnotationTooltip && (
                            <Tooltip
                                permanent={showMakeAnnotationTooltip}
                                text={SIGN_IN_ANNOTATION_CREATE_MESSAGE}
                                side={TOOLTIP_TYPE.bottom}
                            />
                        )}
                    </DialogBody>
                )}
            </DialogBodyContainer>
            <Modal
                hasContainer
                customCursorActive={customCursorActive}
                hasSound={hasSound}
                isOpen={!!bookInvitePendingSendInviteRecipientEmail}
                closeModal={onCloseSendBookInviteModal}
            >
                <GenericModal
                    customCursorActive={customCursorActive}
                    message={bookInviteSendBookInviteModalMessage}
                    callback={onConfirmSendBookInvite}
                    acceptText="Send"
                    handleClose={onCloseSendBookInviteModal}
                    hideCancel={false}
                    onCursorEnter={onCursorEnter}
                    onCursorLeave={onCursorLeave}
                />
            </Modal>
            <Modal
                hasContainer
                customCursorActive={customCursorActive}
                hasSound={hasSound}
                isOpen={!!bookInvitePendingResendAssignment}
                closeModal={onCloseResendBookInviteModal}
            >
                <GenericModal
                    customCursorActive={customCursorActive}
                    message={bookInviteResendBookInviteModalMessage}
                    callback={onConfirmResendBookInvite}
                    acceptText="Resend"
                    handleClose={onCloseResendBookInviteModal}
                    hideCancel={false}
                    onCursorEnter={onCursorEnter}
                    onCursorLeave={onCursorLeave}
                />
            </Modal>
            <Modal
                hasContainer
                customCursorActive={customCursorActive}
                hasSound={hasSound}
                isOpen={!!bookInvitePendingCancelAssignment}
                closeModal={onCloseCancelBookInviteModal}
            >
                <GenericModal
                    customCursorActive={customCursorActive}
                    message={bookInviteCancelBookInviteModalMessage}
                    callback={onConfirmCancelBookInvite}
                    acceptText="Revoke"
                    handleClose={onCloseCancelBookInviteModal}
                    hideCancel={false}
                    onCursorEnter={onCursorEnter}
                    onCursorLeave={onCursorLeave}
                />
            </Modal>
            <Modal
                hasContainer
                customCursorActive={customCursorActive}
                hasSound={hasSound}
                isOpen={showBookInviteCancelCreateInviteDraftModal}
                closeModal={onCloseCancelBookInviteCreateInviteDraftModal}
            >
                <GenericModal
                    customCursorActive={customCursorActive}
                    message={bookInviteCancelCreateInviteDraftModalMessage}
                    callback={onConfirmCancelBookInviteCreateInviteDraft}
                    acceptText="Discard"
                    handleClose={onCloseCancelBookInviteCreateInviteDraftModal}
                    hideCancel={false}
                    onCursorEnter={onCursorEnter}
                    onCursorLeave={onCursorLeave}
                />
            </Modal>
            <CloseButtonContainer
                avatarLength={USER_PROFILE_AVATAR_LENGTH_MULTIPLIER * BODY_FONT_SIZE}
                length={USER_PROFILE_CLOSE_BUTTON_CONTAINER_LENGTH_MULTIPLIER * BODY_FONT_SIZE}
                isExpanded={isExpanded}
                contractedTop={USER_PROFILE_DIALOG_TOP_CONTRACTED_MULTIPLIER * BODY_FONT_SIZE}
                contractedRight={USER_PROFILE_DIALOG_RIGHT_CONTRACTED_MULTIPLIER * BODY_FONT_SIZE}
                expandedTop={CLOSE_BUTTON_CONTAINER_OFFSET_TOP_EXPANDED_MULTIPLIER * BODY_FONT_SIZE}
                expandedRight={CLOSE_BUTTON_CONTAINER_OFFSET_RIGHT_EXPANDED_MULTIPLIER * BODY_FONT_SIZE}
                transitionDuration={DIALOG_BODY_CONTAINER_TRANSITION_DURATION}
            >
                <Button
                    className={HOVER_TARGET_CLASSNAME}
                    customCursorActive={customCursorActive}
                    type={BUTTON_TYPE.secret}
                    background={theme.color.neutral600}
                    height={USER_PROFILE_CLOSE_BUTTON_LENGTH_MULTIPLIER * BODY_FONT_SIZE}
                    icon={CrossIcon}
                    {...(isExpanded
                        ? {
                            ...getTouchMouseEventHandlers({
                                onMouseEnter: onButtonMouseEnter,
                                onMouseLeave: onButtonMouseLeave,
                            }),
                            onClick: onToggleExpand,
                        } : {}
                    )}
                />
            </CloseButtonContainer>
        </Container>
    );
}

export default UserProfileDialog;
